/*
 * automotive.js — custom web components for the tires & auto-parts layer.
 * Pure vanilla ES6, dependency-free, loaded with `defer`.
 * Progressive enhancement only: every component must work without JS
 * (real <form>, <a>, <input>); these classes merely enhance behaviour.
 */
(function () {
  'use strict';

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Monotone Uhr in ms (Feinschliff 3 FIX: Zeitstempel im Scroll-Pfad ohne Lesung der Seite).
  const jetztMs = () => (window.performance && typeof window.performance.now === 'function' ? window.performance.now() : Date.now());

  /* ----------------------------------------------------------------------
   * Pub/Sub bridge.
   * Dawn's assets/pubsub.js + assets/constants.js define `publish`,
   * `subscribe` and `PUB_SUB_EVENTS` as plain (non-module) globals — they
   * are NOT attached to `window`. We resolve them lazily and guard for the
   * (defensive) case where they are absent, so this file never throws.
   * -------------------------------------------------------------------- */
  function getPublish() {
    if (typeof window.publish === 'function') return window.publish;
    if (typeof publish === 'function') return publish; // eslint-disable-line no-undef
    return null;
  }
  function getSubscribe() {
    if (typeof window.subscribe === 'function') return window.subscribe;
    if (typeof subscribe === 'function') return subscribe; // eslint-disable-line no-undef
    return null;
  }
  function cartUpdateEventName() {
    try {
      // eslint-disable-next-line no-undef
      if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        // eslint-disable-next-line no-undef
        return PUB_SUB_EVENTS.cartUpdate;
      }
    } catch (e) {
      /* ReferenceError when the constant is missing — fall through. */
    }
    return 'cart-update';
  }

  // Geldbetrag in Cent -> die Schreibweise DIESES Shops.
  //
  // window.apMoneyFormat setzt layout/theme.liquid aus dem Geldformat des Shops
  // (shop.money_format bzw. shop.money_with_currency_format, je nach
  // settings.currency_code_enabled) — also genau das, was der Liquid-Filter
  // money / money_with_currency daneben ausgibt. Vorher haengte diese Funktion
  // immer ein Symbol HINTEN an: auf derselben Produktseite standen
  // "€108,68 EUR" (Liquid), "€69,41" und "434,72 €" (JS) — drei Schreibweisen,
  // "GESAMT 434,72 €" direkt neben "(€69,41 EUR)" (Audit 24.09.2026,
  // pdp-reifen-geldformat-uneinheitlich). Die Platzhalter sind Shopifys
  // Liste (amount, amount_with_comma_separator …); ein unbekannter oder
  // fehlender faellt auf die alte Rechnung zurueck: Locale aus <html lang>
  // plus Symbol hinten.
  function geldBetrag(cents, art) {
    const wert = (Number(cents) || 0) / 100;
    const trenne = (stellen, tausend, dezimal) => {
      const teile = Math.abs(wert).toFixed(stellen).split('.');
      const ganz = teile[0].replace(/\B(?=(\d{3})+(?!\d))/g, tausend);
      return (wert < 0 ? '-' : '') + (teile[1] ? ganz + dezimal + teile[1] : ganz);
    };
    switch (art) {
      case 'amount':
        return trenne(2, ',', '.');
      case 'amount_no_decimals':
        return trenne(0, ',', '.');
      case 'amount_with_comma_separator':
        return trenne(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return trenne(0, '.', ',');
      case 'amount_with_space_separator':
        return trenne(2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return trenne(0, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return trenne(2, "'", '.');
      case 'amount_with_period_and_space_separator':
        return trenne(2, ' ', '.');
      default:
        return null;
    }
  }

  function nurText(html) {
    if (!/[<&]/.test(html)) return html;
    try {
      return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
    } catch (e) {
      return html.replace(/<[^>]*>/g, '');
    }
  }

  function formatMoney(cents, symbol) {
    const format = typeof window.apMoneyFormat === 'string' ? window.apMoneyFormat : '';
    const platzhalter = /\{\{\s*(\w+)\s*\}\}/;
    const treffer = format.match(platzhalter);
    if (treffer) {
      const betrag = geldBetrag(cents, treffer[1]);
      // Shops duerfen HTML ins Format schreiben (<span class=money>, &euro;);
      // die Aufrufer setzen textContent — also nur der Text, Entitaeten
      // aufgeloest.
      if (betrag !== null) return nurText(format.replace(platzhalter, betrag));
    }
    const amount = (Number(cents) || 0) / 100;
    /* LED-FORK FP-052 Anfang */
    // K-7: Sprache aus <html lang> (request.locale), Waehrung aus cart.currency (window.apWaehrung,
    // layout/theme.liquid), keine feste Sprache. Ohne Shop-Format (andere Marktwaehrung) ueber Intl.
    const locale = document.documentElement.lang || undefined;
    if (!symbol && typeof window.apWaehrung === 'string' && window.apWaehrung) {
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: window.apWaehrung }).format(amount);
      } catch (e) {
        /* unbekannte Waehrung: weiter mit Zahl und Symbol */
      }
    }
    /* LED-FORK FP-052 Ende */
    const formatted = amount.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const sym = symbol || '';
    // Rueckfall ohne Shop-Format: "12,90 €" (Symbol hinten), ohne Symbol nackt.
    return sym ? `${formatted} ${sym}` : formatted;
  }

  /* ----------------------------------------------------------------------
   * Groesseneinstieg — gemeinsame Helfer.
   *
   * Die tyre.*-Metafelder stehen als TEXT in echter Reifenschreibweise
   * ("205", "22.5", "2.75"): ein numerischer Typ wuerde die Dezimalmasse
   * entweder ablehnen oder ein "205.0" erzwingen, das auf keiner Flanke steht.
   * Ein Search-&-Discovery-Filter vergleicht die GESPEICHERTE Zeichenkette,
   * der Auswahlwert muss also exakt auf diese Form normalisiert werden.
   * Spiegelt mfNum() im App-Block (extensions/tyre-search/blocks/
   * tyre-search.liquid) bzw. formatDimension() auf der App-Seite.
   * -------------------------------------------------------------------- */
  function mfNum(value) {
    var n = Number(value);
    if (!isFinite(n)) return value;
    return String(Number(n.toFixed(2)));
  }

  // Zahl, wo es eine ist (2.75 bleibt 2.75) — sonst der Rohwert.
  function sizeNumber(value) {
    var n = Number(value);
    return isFinite(n) ? n : value;
  }

  // Querschnitt-Schluessel fuer Groessen OHNE Querschnitt (12 R22.5, 185R14C, 16.9R30).
  // WOERTLICHER Vertrag mit NO_ASPECT_KEY in app/lib/catalog/available-sizes.server.ts:
  // der Baum traegt ihn nur mit na=1, und immer als LETZTEN Querschnitt einer Breite.
  var NO_ASPECT = '-';
  // "keine Angabe" statt "ohne Querschnitt": die Trefferliste wird bei dieser Wahl
  // BREITER, nicht enger — Search & Discovery kennt kein "Metafeld fehlt", der
  // aspect_ratio-Filter entfaellt dann einfach. Wortgleich mit ap-prefilter.js und mit
  // dem App-Block extensions/tyre-search; derselbe Reifen darf im Shop nicht zwei
  // Namen haben.
  var NO_ASPECT_LABEL = '– (keine Angabe)';

  /*
   * ZIEL jeder Groessensuche ist die SUCHERGEBNIS-Seite mit den
   * tyre.*-Metafeldfiltern — NICHT eine Volltextsuche "205/55R16" (die traf
   * nichts) und keine zusammengebaute Groessen-Tag-URL (der Groessen-Tag ist
   * die verbatim gedruckte Groesse: C-Kennung, ZR-Bauart, x-Notation).
   * KOLLEKTION VOR /search — die aeltere Begruendung hier ("/search wendet dieselben
   * Facetten bei jeder Katalogsgroesse an") ist WIDERLEGT. Shopify rendert keine
   * Filter oberhalb von 5.000 Kollektions-Produkten UND keine oberhalb von 1.000
   * SUCHTREFFERN. q=reifen trifft ~55.000 Reifen, liegt also weit darueber; die
   * Facetten greifen dort nicht verlaesslich. Live gemessen am 2026-08-20:
   *
   *   /search?q=reifen&...width=300&aspect_ratio=70&rim_diameter=20 ->  0 Treffer
   *   /collections/agrarreifen?dieselben Filter                     ->  1 Treffer
   *
   * Deshalb: wenn der Aufrufer eine Kollektion kennt, die klein genug ist (Attribut
   * data-coll), wird DORT gefiltert. Nur ohne solches Ziel bleibt /search — dann
   * unvollstaendig, aber besser als nichts. Gleiche Regel wie im App-Block
   * (extensions/tyre-search) und in ap-prefilter.js.
   */
  function tyreFacetUrl(width, aspect, rim, season, coll) {
    var params = [];
    if (!coll) params.push('q=reifen', 'options%5Bprefix%5D=last');
    // Bei NO_ASPECT faellt der Querschnitt ERSATZLOS weg, statt als "aspect_ratio=-"
    // mitzufahren (mfNum gibt den Rohwert zurueck, Number('-') ist NaN): so ein Reifen
    // traegt das Metafeld gar nicht, ein Filter darauf liefert eine garantiert leere
    // Liste MIT aktivem Filter-Chip. Live gemessen 2026-09-01 auf
    // /collections/lkw-reifen: width=12&rim_diameter=22.5 -> 29 Treffer, dieselbe URL
    // zusaetzlich mit aspect_ratio=70 -> 0.
    var aspectValue = aspect === NO_ASPECT ? '' : mfNum(aspect);
    params.push('filter.p.m.tyre.width=' + encodeURIComponent(mfNum(width)));
    if (aspectValue) params.push('filter.p.m.tyre.aspect_ratio=' + encodeURIComponent(aspectValue));
    params.push('filter.p.m.tyre.rim_diameter=' + encodeURIComponent(mfNum(rim)));
    if (season) params.push('filter.p.m.tyre.season=' + encodeURIComponent(season));
    var base = coll || '/search';
    return base + (base.indexOf('?') > -1 ? '&' : '?') + params.join('&');
  }

  /*
   * ZIELFUNKTION ZUERST. tyreFacetUrl bleibt der Rueckfall, Byte fuer Byte; davor
   * fragt window.apSizeTarget (assets/ap-size-target.js) die App nach dem Ziel,
   * das den GANZEN Bestand zeigt (Split-Kollektion mit Facetten, sonst Tag-Route).
   * Handles und Tags setzt das Theme dabei nie selbst zusammen. Ohne das Skript,
   * ohne scope oder ohne Antwort: sofort und synchron das bisherige Ziel.
   *
   * scope: aus der Produktart der Seite (data-product-type am Formular, die
   * Zuordnung steht in ap-size-target.js). Ohne Produkt (Suche, 404, Ratgeber,
   * Startseiten-Rueckfall) gilt 'auto' — das Formular sucht dort im PKW-lastigen
   * Gesamtbestand, und ein Blatt, das es in auto nicht gibt, beantwortet die
   * Route mit null. Eine Produktart ohne Zuordnung fragt gar nicht.
   *
   * EIGENE KLASSEN-KOLLEKTION (data-coll): dann bleibt schon das bisherige Ziel
   * in der Fahrzeugklasse der Seite (Transporter-Produktseite ->
   * /collections/transporterreifen). Gefragt wird dort nur, wenn der scope genau
   * diese Produktart meint (exactScopeFor: moto, agrar, lkw …). 'auto' fasst
   * PKW, SUV, Transporter und Oldtimer zusammen — sein Ziel zeigte auf einer
   * Transporterseite PKW-Reifen ohne C-Kennung (Nachpruefung 25.09.2026). Ohne
   * data-coll ist das bisherige Ziel /search ueber alle Klassen, 'auto' also
   * enger als der Rueckfall und weiter erlaubt.
   */
  function tyreTargetParams(el, width, aspect, rim, season) {
    var ask = window.apSizeTarget;
    if (typeof ask !== 'function') return null;
    var type = el && el.hasAttribute('data-product-type') ? el.getAttribute('data-product-type') : null;
    var ownColl = !!(el && el.getAttribute('data-coll'));
    var scope = null;
    if (type === null) {
      scope = 'auto';
    } else if (ownColl) {
      scope = typeof ask.exactScopeFor === 'function' ? ask.exactScopeFor(type) : null;
    } else {
      scope = typeof ask.scopeFor === 'function' ? ask.scopeFor(type) : null;
    }
    if (!scope) return null;
    return {
      kind: 'tyre',
      scope: scope,
      w: mfNum(width),
      a: aspect === NO_ASPECT ? '' : mfNum(aspect),
      r: mfNum(rim),
      season: season || '',
    };
  }

  function withSizeTarget(params, fallback, done) {
    if (!params) {
      done(fallback);
      return;
    }
    window.apSizeTarget(params).then(
      function (url) {
        done(url || fallback);
      },
      function () {
        done(fallback);
      }
    );
  }

  /*
   * Groesse eines Produkts als Vorbelegung — NUR die saubere metrische
   * Schreibweise "205/55 R16" (auch "315/80 R22.5", "235/45 ZR18"). Zoll-,
   * C- und Agrar-Notationen ("12 R22.5", "185R14C", "11.2-24") werden NICHT
   * zerlegt: im Zweifel bleibt das Feld leer, statt eine Groesse zu raten.
   */
  function parsePresetSize(text) {
    var m = /^\s*(\d{2,3}(?:\.\d{1,2})?)\s*\/\s*(\d{2,3}(?:\.\d{1,2})?)\s*Z?R\s*(\d{2}(?:\.\d)?)\s*$/i.exec(
      String(text || '')
    );
    if (!m) return null;
    return { w: mfNum(m[1]), h: mfNum(m[2]), d: mfNum(m[3]) };
  }

  // Ohne Querschnitt faellt auch der Bruchstrich weg: "12 R22.5", nicht "12/- R22.5".
  // Dieses Label steht spaeter im Chip "Zuletzt gesucht" (initLastSizeChips).
  function tyreSizeLabel(width, aspect, rim) {
    if (aspect === NO_ASPECT) return mfNum(width) + ' R' + mfNum(rim);
    return mfNum(width) + '/' + mfNum(aspect) + ' R' + mfNum(rim);
  }

  // Sortiert numerisch, laesst NO_ASPECT aber als ZEICHENKETTE stehen und stellt ihn ans
  // ENDE. Das fruehere .map(Number) machte aus ihm eine Option "NaN" — waehlbar und
  // nirgends hinfuehrend. Dieselbe Regel wie numKeys in ap-prefilter.js, sortKeys im
  // App-Block extensions/tyre-search und die Einsortierung in buildSizeTree
  // (available-sizes.server.ts): vier Stellen, eine Reihenfolge.
  function sortedNumericKeys(obj) {
    var ks = Object.keys(obj || {});
    var i = ks.indexOf(NO_ASPECT);
    if (i > -1) ks.splice(i, 1);
    ks.sort(function (a, b) {
      return a - b;
    });
    if (i > -1) ks.push(NO_ASPECT);
    return ks;
  }

  // Metrische Breiten (>= 100 mm) zuerst, danach der Rest — beide Teile in der
  // gelieferten (numerischen) Reihenfolge. Nur fuer Formulare mit
  // data-width-order="metric-first" (applyTree).
  function metricFirst(values) {
    var metrisch = [];
    var rest = [];
    (values || []).forEach(function (v) {
      (Number(v) >= 100 ? metrisch : rest).push(v);
    });
    return metrisch.concat(rest);
  }

  // Einen Wert nur dann setzen, wenn es ihn in der Liste wirklich gibt —
  // sonst stuende im Feld eine Groesse, die zu keinem Treffer fuehrt.
  function selectIfPresent(select, value) {
    if (!select || value === undefined || value === null || value === '') return false;
    var wanted = String(value);
    // Liste bei Bedarf (Phase D Teil 2, P1 Massnahme 6): gegen die gespeicherte Liste pruefen, ohne sie zu zeichnen.
    if (select.__apListe) return auswahlWaehlen(select, wanted);
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === wanted) {
        select.value = wanted;
        return true;
      }
    }
    return false;
  }

  /* AUSWAHLLISTEN BEI BEDARF (P1 M6, Probe PD-11): die Liste steht in select.__apListe; im Ruhezustand stehen im DOM nur
   * Platzhalter und gesetzter Wert (<= 2 Optionen), voll bei focus/pointerdown/mousedown/touchstart/keydown (vor der
   * Standardaktion) und fuer Skripte, die die Liste lesen oder einen anderen Wert setzen. Ohne Skript: statische Liste. */
  var AUSWAHL_BEDARF = ['focus', 'pointerdown', 'mousedown', 'touchstart', 'keydown'];

  function auswahlEigenschaft(name) {
    var p = typeof HTMLSelectElement === 'function' ? HTMLSelectElement.prototype : null;
    while (p) {
      var d = Object.getOwnPropertyDescriptor(p, name);
      if (d) return d;
      p = Object.getPrototypeOf(p);
    }
    return null;
  }

  /** Der Wert des Feldes, wie der Browser ihn fuehrt (an den eigenen Zugriffs-Eigenschaften vorbei). */
  function auswahlWert(select) {
    var d = auswahlEigenschaft('value');
    return d && d.get ? d.get.call(select) : select.value;
  }

  function auswahlOption(wert, text) {
    var o = document.createElement('option');
    o.value = wert;
    o.textContent = text;
    return o;
  }

  /** Zeichnet die Liste: voll = alle Eintraege, sonst Platzhalter + gesetzter Wert (ohne Platzhalter: der erste
   *  Eintrag, wie ihn der Browser in einer vollen Liste vorwaehlen wuerde). aktuell = der zu waehlende Wert. */
  function auswahlZeichnen(select, voll, aktuell) {
    var liste = select.__apListe;
    if (!liste) return;
    var hat = false;
    for (var k = 0; k < liste.eintraege.length; k++) {
      if (liste.eintraege[k].wert === aktuell) {
        hat = true;
        break;
      }
    }
    select.innerHTML = '';
    if (liste.kopf) select.appendChild(auswahlOption(liste.kopf.wert, liste.kopf.text));
    var wahl = liste.kopf ? liste.kopf.wert : null;
    for (var i = 0; i < liste.eintraege.length; i++) {
      var e = liste.eintraege[i];
      var gewaehlt = hat ? e.wert === aktuell : !liste.kopf && i === 0;
      if (!voll && !gewaehlt) continue;
      var o = auswahlOption(e.wert, e.text);
      if (gewaehlt) {
        o.selected = true;
        wahl = e.wert;
      }
      select.appendChild(o);
    }
    // Den Wert ausdruecklich setzen: nach innerHTML = '' haelt nicht jede Umgebung die Auswahl ueber selected.
    var dv = auswahlEigenschaft('value');
    if (wahl !== null && dv && dv.set) dv.set.call(select, wahl);
    select.__apVoll = !!voll;
  }

  /** Volle Liste, Wert bleibt (Fokus, Zeiger, Taste, Skript-Zugriff). */
  function auswahlVoll(select) {
    if (!select.__apListe || select.__apVoll) return;
    auswahlZeichnen(select, true, auswahlWert(select));
  }

  /** Einmal je Feld: die Ausloeser der vollen Liste und die Zugriffs-Eigenschaften fuer Skripte. */
  function auswahlBedarf(select) {
    if (select.__apBedarf) return;
    select.__apBedarf = true;
    var voll = function () {
      auswahlVoll(select);
    };
    for (var i = 0; i < AUSWAHL_BEDARF.length; i++) {
      select.addEventListener(AUSWAHL_BEDARF[i], voll, { capture: true, passive: true });
    }
    ['options', 'selectedIndex', 'length'].forEach(function (name) {
      var d = auswahlEigenschaft(name);
      if (!d || !d.get) return;
      var eigen = {
        configurable: true,
        get: function () {
          voll();
          return d.get.call(select);
        },
      };
      if (d.set) {
        eigen.set = function (v) {
          voll();
          d.set.call(select, v);
        };
      }
      Object.defineProperty(select, name, eigen);
    });
    var dv = auswahlEigenschaft('value');
    if (dv && dv.get && dv.set) {
      Object.defineProperty(select, 'value', {
        configurable: true,
        get: function () {
          return dv.get.call(select);
        },
        set: function (v) {
          if (!select.__apVoll && String(v) !== '' && String(v) !== dv.get.call(select)) voll();
          dv.set.call(select, v);
        },
      });
    }
  }

  /** Neue Liste fuer ein Feld (fill): gespeichert, im Ruhezustand kompakt gezeichnet. Steht der Fokus im Feld oder
   *  war die Liste schon einmal offen, gleich vollstaendig. Ohne Zugriffs-Eigenschaften des Browsers (sehr alte
   *  Umgebung) immer vollstaendig — wie bisher. */
  function auswahlListe(select, eintraege, kopf) {
    var aktuell = auswahlWert(select);
    var warVoll = !!select.__apVoll;
    select.__apListe = { eintraege: eintraege, kopf: kopf || null };
    var kompaktMoeglich = !!auswahlEigenschaft('options') && !!auswahlEigenschaft('value');
    var voll = !kompaktMoeglich || warVoll || document.activeElement === select;
    auswahlZeichnen(select, voll, aktuell);
    if (kompaktMoeglich) auswahlBedarf(select);
  }

  /** Einen Wert waehlen, wenn ihn die Liste kennt (selectIfPresent) — ohne die volle Liste zu zeichnen. */
  function auswahlWaehlen(select, wanted) {
    var liste = select.__apListe;
    var hat = false;
    for (var i = 0; i < liste.eintraege.length; i++) {
      if (liste.eintraege[i].wert === wanted) {
        hat = true;
        break;
      }
    }
    if (!hat) return false;
    if (select.__apVoll) {
      var dv = auswahlEigenschaft('value');
      if (dv && dv.set) dv.set.call(select, wanted);
      else select.value = wanted;
    } else {
      auswahlZeichnen(select, false, wanted);
    }
    return true;
  }

  // Pro Proxy-URL genau ein Abruf, auch wenn mehrere Bausteine derselben Seite
  // fragen. Denselben Cache benutzt die Kaskade in
  // sections/collection-prefilter.liquid — Schluessel ist die volle URL.
  function fetchSizeTree(url) {
    window.__apSizeTrees = window.__apSizeTrees || {};
    if (!window.__apSizeTrees[url]) {
      window.__apSizeTrees[url] = fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .catch(function () {
          return null;
        });
    }
    return window.__apSizeTrees[url];
  }

  /* ----------------------------------------------------------------------
   * <tyre-size-search>
   * Umschliesst das Groessen-Formular der Section tyre-size-search.
   *
   * Ohne JS traegt das Formular sich selbst: seine Felder heissen bereits wie
   * die Filterparameter, ein normaler GET-Submit landet also auf genau der
   * gleichen Trefferseite. Diese Klasse legt drei Dinge obendrauf:
   *   1. leere Parameter aus der URL halten und die Masse normalisieren,
   *   2. die Auswahllisten aus dem App-Proxy mit den TATSAECHLICH lieferbaren
   *      Groessen fuellen und zur Kaskade verdrahten — faellt der Proxy aus
   *      oder antwortet er leer, bleiben die statischen Listen aus dem Liquid
   *      stehen (ein ungenaues Formular ist besser als ein kaputtes),
   *   3. die gewaehlte Groesse als "ap_last_size" merken.
   * -------------------------------------------------------------------- */
  class TyreSizeSearch extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.tree = null;
      this.heads = {};
      this.onSubmit = this.onSubmit.bind(this);
      this.onWidthChange = this.onWidthChange.bind(this);
      this.onAspectChange = this.onAspectChange.bind(this);
      this.onRimChange = this.onRimChange.bind(this);
      this.onUserChange = this.onUserChange.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.ownPrefill = false;
    }

    connectedCallback() {
      // Nachschlagen, falls der Konstruktor vor den Kindern lief (Element per
      // innerHTML eingefuegt, nachdem es schon definiert war).
      if (!this.form) this.form = this.querySelector('form');
      if (!this.form) return;
      this.width = this.querySelector('[data-size-part="width"]');
      this.aspect = this.querySelector('[data-size-part="aspect"]');
      this.rim = this.querySelector('[data-size-part="rim"]');
      this.season = this.querySelector('[data-size-part="season"]');
      // Die erste Option jedes Feldes ist sein uebersetzter Platzhalter. Vor dem
      // ersten Neuaufbau merken, sonst steht dort spaeter nichts mehr.
      this.heads = {
        width: this.headText(this.width),
        aspect: this.headText(this.aspect),
        rim: this.headText(this.rim),
        season: this.headText(this.season),
      };

      this.form.addEventListener('submit', this.onSubmit);
      if (this.width) this.width.addEventListener('change', this.onWidthChange);
      if (this.aspect) this.aspect.addEventListener('change', this.onAspectChange);
      if (this.rim) this.rim.addEventListener('change', this.onRimChange);
      if (this.season) this.season.addEventListener('change', this.onUserChange);
      window.addEventListener('pageshow', this.onPageShow);

      this.prefill();
      this.loadTree();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.width) this.width.removeEventListener('change', this.onWidthChange);
      if (this.aspect) this.aspect.removeEventListener('change', this.onAspectChange);
      if (this.rim) this.rim.removeEventListener('change', this.onRimChange);
      if (this.season) this.season.removeEventListener('change', this.onUserChange);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    headText(select) {
      return select && select.options.length ? select.options[0].textContent : '';
    }

    value(select) {
      return select && select.value ? String(select.value).trim() : '';
    }

    // Baut ein Auswahlfeld neu auf und behaelt dabei den aktuellen Wert, wenn es
    // ihn weiterhin gibt — sonst spraenge das gerade gewaehlte Feld auf den
    // Platzhalter zurueck und die naechste Ebene bliebe fuer immer gesperrt.
    // BEI BEDARF (Phase D Teil 2, P1 Massnahme 6): die Liste steht vollstaendig in
    // auswahlListe(); im DOM stehen im Ruhezustand nur Platzhalter und gesetzter
    // Wert, die volle Liste kommt bei Fokus, Zeiger oder Taste (siehe unten).
    fill(select, values, placeholder, suffix) {
      if (!select) return;
      var eintraege = [];
      for (var i = 0; i < values.length; i++) {
        var val = String(values[i]);
        // NO_ASPECT bekommt seine Beschriftung statt eines nackten "-", und KEIN Suffix:
        // das Zoll-Zeichen haengt am Durchmesser-Feld, dort gibt es den Schluessel nicht.
        // Deutsche Zahl NUR in der Beschriftung (Art-Director R3, 28.09.2026: „2.25",
        // „19.5"" gegen „19,5 Zoll" im Mega-Menue) — der value bleibt unveraendert, er ist
        // der Filterwert der Route.
        var label = val.replace(/(\d)\.(\d)/g, '$1,$2');
        eintraege.push({
          wert: val,
          text: val === NO_ASPECT ? NO_ASPECT_LABEL : suffix ? label + suffix : label,
        });
      }
      auswahlListe(select, eintraege, { wert: '', text: placeholder || '' });
    }

    /* Kaskade: immer NUR die Ebenen unter der geaenderten neu aufbauen. */
    cascadeAspects() {
      if (!this.tree) return;
      var byAspect = this.value(this.width) ? this.tree.tree[this.value(this.width)] : null;
      var values = byAspect ? sortedNumericKeys(byAspect) : [];
      this.fill(this.aspect, values, this.heads.aspect);
      if (this.aspect) this.aspect.disabled = !values.length;
    }

    cascadeRims() {
      if (!this.tree) return;
      var byAspect = this.value(this.width) ? this.tree.tree[this.value(this.width)] : null;
      var byRim = byAspect && this.value(this.aspect) ? byAspect[this.value(this.aspect)] : null;
      var values = byRim ? sortedNumericKeys(byRim) : [];
      // Zoll-Zeichen wie im Readout (″, R3) — nur in der Beschriftung.
      this.fill(this.rim, values, this.heads.rim, '″');
      if (this.rim) this.rim.disabled = !values.length;
    }

    cascadeSeasons() {
      if (!this.tree || !this.season) return;
      var byAspect = this.value(this.width) ? this.tree.tree[this.value(this.width)] : null;
      var byRim = byAspect && this.value(this.aspect) ? byAspect[this.value(this.aspect)] : null;
      var values = byRim && this.value(this.rim) ? byRim[this.value(this.rim)] || [] : [];
      // Der Proxy liefert bereits die deutsche Saisonbezeichnung — sie IST der
      // Filterwert, Wert und Beschriftung sind dasselbe.
      this.fill(this.season, values, this.heads.season);
      this.season.disabled = !values.length;
    }

    onWidthChange() {
      this.ownPrefill = false;
      this.cascadeAspects();
      this.cascadeRims();
      this.cascadeSeasons();
    }

    onAspectChange() {
      this.ownPrefill = false;
      this.cascadeRims();
      this.cascadeSeasons();
    }

    onRimChange() {
      this.ownPrefill = false;
      this.cascadeSeasons();
    }

    loadTree() {
      var url = this.dataset.proxy;
      if (!url) return;
      // na=1 nimmt die Groessen OHNE Querschnitt mit auf (Schluessel NO_ASPECT). Ohne den
      // Parameter laesst available-sizes.server.ts sie per "aspectRatio IS NOT NULL"
      // lautlos fallen. Live gemessen 2026-09-01 am Proxy: der ungefilterte Baum dieses
      // Formulars waechst von 1.850 auf 2.260 Groessen. Die 410 fehlenden sind genau die
      // C-, Zoll- und Agrar-Notationen — bei den LKW-Groessen allein 28 von 84, also ein
      // Drittel der Vorlage, auf der dieser Baustein sitzt (product.reifen-lkw.json).
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'na=1';
      var self = this;
      this.form.setAttribute('aria-busy', 'true');
      fetchSizeTree(url).then(function (tree) {
        self.form.removeAttribute('aria-busy');
        // Nur uebernehmen, wenn wirklich eine Matrix zurueckkam.
        if (!tree || !tree.tree || !tree.widths || !tree.widths.length) return;
        self.applyTree(tree);
      });
    }

    applyTree(tree) {
      this.tree = tree;
      // data-width-order="metric-first" (leerer Warenkorb, Vorschau-Runde 1):
      // die metrischen Breiten (>= 100 mm, 125 … 355) vor die Zoll-, Oldtimer-
      // und Offroad-Masse. Numerisch sortiert standen dort rund 40 bis 60
      // Sondermasse ("2.5, 3.5, 4, 4.4 …") vor der ersten PKW-Breite. Die
      // Sondermasse bleiben waehlbar, nur dahinter.
      var widths = tree.widths;
      if (this.getAttribute('data-width-order') === 'metric-first') widths = metricFirst(widths);
      this.fill(this.width, widths, this.heads.width);
      // Aus den AKTUELLEN Werten neu kaskadieren: waehrend des Ladens konnte
      // aus der statischen Liste schon etwas gewaehlt worden sein.
      this.cascadeAspects();
      this.cascadeRims();
      this.cascadeSeasons();
      this.prefill();
    }

    // Vorbelegung — nur solange der Kunde noch nichts selbst gewaehlt hat und nur
    // mit Werten, die in der Liste stehen. Zwei Quellen:
    //   * der Merker "Zuletzt gesucht" (readLastSize),
    //   * die Vorgabe der Seite (data-preset-*): auf der Produktseite die Groesse
    //     DIESES Reifens (Befund pdp-reifen-groessenwechsel-nicht-vorbelegt), auf
    //     der Suchseite die Groesse aus den aktiven Filtern.
    // Reihenfolge: der Merker zuerst, die Seite als Rueckfall — ausser die Seite
    // sagt data-preset-mode="first" (Suchseite: "Groesse aendern" soll die
    // Groesse zeigen, deren Treffer gerade darueber stehen, nicht die vorige).
    // Die eigene Vorbelegung darf beim zweiten Lauf (Baum geladen) nachgezogen
    // werden; eine Wahl des Kunden (change-Ereignis) beendet das.
    prefill() {
      if (!this.width || !this.aspect || !this.rim) return;
      if (!this.ownPrefill && (this.value(this.width) || this.value(this.aspect) || this.value(this.rim))) {
        return;
      }
      var preset = this.readPreset();
      var last = readLastSize();
      var order = this.getAttribute('data-preset-mode') === 'first' ? [preset, last] : [last, preset];
      for (var i = 0; i < order.length; i++) {
        if (this.applySize(order[i])) {
          this.ownPrefill = true;
          return;
        }
      }
    }

    // true, sobald wenigstens die Breite stand — wie bisher bleibt eine halb
    // passende Groesse dann stehen, statt auf die naechste Quelle zu springen.
    applySize(size) {
      if (!size || typeof size !== 'object') return false;
      if (!selectIfPresent(this.width, size.w)) return false;
      this.cascadeAspects();
      // Ein leeres h ist der ausdrueckliche Querschnitt-Platzhalter (siehe rememberSize
      // und ap-prefilter.js) — ohne diese Ruecknahme koennte der Merker einen Zustand
      // schreiben, den er nie wieder liest. Ein FEHLENDES h bleibt ein Abbruch.
      if (!selectIfPresent(this.aspect, size.h === '' ? NO_ASPECT : size.h)) return true;
      this.cascadeRims();
      if (!selectIfPresent(this.rim, size.d)) return true;
      this.cascadeSeasons();
      if (size.s) selectIfPresent(this.season, size.s);
      return true;
    }

    readPreset() {
      var season = this.getAttribute('data-preset-season') || '';
      var text = this.getAttribute('data-preset-size');
      if (text !== null) {
        var parsed = parsePresetSize(text);
        if (parsed) parsed.s = season;
        return parsed;
      }
      var w = this.getAttribute('data-preset-w');
      var d = this.getAttribute('data-preset-d');
      if (!w || !d) return null;
      // Fehlt data-preset-h ganz, ist der Querschnitt unbekannt (Abbruch nach der
      // Breite); ein LEERES h heisst "ohne Querschnitt" wie im Merker.
      var h = this.getAttribute('data-preset-h');
      return { w: w, h: h === null ? undefined : h, d: d, s: season };
    }

    onUserChange() {
      this.ownPrefill = false;
    }

    // Zurueck-Taste (bfcache): das Formular wartet auf nichts mehr.
    onPageShow() {
      if (this.form) this.form.removeAttribute('aria-busy');
    }

    onSubmit(event) {
      if (!this.form) return;
      var width = this.value(this.width);
      var aspect = this.value(this.aspect);
      var rim = this.value(this.rim);
      // Unvollstaendig: nichts abfangen — die Felder sind `required`, der Browser
      // blockt den Submit selbst und meldet das fehlende Feld.
      if (!width || !aspect || !rim) return;

      event.preventDefault();
      var season = this.value(this.season);
      var fallback = tyreFacetUrl(width, aspect, rim, season, this.getAttribute('data-coll'));
      var params = tyreTargetParams(this, width, aspect, rim, season);
      if (params) this.form.setAttribute('aria-busy', 'true');
      // "Zuletzt gesucht" merkt sich das Ziel, das wirklich angesprungen wird.
      withSizeTarget(params, fallback, function (url) {
        rememberSize(width, aspect, rim, url);
        window.location.assign(url);
      });
    }
  }

  /**
   * Der Hauptinhalt <main id="MainContent"> als Suchwurzel — NIE das ganze
   * Dokument, wenn nach dem Kaufformular gesucht wird. Der Warenkorb-
   * Schubkasten wird in layout/theme.liquid VOR <main> gerendert und traegt
   * bei nicht leerem Korb die Partnerformulare des Montagechecks; eine
   * dokumentweite Suche findet die zuerst. Faellt <main> weg (Passwortseite,
   * fremdes Layout), bleibt das Dokument der Rueckfall — dann gibt es auch
   * keinen Schubkasten davor.
   */
  const hauptinhalt = () => document.getElementById('MainContent') || document;

  /* ----------------------------------------------------------------------
   * window.ApCartAdd — DER Warenkorb-Pfad des Themes. Jeder Knopf, der etwas
   * in den Warenkorb legt, laeuft hier durch: Hauptknopf der Kaufkarte, Karten-
   * Schnellkauf, Montagecheck- und "Passend dazu"-Knoepfe der Schublade,
   * Zubehoer auf /cart (alle ueber assets/product-form.js), Sticky-Leiste,
   * Satzpreis, Staffelpreise (hier), "Zusammen in den Warenkorb"
   * (assets/needed-with.js). Der Komplettrad-Knopf (assets/komplettrad.js)
   * nutzt die Auswertung add() direkt, weil er nach dem Ablegen weiterleitet.
   * Die Proben dazu: tests/warenkorb-rueckmeldung.test.js.
   *
   * WARUM (Betreiber 26.09.2026: "einige Warenkorb-Buttons: Sidebar oeffnet
   * nicht, keine Fehlermeldung, nix"). Live nachgestellt waren es elf stumme
   * oder irrefuehrende Faelle, alle aus demselben Muster — jeder Einstieg
   * hatte seine eigene Auswertung:
   *   - Karten-Schnellkauf, Bestand 1 und 1 liegt schon im Korb: 422, der
   *     Knopf wurde zu "AUSVERKAUFT" und blieb bis zum Neuladen gesperrt;
   *   - Montagecheck/"Passend dazu"/Zubehoer: 422 ohne jede Meldung;
   *   - Hauptknopf und Karte bei Netzfehler, 429 (Bot-Schutz) oder 500:
   *     nur ein SyntaxError in der Konsole;
   *   - "Zusammen in den Warenkorb", Hauptprodukt schon im Korb: KEINE
   *     Anfrage, kein Spinner, nichts;
   *   - Teilmenge / Korb schon voll: die Schublade blieb absichtlich zu, der
   *     Text kam 0,5–2,2 s spaeter und behauptete auch dann eine Teilablage,
   *     wenn gar nichts dazukam;
   *   - Satzpreis und "Zusammen …" ohne Doppelklick-Sperre (3 statt 2 Felgen).
   *
   * VERTRAG — jeder Klick endet in genau einem klaren Zustand:
   *   ok        alles abgelegt  -> Schublade oeffnet mit der neuen Position
   *   teil      Shopify legte trotz 422 die lieferbare Restmenge ab
   *             -> Schublade oeffnet + "Nur noch X Stueck verfuegbar – X
   *                liegen jetzt im Warenkorb"
   *   voll      nichts dazu, der Korb hatte schon alles -> Schublade oeffnet +
   *             "Sie haben bereits alle verfuegbaren X Stueck im Warenkorb"
   *   fehler    nichts abgelegt (ausverkauft, 5xx …) -> Meldung am Knopf
   *             (role=alert), Knopf sofort wieder bedienbar. Liegt der Knopf
   *             IN der Schublade, steht die Meldung mit dem Artikelnamen im
   *             festen Hinweisplatz oben — unten unter dem Kartenknopf lag sie
   *             unter dem Schubladenfuss (live 26.09.2026).
   *   netz      Netzfehler oder keine Antwort binnen 15 s -> wie fehler; kam
   *             die Ware doch an, oeffnet danach still die Schublade
   *   pruefung  Cloudflare-Pruefseite (429/403 als HTML, cf-mitigated): ein
   *             fetch kann sie nicht loesen, jede Wiederholung waere wieder
   *             429. Der Klick geht deshalb als normales Formular an
   *             /cart/add — der Browser zeigt die Pruefung, Shopify legt ab
   *             und leitet auf /cart.
   * Auf /cart gibt es keine Schublade: dort zieht die Seite nach (cartUpdate)
   * und die Meldung steht am Knopf bzw. ueber dem Montagecheck. Der Knopf
   * zeigt ab dem Klick seinen Ladezustand (Dawns Spinner), ein zweiter Klick
   * waehrend des Laufs schickt NICHTS ab.
   *
   * SHOPIFYS VERTRAG (live gemessen 26.09.2026, Belege im Befund):
   *   - Fehler entscheidet der HTTP-Status, nicht der Koerper: /cart/add mit
   *     JSON liefert 422 mit NUR {errors}, mit Formulardaten {status,
   *     message, description}; 429/5xx kommen als HTML (kein JSON!).
   *   - Bei Ueberbestand legt Shopify TROTZ 422 die lieferbare Menge ab. Ob
   *     etwas dazukam, sagt nur die Menge VOR dem Klick — Teilmenge und "Korb
   *     schon voll" sind fuer Shopify dasselbe 422. Deshalb fuehrt dieses
   *     Modul einen Korbstand (Menge je Variante): Startwert aus Liquid
   *     (window.apKorbStand, layout/theme.liquid), danach aus jedem
   *     /cart.js-Abruf und jedem vollstaendigen Korb im cartUpdate. Ist er
   *     unbekannt, steht der Teilmengen-Text — er ist in beiden Faellen wahr.
   *   - MEHRERE Positionen in einem Aufruf ("Zusammen …"): hat EINE davon
   *     keinen freien Bestand, legt Shopify ALLES ODER NICHTS ab (422, Korb
   *     unveraendert). Nur bei Ueberbestand ohne Null-Position kommt eine
   *     Teilmenge an. Deshalb wird je Position gegen den Stand VOR dem Klick
   *     verglichen; kam gar nichts dazu, werden die Positionen einzeln
   *     nachgelegt und die fehlende Zeile beim Namen genannt — vorher oeffnete
   *     die Schublade wie bei Erfolg, und die Felge fehlte (live 26.09.2026).
   *   - Shopifys Texte sind Du-Form; angezeigt werden NUR eigene Texte
   *     (window.apKaufStrings aus ap.kauf.*, Rueckfall unten).
   *
   * API
   *   ApCartAdd.kauf(auftrag) -> Promise<Ergebnis>   Klick bis Zustand
   *     auftrag { knopf, host, anfrage: items[] | FormData | (korb) -> items[],
   *               quelle, meldung?(text, art), vorSchublade?() -> Promise,
   *               aktiv?, texte?, titel?, mindestMenge?, hinweis?: () -> text }
   *     Eine Funktion als anfrage bekommt den FRISCHEN Korb (korb.menge(id)
   *     -> Zahl, null = unbekannt) und rechnet erst dann ("Zusammen …").
   *   ApCartAdd.add(anfrage, { sections?, sectionsUrl?, texte? })
   *       -> Promise<{ status: 'ok'|'teil'|'voll'|'fehler'|'netz'|'pruefung',
   *                    data?, imKorb?, vorher?, cart?, sections?, meldung }>
   *     nur die Auswertung (Komplettrad). Bei teil/voll sind Kopfzaehler
   *     und Schublade bereits neu gezeichnet (cartUpdate), geoeffnet wird
   *     nichts.
   *   ApCartAdd.abgleich(variantId) -> Promise<{ imKorb, cart }>
   *   ApCartAdd.meldung(host, text, art)   Meldezeile am Knopf
   *   ApCartAdd.laedt(knopf, an)            Ladezustand eines Knopfs
   *   ApCartAdd.fehlerText(data), ApCartAdd.mengeImKorb(cart, id)
   *   ApCartAdd.frist                        Zeitlimit je Anfrage in ms (15000)
   * -------------------------------------------------------------------- */
  function cartBasisUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  // Die EINE Stelle, die die Hinzufuegen-Adresse kennt (JSON-Anfrage und der
  // Formular-Rueckfall bei der Cloudflare-Pruefung).
  function addUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  }

  function fehlerText(data) {
    if (!data) return '';
    const e = data.errors;
    if (typeof e === 'string' && e) return e;
    if (e && typeof e === 'object') {
      const teile = [];
      Object.keys(e).forEach((k) => {
        const v = e[k];
        if (Array.isArray(v)) teile.push(v.join(' '));
        else if (typeof v === 'string') teile.push(v);
      });
      if (teile.length) return teile.join(' ');
    }
    return data.description || data.message || '';
  }

  // Fehler ist, was der HTTP-Status sagt — und zur Sicherheit auch ein
  // Fehlerkoerper hinter einem 200 (errors bzw. /cart/add.js-Form mit status).
  function istFehlerAntwort(res, data) {
    if (res && res.ok === false) return true;
    if (!data) return false;
    if (data.errors) return true;
    return typeof data.status === 'number' && data.status >= 400;
  }

  // Cloudflares Pruefseite ("Verifying your connection …"): 429/403 mit
  // HTML-Koerper bzw. Kopf cf-mitigated (live mitgeschnitten 26.09.2026).
  // Ein fetch kann sie nicht loesen — nur eine echte Seitennavigation.
  function istPruefseite(res, data) {
    if (!res || (res.status !== 429 && res.status !== 403)) return false;
    const kopf = res.headers && typeof res.headers.get === 'function' ? res.headers : null;
    if (kopf && kopf.get('cf-mitigated')) return true;
    if (kopf && String(kopf.get('content-type') || '').indexOf('text/html') !== -1) return true;
    return !data;
  }

  function mengeImKorb(cart, variantId) {
    const id = String(variantId);
    const items = (cart && cart.items) || [];
    return items.reduce((summe, item) => {
      if (!item) return summe;
      const vid = item.variant_id != null ? item.variant_id : item.id;
      return String(vid) === id ? summe + (Number(item.quantity) || 0) : summe;
    }, 0);
  }

  /* --- Texte ------------------------------------------------------------
   * Quelle ist window.apKaufStrings (layout/theme.liquid, Locale ap.kauf.*,
   * de/en). Die Konstanten hier sind nur der Rueckfall, falls das Layout sie
   * nicht liefert — eine leere Meldung waere wieder "nix passiert".
   * [n] ist die Stueckzahl, [titel] der Artikelname; *Eins ist die Einzahl
   * (n = 1). */
  // TODO i18n: Rueckfalltexte; die echten stehen in locales/*.json (ap.kauf).
  const KAUF_TEXTE = {
    teil: 'Nur noch [n] Stück verfügbar – [n] liegen jetzt im Warenkorb.',
    teilEins: 'Nur noch 1 Stück verfügbar – es liegt jetzt im Warenkorb.',
    voll: 'Sie haben bereits alle verfügbaren [n] Stück im Warenkorb.',
    vollEins: 'Das letzte verfügbare Stück liegt bereits in Ihrem Warenkorb.',
    teilMehrere:
      'Nicht alle Artikel waren in der gewünschten Menge verfügbar – der Warenkorb zeigt, was jetzt darin liegt.',
    schonImKorb: 'Die gewählte Menge liegt bereits in Ihrem Warenkorb.',
    nichtVerfuegbar: 'Dieser Artikel ist derzeit nicht verfügbar und konnte nicht in den Warenkorb gelegt werden.',
    fehler: 'Das hat leider nicht geklappt. Bitte versuchen Sie es erneut.',
    hinzugefuegt: 'In den Warenkorb gelegt.',
    variante: 'Bitte wählen Sie zuerst eine lieferbare Variante.',
    zeileVoll: '[titel]: Der gesamte verfügbare Bestand liegt bereits in Ihrem Warenkorb.',
    zeileAus: '[titel] ist derzeit nicht verfügbar.',
    zeileTeil: '[titel]: Nur noch [n] Stück verfügbar – so viele liegen jetzt im Warenkorb.',
    restDazu: 'Alle übrigen Artikel liegen jetzt im Warenkorb.',
    einArtikel: 'Ein Artikel',
    pruefung: 'Kurze Sicherheitsprüfung – Sie werden gleich weitergeleitet.',
    zuViele: 'Gerade gehen sehr viele Anfragen ein. Bitte laden Sie die Seite neu und versuchen Sie es dann erneut.',
  };

  // Eigene Vorlagen eines Aufrufers (heute nur der Komplettrad-Knopf: "Nicht
  // alle 4 Felgen …") gehen vor; sonst die Locale, zuletzt der Rueckfall.
  function kaufTexte(eigene) {
    const g = window.apKaufStrings || {};
    const t = {};
    Object.keys(KAUF_TEXTE).forEach((k) => {
      t[k] = (typeof g[k] === 'string' && g[k]) || KAUF_TEXTE[k];
    });
    const e = eigene || {};
    if (e.teilmenge) {
      t.teil = e.teilmenge;
      t.teilEins = '';
      t.voll = e.teilmenge;
      t.vollEins = '';
    }
    if (e.fehler) t.fehler = e.fehler;
    return t;
  }

  function mitMenge(vorlage, eins, n) {
    const text = n === 1 && eins ? eins : vorlage;
    return String(text || '').split('[n]').join(String(n));
  }

  // [n] zuerst: ein Artikelname darf "[n]" enthalten, ohne ersetzt zu werden.
  function zeilenText(vorlage, titel, n) {
    return String(vorlage || '')
      .split('[n]')
      .join(String(n))
      .split('[titel]')
      .join(titel);
  }

  /* --- Korbstand (Menge je Variante VOR dem Klick) ----------------------
   * null = unbekannt. Ein Abruf, der vor einem Hinzufuegen startete und
   * danach ankommt, zaehlt nicht: er koennte den Stand NACH dem Klick
   * zeigen und aus einer Teilmenge ein "war schon voll" machen. */
  const korb = { mengen: null, adds: 0, laufend: 0 };

  function korbSetzen(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const m = {};
    cart.items.forEach((item) => {
      if (!item) return;
      const vid = String(item.variant_id != null ? item.variant_id : item.id);
      m[vid] = (m[vid] || 0) + (Number(item.quantity) || 0);
    });
    korb.mengen = m;
  }

  function korbAusPaaren(paare) {
    if (!Array.isArray(paare)) return;
    korbSetzen({
      items: paare
        .filter((p) => Array.isArray(p) && p.length >= 2)
        .map((p) => ({ variant_id: p[0], quantity: p[1] })),
    });
  }

  function korbMenge(variantId) {
    if (!korb.mengen) return null;
    return korb.mengen[String(variantId)] || 0;
  }

  function korbVergessen() {
    korb.mengen = null;
  }

  function gleicheMengen(a, b) {
    const ka = Object.keys(a).filter((k) => a[k] > 0);
    const kb = Object.keys(b).filter((k) => b[k] > 0);
    return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
  }

  /* --- Zeitlimit ----------------------------------------------------------
   * Haengt /cart/add, /cart.js oder der Sections-Abruf (Funkloch, Captive
   * Portal), drehte der Spinner endlos, und die Doppelklick-Sperre gab jedem
   * weiteren Klick denselben haengenden Lauf zurueck. Jetzt bricht jede
   * Anfrage nach ApCartAdd.frist (15 s) ab und endet als 'netz'. */
  const FRIST_MS = 15000;

  function mitFrist(start) {
    const ms = ApCartAdd.frist > 0 ? ApCartAdd.frist : FRIST_MS;
    const steuerung = typeof AbortController === 'function' ? new AbortController() : null;
    let uhr = null;
    const frist = new Promise((_, reject) => {
      uhr = setTimeout(() => {
        if (steuerung) steuerung.abort();
        const e = new Error('Keine Antwort binnen ' + ms + ' ms');
        e.name = 'ApZeit';
        reject(e);
      }, ms);
    });
    let lauf;
    try {
      lauf = Promise.resolve(start(steuerung ? steuerung.signal : undefined));
    } catch (e) {
      lauf = Promise.reject(e);
    }
    return Promise.race([lauf, frist]).then(
      (x) => {
        clearTimeout(uhr);
        return x;
      },
      (e) => {
        clearTimeout(uhr);
        throw e;
      }
    );
  }

  function holeKorb() {
    return mitFrist((signal) =>
      fetch(cartBasisUrl() + '.js', { headers: { Accept: 'application/json' }, signal: signal }).then((r) => r.json())
    )
      .then((cart) => (cart && Array.isArray(cart.items) ? cart : null))
      .catch(() => null);
  }

  function korbAuffrischen() {
    const beiStart = korb.adds;
    return holeKorb().then((cart) => {
      if (cart && korb.adds === beiStart && korb.laufend === 0) korbSetzen(cart);
      return cart;
    });
  }

  korbAusPaaren(window.apKorbStand);

  // Zurueck-Taste (bfcache) oder Rueckkehr aus einem anderen Tab: der Korb
  // kann sich dort geaendert haben. Frisch lesen — und weicht er vom
  // bekannten Stand ab, ALLES nachziehen, was daran haengt: Kopfzaehler,
  // Schublade und das Mengenfeld der Kaufkarte (product-info.js
  // fetchQuantityRules auf cartUpdate). Vorher wurde nur der interne Stand
  // erneuert: nach "Korb im anderen Tab geleert" stand das Feld weiter auf
  // 0, der Kopfzaehler auf 1, und der Kaufknopf meldete bei einem
  // lieferbaren Artikel "nicht verfuegbar" (live 26.09.2026, Debica).
  function rueckkehrAbgleich() {
    const alt = korb.mengen;
    korbVergessen();
    korbAuffrischen().then((cart) => {
      if (!cart || !alt || !korb.mengen || gleicheMengen(alt, korb.mengen)) return;
      zeichneKorbNeu(cart, null, 'ap-korb-abgleich');
    });
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) rueckkehrAbgleich();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') rueckkehrAbgleich();
  });

  /* --- Anfrage ----------------------------------------------------------- */
  function holeSections(ids) {
    // OHNE Accept-Kopf: "/cart" mit Accept: application/json IST /cart.js —
    // Shopify liefert dann das Warenkorb-JSON statt der Sections (Vorschau
    // 25.09.2026 gemessen, der Kopfzaehler blieb leer).
    return mitFrist((signal) => fetch(cartBasisUrl() + '?sections=' + ids, { signal: signal }).then((r) => r.json()))
      .then((s) => (s && typeof s === 'object' ? s : null))
      .catch(() => null);
  }

  function istFormData(anfrage) {
    return typeof FormData !== 'undefined' && anfrage instanceof FormData;
  }

  // Welche Varianten in welcher Menge angefragt sind — fuer die Auswertung.
  // Eine fehlende Menge ist bei Shopify 1; 0 bleibt 0 (Produktseite, deren
  // Mengenfeld auf "Bestand minus Korb" gekappt ist). titel nennt die Zeile
  // in einer Meldung ("Zusammen …"); an Shopify geht er nicht. roh ist die
  // Position, wie der Aufrufer sie schickte (Id-Typ, Eigenschaften) — so geht
  // sie auch beim einzelnen Nachlegen raus.
  function positionen(anfrage) {
    if (istFormData(anfrage)) {
      const id = anfrage.get('id');
      const roh = anfrage.get('quantity');
      const n = roh === null ? 1 : parseInt(roh, 10);
      return id ? [{ id: String(id), quantity: Number.isFinite(n) ? n : 1, titel: '' }] : [];
    }
    return (Array.isArray(anfrage) ? anfrage : [])
      .filter((p) => p && p.id)
      .map((p) => {
        const n = p.quantity === undefined ? 1 : parseInt(p.quantity, 10);
        return { id: String(p.id), quantity: Number.isFinite(n) ? n : 1, titel: p.titel ? String(p.titel) : '', roh: p };
      });
  }

  // Nur was Shopify kennt — Hilfsfelder wie titel bleiben hier.
  function shopifyPosition(p) {
    const o = { id: p.id };
    if (p.quantity !== undefined) o.quantity = p.quantity;
    if (p.properties) o.properties = p.properties;
    if (p.selling_plan) o.selling_plan = p.selling_plan;
    return o;
  }

  function sendeAdd(anfrage, opt, signal) {
    if (istFormData(anfrage)) {
      // Dawns Weg (product-form.js): Formulardaten, damit Eigenschaften,
      // Abo-Plan und Geschenkkarten-Empfaenger unveraendert mitgehen.
      if (opt.sections && !anfrage.has('sections')) {
        anfrage.append('sections', opt.sections);
        anfrage.append('sections_url', opt.sectionsUrl || window.location.pathname);
      }
      return fetch(addUrl(), {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: anfrage,
        signal: signal,
      });
    }
    const body = { items: (Array.isArray(anfrage) ? anfrage : []).map(shopifyPosition) };
    if (opt.sections) {
      body.sections = opt.sections;
      body.sections_url = opt.sectionsUrl || window.location.pathname;
    }
    return fetch(addUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
      signal: signal,
    });
  }

  // Anfrage samt Koerper innerhalb der Frist: haengt der Koerper, zaehlt das
  // genauso wie eine haengende Antwort.
  function sendeMitFrist(anfrage, opt) {
    return mitFrist((signal) => sendeAdd(anfrage, opt, signal).then((res) => lies(res).then((data) => ({ res, data }))));
  }

  /* Rueckfall bei der Cloudflare-Pruefung: dieselbe Ablage als ECHTES
   * Formular (application/x-www-form-urlencoded an /cart/add ohne .js). Der
   * Browser navigiert, zeigt die interaktive Pruefung, Shopify legt ab und
   * leitet auf /cart. Formulardaten gehen unveraendert mit (ohne die
   * sections-Felder des fetch-Wegs), Positionen als items[n][id]/[quantity]. */
  function nativAbsenden(anfrage) {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = addUrl().replace(/\.js$/, '');
    form.hidden = true;
    const feld = (name, wert) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(wert);
      form.appendChild(input);
    };
    if (istFormData(anfrage)) {
      anfrage.forEach((wert, name) => {
        if (name === 'sections' || name === 'sections_url' || typeof wert !== 'string') return;
        feld(name, wert);
      });
    } else {
      (Array.isArray(anfrage) ? anfrage : []).forEach((p, i) => {
        feld('items[' + i + '][id]', p.id);
        feld('items[' + i + '][quantity]', p.quantity === undefined ? 1 : p.quantity);
        if (p.properties) {
          Object.keys(p.properties).forEach((k) => feld('items[' + i + '][properties][' + k + ']', p.properties[k]));
        }
      });
    }
    document.body.appendChild(form);
    HTMLFormElement.prototype.submit.call(form);
  }

  function veroeffentliche(daten) {
    const pub = getPublish();
    if (!pub) return Promise.resolve();
    try {
      const lauf = pub(cartUpdateEventName(), daten);
      return lauf && typeof lauf.then === 'function' ? lauf.catch(() => {}) : Promise.resolve();
    } catch (e) {
      // Ein Abonnent warf — alles Weitere (Zaehler, Schublade) laeuft trotzdem.
      console.error(e);
      return Promise.resolve();
    }
  }

  function setzeZaehler(html) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    const neu = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (neu) bubble.innerHTML = neu.innerHTML;
  }

  // Kopfzaehler aus mitgelieferten Sections, sonst ein eigener kleiner Abruf.
  function zaehlerNeu(sections) {
    if (sections && sections['cart-icon-bubble']) {
      setzeZaehler(sections['cart-icon-bubble']);
      return Promise.resolve();
    }
    if (!document.getElementById('cart-icon-bubble')) return Promise.resolve();
    return holeSections('cart-icon-bubble').then((s) => {
      if (s) setzeZaehler(s['cart-icon-bubble']);
    });
  }

  // Kopfzaehler und alles, was an cartUpdate haengt (Schubladen-Positionen,
  // Warenkorbseite, Versandleiste, "2 im Warenkorb" der Buybox, Mengenfeld der
  // Kaufkarte), auf den echten Stand ziehen — OHNE die Schublade zu oeffnen.
  // Liefert das Promise der Abonnenten (die Warenkorbseite wartet darauf).
  // apKauf markiert die eigenen Rundrufe: nur FREMDE Aenderungen raeumen die
  // Meldezeile der Warenkorbseite ab (beobachteKorb unten).
  function zeichneKorbNeu(cart, sections, quelle) {
    const daten = { source: quelle || 'ap-cart-add', cartData: cart };
    if (!quelle) daten.apKauf = true;
    return Promise.all([zaehlerNeu(sections), veroeffentliche(daten)]);
  }

  function abgleich(variantId) {
    return holeKorb().then((cart) => {
      if (!cart) return { imKorb: 0, cart: null };
      korbSetzen(cart);
      const imKorb = mengeImKorb(cart, variantId);
      if (imKorb > 0) zeichneKorbNeu(cart);
      return { imKorb, cart };
    });
  }

  function lies(res) {
    if (!res || typeof res.json !== 'function') return Promise.resolve(null);
    // 429 (Bot-Schutz) und 5xx kommen als HTML — kein JSON, kein Absturz.
    return Promise.resolve()
      .then(() => res.json())
      .catch(() => null);
  }

  /* MEHRERE Positionen je Zeile gegen den Stand VOR dem Klick lesen. Nur
   * eine gewachsene Zeile ist eine Teilablage; jede Zeile, die weniger bekam
   * als angefragt, wird beim Namen genannt. */
  function mehrereAuswerten(pos, vorher, cart, texte) {
    const zeilen = pos.map((p) => {
      const vor = vorher[p.id];
      const nach = mengeImKorb(cart, p.id);
      return { p: p, vor: vor, nach: nach, dazu: typeof vor === 'number' ? nach - vor : null };
    });
    if (zeilen.some((z) => z.dazu === null)) {
      // Stand vor dem Klick unbekannt: nur grob — "der Warenkorb zeigt, was
      // jetzt darin liegt" ist in jedem Fall wahr.
      return zeilen.some((z) => z.nach > 0)
        ? { status: 'teil', meldung: texte.teilMehrere }
        : { status: 'fehler', meldung: texte.nichtVerfuegbar };
    }
    const gewachsen = zeilen.some((z) => z.dazu > 0);
    const knapp = zeilen.filter((z) => z.dazu < z.p.quantity);
    if (!knapp.length) return { status: 'ok', nichtsDazu: false };
    const saetze = knapp.map((z) => {
      const titel = z.p.titel || texte.einArtikel;
      if (z.dazu > 0) return zeilenText(texte.zeileTeil, titel, z.nach);
      if (z.vor > 0) return zeilenText(texte.zeileVoll, titel, z.nach);
      return zeilenText(texte.zeileAus, titel, 0);
    });
    if (gewachsen && knapp.length < zeilen.length) saetze.push(texte.restDazu);
    let status = 'fehler';
    if (gewachsen) status = 'teil';
    else if (zeilen.some((z) => z.vor > 0)) status = 'voll';
    return { status: status, meldung: saetze.join(' '), nichtsDazu: !gewachsen };
  }

  // Nach einer Ablehnung (oder wenn gar nichts anzufragen war): echten Stand
  // lesen und in genau einen Zustand uebersetzen. Die Sections fuer die
  // Schublade kommen im selben Atemzug, damit sie ohne weitere Pause aufgeht.
  // cartVorab: ein gerade gelesener Korb (spart den zweiten /cart.js-Abruf).
  function nachlauf(pos, vorher, opt, texte, art, grund, cartVorab) {
    return Promise.all([
      cartVorab ? Promise.resolve(cartVorab) : holeKorb(),
      opt.sections ? holeSections(opt.sections) : Promise.resolve(null),
    ]).then(([cart, sections]) => {
      if (!cart) return { status: 'fehler', imKorb: 0, grund: grund || '', meldung: texte.fehler };
      korbSetzen(cart);
      const erste = pos[0];
      const imKorb = erste ? mengeImKorb(cart, erste.id) : 0;
      const ergebnis = {
        status: 'fehler',
        imKorb: imKorb,
        vorher: erste ? vorher[erste.id] : null,
        cart: cart,
        sections: sections,
        grund: grund || '',
        meldung: texte.nichtVerfuegbar,
      };
      if (!pos.length) {
        // "Zusammen in den Warenkorb": alles Angefragte lag schon drin.
        ergebnis.status = 'voll';
        ergebnis.meldung = texte.schonImKorb;
      } else if (pos.length > 1) {
        Object.assign(ergebnis, mehrereAuswerten(pos, vorher, cart, texte));
      } else if (imKorb > 0) {
        const v = vorher[erste.id];
        const nichtsDazu = art === 'leer' || (typeof v === 'number' && imKorb <= v);
        ergebnis.status = nichtsDazu ? 'voll' : 'teil';
        ergebnis.meldung = nichtsDazu
          ? mitMenge(texte.voll, texte.vollEins, imKorb)
          : mitMenge(texte.teil, texte.teilEins, imKorb);
      }
      // Einzeln nachgelegt kam doch alles an: wie ein Erfolg darstellen.
      if (ergebnis.status === 'ok') ergebnis.data = Object.assign({}, cart, { sections: sections });
      return ergebnis;
    });
  }

  // Positionen einzeln nacheinander ablegen (Reihenfolge wie angefragt, das
  // Hauptprodukt zuerst). Bricht bei Netzfehler, Pruefseite oder 5xx ab — die
  // Auswertung danach liest ohnehin den echten Korb.
  function einzelnNachlegen(pos) {
    korb.adds += 1;
    return pos.reduce(
      (kette, p) =>
        kette.then((weiter) => {
          if (!weiter) return false;
          return sendeMitFrist([p.roh || p], {})
            .then(({ res, data }) => !istPruefseite(res, data) && !(res && res.status >= 500))
            .catch(() => false);
        }),
      Promise.resolve(true)
    );
  }

  function abgelehnt(pos, vorher, opt, texte, grund) {
    if (pos.length < 2 || opt.einzeln) return nachlauf(pos, vorher, opt, texte, 'abgelehnt', grund);
    return holeKorb().then((cart) => {
      const bekannt = pos.every((p) => typeof vorher[p.id] === 'number');
      const nichtsDazu = !!cart && bekannt && pos.every((p) => mengeImKorb(cart, p.id) <= vorher[p.id]);
      if (!nichtsDazu) return nachlauf(pos, vorher, opt, texte, 'abgelehnt', grund, cart || undefined);
      // Alles oder nichts: eine Zeile ohne freien Bestand hielt ALLE auf.
      return einzelnNachlegen(pos).then(() =>
        nachlauf(pos, vorher, Object.assign({}, opt, { einzeln: true }), texte, 'einzeln', grund)
      );
    });
  }

  function pruefseite(anfrage, opt, texte, grund) {
    if (opt.nativ !== false) {
      try {
        nativAbsenden(anfrage);
        return { status: 'pruefung', imKorb: 0, grund: grund, meldung: texte.pruefung };
      } catch (e) {
        console.error(e);
      }
    }
    return { status: 'fehler', imKorb: 0, grund: grund, meldung: texte.zuViele };
  }

  // Die Menge einer einzelnen Position neu setzen (Nachlage bei Feld 0).
  function mitNeuerMenge(anfrage, n) {
    if (istFormData(anfrage)) {
      anfrage.set('quantity', String(n));
      return anfrage;
    }
    return (Array.isArray(anfrage) ? anfrage : []).map((p) => Object.assign({}, p, { quantity: n }));
  }

  function cartAdd(anfrage, optionen) {
    const opt = optionen || {};
    const texte = kaufTexte(opt.texte);
    const pos = positionen(anfrage);
    const vorher = {};
    pos.forEach((p) => {
      vorher[p.id] = korbMenge(p.id);
    });
    // Direkte Aufrufer (Komplettrad) bekommen bei teil/voll Kopfzaehler und
    // Schublade schon neu gezeichnet; kauf() zeichnet selbst (neuZeichnen
    // false) — sonst holte die Schublade ihre Section ein zweites Mal.
    const abschluss = (ergebnis) => {
      ergebnis.neuGezeichnet =
        opt.neuZeichnen !== false && (ergebnis.status === 'teil' || ergebnis.status === 'voll')
          ? zeichneKorbNeu(ergebnis.cart, ergebnis.sections)
          : Promise.resolve();
      return ergebnis;
    };

    // Nichts anzufragen (Mengenfeld 0, weil der ganze Bestand im Korb liegt;
    // "Zusammen …" ohne Nachlage): kein 422 provozieren, sondern den Stand
    // zeigen.
    const nutzbar = pos.filter((p) => p.quantity > 0);
    if (!nutzbar.length) {
      return holeKorb().then((cart) => {
        // Feld auf 0, der Korb ist aber LEER (anderswo geleert, Zurueck-Taste
        // mit wiederhergestelltem Feldwert): NIE "nicht verfuegbar" melden —
        // die Mindestmenge ablegen. Live 26.09.2026 meldete der Knopf bei
        // einem lieferbaren Einzelstueck "nicht verfuegbar", ohne je eine
        // Anfrage zu schicken.
        if (cart && pos.length === 1 && mengeImKorb(cart, pos[0].id) === 0 && !opt.nachgelegt) {
          korbSetzen(cart);
          const n = Math.max(1, parseInt(opt.mindestMenge, 10) || 1);
          return cartAdd(mitNeuerMenge(anfrage, n), Object.assign({}, opt, { nachgelegt: true }));
        }
        return nachlauf(pos, vorher, opt, texte, 'leer', '', cart || undefined).then(abschluss);
      });
    }

    korb.adds += 1;
    korb.laufend += 1;
    let offen = true;
    const fertig = () => {
      if (offen) {
        offen = false;
        korb.laufend -= 1;
      }
    };

    return sendeMitFrist(anfrage, opt).then(
      ({ res, data }) => {
        fertig();
        if (!istFehlerAntwort(res, data)) {
          // Der Korb hat sich geaendert: Stand vergessen, der Nachlauf des
          // Aufrufers (kauf) liest ihn still neu.
          korbVergessen();
          return { status: 'ok', data: data || {} };
        }
        const grund = fehlerText(data);
        const httpCode = res && typeof res.status === 'number' ? res.status : 0;
        const code = data && typeof data.status === 'number' && data.status >= 400 ? data.status : httpCode;
        if (istPruefseite(res, data)) return pruefseite(anfrage, opt, texte, grund);
        // 422 = Bestand/Menge (ausverkauft, Teilmenge, Korb voll). Ohne
        // Statuszahl, aber mit Shopify-Fehlerkoerper: genauso behandeln.
        if (data && (code === 422 || (!code && grund))) return abgelehnt(pos, vorher, opt, texte, grund).then(abschluss);
        // 429 mit JSON (Shopifys eigene Drossel): "erneut versuchen" hilft
        // hier nicht sofort — ehrlich sagen, was hilft.
        if (code === 429) return { status: 'fehler', imKorb: 0, grund: grund, meldung: texte.zuViele };
        // 5xx, 404 … — nichts abgelegt.
        return { status: 'fehler', imKorb: 0, grund: grund, meldung: texte.fehler };
      },
      (err) => {
        fertig();
        console.warn('Cart add network error:', err);
        return {
          status: 'netz',
          zeit: !!(err && err.name === 'ApZeit'),
          pos: pos,
          vorherAlle: vorher,
          meldung: texte.fehler,
        };
      }
    );
  }

  /* --- Darstellung ------------------------------------------------------- */

  // Ladezustand: Dawns Spinner im Knopf (".button.loading", base.css) — fehlt
  // er im Markup (Sticky-Leiste, Satzpreis, "Zusammen …", Komplettrad), wird
  // er einmal eingesetzt. Er steht absolut, versteckt per .hidden: kein CLS.
  function spinnerVon(knopf) {
    return Array.prototype.find.call(knopf.children || [], (c) => c.classList && c.classList.contains('loading__spinner'));
  }

  function laedt(knopf, an) {
    if (!knopf) return;
    if (an) {
      knopf.setAttribute('aria-busy', 'true');
      knopf.setAttribute('aria-disabled', 'true');
    } else {
      knopf.removeAttribute('aria-busy');
      knopf.removeAttribute('aria-disabled');
    }
    knopf.classList.toggle('is-loading', !!an);
    if (!knopf.classList.contains('button')) return;
    let spinner = spinnerVon(knopf);
    if (!spinner && an) {
      spinner = document.createElement('div');
      spinner.className = 'loading__spinner hidden';
      spinner.setAttribute('aria-hidden', 'true');
      spinner.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>';
      knopf.appendChild(spinner);
    }
    knopf.classList.toggle('loading', !!an);
    if (spinner) spinner.classList.toggle('hidden', !an);
  }

  // Eine Meldung, die ausserhalb des Sichtfelds entsteht, ins Bild holen —
  // 'nearest' bewegt nichts, wenn sie schon zu sehen ist.
  function insBild(el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    try {
      el.scrollIntoView({ block: 'nearest' });
    } catch (e) {
      /* aeltere Browser ohne Optionen: dann eben nicht */
    }
  }

  /* Meldezeile am Knopf: vorhandene Zeile [data-ap-kauf-meldung], sonst wird
   * sie beim ersten Text angelegt (role=alert — ein eingefuegtes alert wird
   * samt Inhalt angesagt). Ist der Host nicht mehr im Dokument
   * (Warenkorbseite: der Montagecheck wurde gerade neu gezeichnet), steht die
   * Meldung ueber dem Montagecheck ([data-ap-kauf-seite]).
   *
   * WO die Zeile entsteht, bestimmt das Markup — so, dass der Knopf NICHT
   * springt (live 26.09.2026: wer nach einer Meldung erneut klickte, traf an
   * derselben Stelle die Meldung statt des Knopfs):
   *   - [data-ap-kauf-meldung-ziel] an einem Vorfahren: die Zeile wird dort
   *     eine eigene volle Zeile (Montagecheck-Partner: vorher hing sie IN der
   *     flex:0-Formularhuelle, die auf die Textbreite wuchs, Titel und Preis
   *     auf 0 px drueckte und die Seite waagerecht ueberlaufen liess);
   *   - data-ap-kauf-meldung-ort="vor" am Host: VOR dem Formular (Karten —
   *     ihre Kaufgruppe ist unten verankert, eine Zeile darunter schob den
   *     Knopf um 83 px nach oben). */
  function meldung(host, text, art) {
    const imDokument = !!(host && host.isConnected);
    const ziel = imDokument && typeof host.closest === 'function' ? host.closest('[data-ap-kauf-meldung-ziel]') : null;
    const ort = imDokument ? ziel || host : null;
    let zeile = ort ? ort.querySelector('[data-ap-kauf-meldung]') : null;
    if (!zeile && !imDokument) zeile = document.querySelector('[data-ap-kauf-seite]');
    if (!zeile) {
      if (!text || !ort) return;
      zeile = document.createElement('p');
      zeile.className = 'ap-kauf-meldung';
      zeile.setAttribute('role', 'alert');
      zeile.setAttribute('data-ap-kauf-meldung', '');
      if (!ziel && host.getAttribute('data-ap-kauf-meldung-ort') === 'vor' && host.firstElementChild) {
        host.insertBefore(zeile, host.firstElementChild);
      } else {
        ort.appendChild(zeile);
      }
    }
    zeile.classList.toggle('ap-kauf-meldung--fehler', art === 'fehler');
    zeile.classList.toggle('ap-kauf-meldung--hinweis', art === 'hinweis');
    zeile.classList.toggle('ap-kauf-meldung--erfolg', art === 'erfolg');
    zeile.textContent = text || '';
    if (text) insBild(zeile);
  }

  function zeigeMeldung(auftrag, text, art) {
    if (typeof auftrag.meldung === 'function') {
      try {
        auftrag.meldung(text || '', art || null);
        return;
      } catch (e) {
        console.error(e);
      }
    }
    meldung(auftrag.host, text, art);
  }

  // Wohin fuehrt ein Klick? Ein Knopf IN der Schublade bleibt in der
  // Schublade. Auf /cart (Warenkorbseite) keine Schublade — sie laege
  // vollflaechig ueber der Seite, die schon aktuell ist (Vorschau 25.09.2026).
  function kaufZiel(host) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && host && drawer.contains(host)) return 'schublade';
    if (document.getElementById('main-cart-items')) return 'seite';
    return drawer ? 'schublade' : 'ohne';
  }

  function schubladenHinweis(drawer, text, art) {
    if (!drawer) return;
    if (typeof drawer.zeigeHinweis === 'function') {
      drawer.zeigeHinweis(text, art);
      return;
    }
    const slot = drawer.querySelector('[data-ap-cart-hinweis]');
    if (slot) slot.textContent = text || '';
  }

  function schubladeAuf(drawer) {
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  // Schublade mit frischem Inhalt zeichnen und oeffnen. renderContents
  // oeffnet selbst; wirft es (Section fehlt), geht sie trotzdem auf — der
  // Artikel LIEGT im Korb, eine Fehlermeldung waere hier falsch.
  // Liefert true, wenn der Inhalt frisch gezeichnet wurde: nur dann darf der
  // Rundruf danach "schubladeFrisch" sagen (sonst holte niemand den neuen
  // Inhalt, und die Schublade zeigte den alten Korb).
  function schubladeZeichnen(drawer, antwort, ids) {
    const sections = antwort && antwort.sections;
    if (sections && sections['cart-drawer'] && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(antwort);
        return Promise.resolve(true);
      } catch (e) {
        console.error(e);
      }
    }
    return (ids ? holeSections(ids) : Promise.resolve(null)).then((frisch) => {
      if (frisch && frisch['cart-drawer'] && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents({ sections: frisch });
          return true;
        } catch (e) {
          console.error(e);
        }
      }
      schubladeAuf(drawer);
      return false;
    });
  }

  function sectionIds(drawer) {
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return null;
    try {
      return drawer
        .getSectionsToRender()
        .map((s) => s.id)
        .join(',');
    } catch (e) {
      return null;
    }
  }

  function ohneSchublade(antwort) {
    const notification = document.querySelector('cart-notification');
    if (notification && antwort && antwort.sections && typeof notification.renderContents === 'function') {
      notification.renderContents(antwort);
      return;
    }
    window.location.assign(cartBasisUrl());
  }

  // Der Artikelname fuer eine Meldung im Schubladen-Hinweis: der Kunde
  // klickte dort in einer Liste — "Das hat nicht geklappt" allein sagt nicht,
  // WAS. Quelle: auftrag.titel oder data-ap-kauf-titel am Host bzw. Vorfahren.
  function titelVon(auftrag) {
    if (auftrag.titel) return String(auftrag.titel);
    const host = auftrag.host;
    const el = host && typeof host.closest === 'function' ? host.closest('[data-ap-kauf-titel]') : null;
    return el ? el.getAttribute('data-ap-kauf-titel') || '' : '';
  }

  function zusatzVon(auftrag) {
    if (typeof auftrag.hinweis !== 'function') return '';
    try {
      return String(auftrag.hinweis() || '');
    } catch (e) {
      return '';
    }
  }

  /* FOKUS nach einem Klick IN der offenen Schublade: renderContents ersetzt
   * den geklickten Knopf, open() kehrt bei offener Schublade sofort zurueck —
   * der Fokus lag danach auf <body> hinter einer inerten Seite, ein
   * Vorleseprogramm verlor die Position, und der neu eingesetzte Hinweis
   * wurde nicht zuverlaessig angesagt. Jetzt: Fokus auf den Hinweis (steht
   * einer) bzw. auf den Dialog, Dawns Fokusfalle mit dem NEUEN Inhalt. */
  function fokusInSchublade(drawer, k) {
    if (!drawer || !k.inSchublade || !k.warOffen) return;
    setTimeout(() => {
      const aktiv = document.activeElement;
      if (aktiv && aktiv !== document.body && aktiv.isConnected && drawer.contains(aktiv)) return;
      const slot = drawer.querySelector('[data-ap-cart-hinweis]');
      const ziel = slot && slot.textContent ? slot : drawer.querySelector('.drawer__inner');
      if (!ziel) return;
      if (!ziel.hasAttribute('tabindex')) ziel.setAttribute('tabindex', '-1');
      if (typeof trapFocus === 'function') {
        try {
          trapFocus(document.getElementById('CartDrawer') || drawer, ziel);
        } catch (e) {
          /* ohne Falle bleibt wenigstens der Fokus */
        }
      }
      if (document.activeElement !== ziel && typeof ziel.focus === 'function') ziel.focus();
    });
  }

  // Warenkorbseite: der Montagecheck wurde neu gezeichnet, der Knopf ist weg —
  // der Fokus gehoert an die Meldung darueber, nicht an <body>.
  function fokusSeite(auftrag) {
    if (auftrag.host && auftrag.host.isConnected) return;
    const zeile = document.querySelector('[data-ap-kauf-seite]');
    if (!zeile || !zeile.textContent) return;
    if (!zeile.hasAttribute('tabindex')) zeile.setAttribute('tabindex', '-1');
    if (typeof zeile.focus === 'function') zeile.focus();
  }

  function praesentiere(ergebnis, auftrag, k) {
    const texte = kaufTexte(auftrag.texte);
    const st = ergebnis.status;
    const drawer = k.drawer;
    const zusatz = zusatzVon(auftrag);
    const mitZusatz = (text) => [text, zusatz].filter(Boolean).join(' ');
    const vor = () =>
      typeof auftrag.vorSchublade === 'function' ? Promise.resolve(auftrag.vorSchublade()) : Promise.resolve();

    if (st === 'pruefung') {
      // Die Seite navigiert gleich (Formular-Rueckfall) — bis dahin sagen, warum.
      zeigeMeldung(auftrag, ergebnis.meldung, 'hinweis');
      return Promise.resolve(ergebnis);
    }

    if (st === 'ok') {
      const daten = {
        source: auftrag.quelle || 'ap-cart-add',
        productVariantId: auftrag.variantId,
        cartData: ergebnis.data,
        apKauf: true,
      };
      // Der Rundruf traegt den VOLLEN Korb (ein /cart.js, das ohnehin den
      // Korbstand erneuert): Versandleiste und Mindestmengen-Riegel lasen aus
      // der blossen Add-Antwort weder Summe noch Stueckzahl und holten je
      // einen eigenen /cart.js (mitgeschnitten 27.09.2026). Faellt der Abruf
      // aus, bleibt es bei der Add-Antwort wie bei Dawn.
      const mitKorb = () =>
        korbAuffrischen().then((cart) => {
          if (cart) daten.cartData = cart;
        });
      if (k.ziel === 'schublade' && drawer) {
        return vor()
          .then(() => schubladeZeichnen(drawer, ergebnis.data, k.ids))
          .then((frisch) => {
            // Die Schublade ist aus der Antwort gezeichnet — ihr eigener
            // Nachabruf auf cartUpdate (cart-drawer-items) waere doppelt.
            if (frisch) daten.schubladeFrisch = true;
            else zaehlerNeu(ergebnis.data && ergebnis.data.sections);
            if (zusatz) schubladenHinweis(drawer, zusatz, 'hinweis');
            fokusInSchublade(drawer, k);
            // Nicht abwarten: die Schublade steht, der Knopf ist gleich frei.
            mitKorb().then(() => veroeffentliche(daten));
            return ergebnis;
          });
      }
      if (k.ziel === 'seite') {
        // Kopfzaehler: auf /cart tauscht kein Abonnent #cart-icon-bubble —
        // vorher zeigte er nach "In den Warenkorb gelegt." den alten Stand.
        return mitKorb()
          .then(() => Promise.all([zaehlerNeu(ergebnis.data && ergebnis.data.sections), veroeffentliche(daten)]))
          .then(() => {
            zeigeMeldung(auftrag, mitZusatz(texte.hinzugefuegt), 'erfolg');
            fokusSeite(auftrag);
            return ergebnis;
          });
      }
      veroeffentliche(daten);
      ohneSchublade(ergebnis.data);
      return Promise.resolve(ergebnis);
    }

    if (st === 'teil' || st === 'voll') {
      const text = mitZusatz(ergebnis.meldung);
      if (k.ziel === 'schublade' && drawer) {
        const mitSchublade = ergebnis.sections && ergebnis.sections['cart-drawer'];
        return vor()
          // Die Sections kamen schon mit dem Nachlauf — kein zweiter Abruf.
          .then(() => schubladeZeichnen(drawer, { sections: ergebnis.sections }, mitSchublade ? null : k.ids))
          .then((frisch) => {
            // Frisch gezeichnet: der Kopfzaehler kam mit den Sections, der
            // Rundruf sagt "schon aktuell". Sonst (Section fehlte) wie
            // ausserhalb der Schublade: Zaehler holen, Abonnenten ziehen nach.
            if (frisch) veroeffentliche({ source: 'ap-cart-add', cartData: ergebnis.cart, apKauf: true, schubladeFrisch: true });
            else zeichneKorbNeu(ergebnis.cart, ergebnis.sections);
            schubladenHinweis(drawer, text, 'hinweis');
            fokusInSchublade(drawer, k);
            return ergebnis;
          });
      }
      return zeichneKorbNeu(ergebnis.cart, ergebnis.sections).then(() => {
        zeigeMeldung(auftrag, text, 'hinweis');
        fokusSeite(auftrag);
        return ergebnis;
      });
    }

    const text = ergebnis.meldung || texte.fehler;
    const schublade = k.inSchublade ? document.querySelector('cart-drawer') : null;
    if (schublade) {
      // Knopf IN der Schublade: die Zeile unter einer Karte lag unter dem
      // festen Schubladenfuss und war praktisch unsichtbar. Der feste
      // Hinweisplatz oben ist immer im Bild — mit dem Artikelnamen.
      const titel = titelVon(auftrag);
      schubladenHinweis(schublade, titel ? titel + ': ' + text : text, 'fehler');
      return Promise.resolve(ergebnis);
    }
    zeigeMeldung(auftrag, text, 'fehler');
    return Promise.resolve(ergebnis);
  }

  // Lief ein Klick in das Zeitlimit, kann die Ware trotzdem angekommen sein
  // (die Antwort ging unterwegs verloren). Still nachsehen — lag sie doch im
  // Korb, die Meldung zuruecknehmen und die Schublade zeigen.
  function spaeterPruefen(ergebnis, auftrag, k) {
    const pos = ergebnis.pos || [];
    const vorher = ergebnis.vorherAlle || {};
    if (!pos.length) return;
    holeKorb().then((cart) => {
      if (!cart) return;
      const dazu = pos.some((p) => typeof vorher[p.id] === 'number' && mengeImKorb(cart, p.id) > vorher[p.id]);
      if (!dazu) return;
      korbSetzen(cart);
      zeigeMeldung(auftrag, '', null);
      if (k.ziel === 'schublade' && k.drawer) {
        schubladenHinweis(k.drawer, '');
        schubladeZeichnen(k.drawer, null, k.ids).then((frisch) =>
          frisch
            ? veroeffentliche({ source: 'ap-cart-add', cartData: cart, apKauf: true, schubladeFrisch: true })
            : zeichneKorbNeu(cart, null)
        );
        return;
      }
      zeichneKorbNeu(cart, null);
    });
  }

  // "Zusammen …" rechnet mit dem FRISCHEN Korb: die Liquid-Attribute
  // (data-cart-quantity) und der gemerkte Stand koennen veraltet sein
  // (zweiter Tab, Zurueck-Taste) — daraus wurde "liegt bereits im Korb" bei
  // leerem Korb bzw. ein zweiter Satz Zentrierringe (live 26.09.2026).
  function anfrageMitKorb(rechne) {
    return holeKorb().then((cart) => {
      if (cart && korb.laufend === 0) korbSetzen(cart);
      return rechne({ menge: (id) => (cart ? mengeImKorb(cart, id) : korbMenge(id)) });
    });
  }

  const laeuft = new WeakMap();

  function kauf(auftrag) {
    const a = auftrag || {};
    const knopf = a.knopf || null;
    // Doppelklick-Sperre: waehrend des Laufs liefert jeder weitere Klick
    // denselben Lauf zurueck — es geht KEINE zweite Anfrage raus.
    if (knopf && laeuft.has(knopf)) return laeuft.get(knopf);

    const ziel = kaufZiel(a.host);
    const schublade = document.querySelector('cart-drawer');
    const drawer = ziel === 'schublade' ? schublade : null;
    const k = {
      ziel: ziel,
      drawer: drawer,
      // Auf /cart den Kopfzaehler gleich mit der Ablage holen.
      ids: drawer
        ? sectionIds(drawer)
        : ziel === 'seite' && document.getElementById('cart-icon-bubble')
          ? 'cart-icon-bubble'
          : null,
      inSchublade: !!(schublade && a.host && schublade.contains(a.host)),
      warOffen: !!(schublade && schublade.classList.contains('active')),
    };
    // Rueckgabe des Fokus beim Schliessen: NIE an einen Knopf in der
    // Schublade — der wird gleich ersetzt. Dann bleibt der alte Ausloeser.
    if (drawer && !k.inSchublade && typeof drawer.setActiveElement === 'function') {
      drawer.setActiveElement(a.aktiv || knopf || document.activeElement);
    }

    zeigeMeldung(a, '', null);
    // Der Hinweis des VORIGEN Klicks gilt nicht mehr — sonst stand oben
    // "Nur noch 20 Stueck …" zu einem anderen Artikel, waehrend die neue
    // Meldung unter dem Schubladenfuss verschwand (live 26.09.2026).
    if (schublade) schubladenHinweis(schublade, '');
    if (ziel === 'seite') {
      // Die Meldung des vorigen Klicks ueber dem Montagecheck gilt nicht mehr.
      const seitenZeile = document.querySelector('[data-ap-kauf-seite]');
      if (seitenZeile) seitenZeile.textContent = '';
    }
    laedt(knopf, true);

    const lauf = Promise.resolve()
      .then(() => (typeof a.anfrage === 'function' ? anfrageMitKorb(a.anfrage) : a.anfrage))
      .then((anfrage) =>
        cartAdd(anfrage, {
          sections: k.ids,
          sectionsUrl: a.sectionsUrl,
          texte: a.texte,
          mindestMenge: a.mindestMenge,
          neuZeichnen: false,
        })
      )
      .then((ergebnis) =>
        Promise.resolve()
          .then(() => praesentiere(ergebnis, a, k))
          .catch((e) => {
            // Die Darstellung warf. Liegt der Artikel im Korb, ist die
            // Schublade die ehrliche Antwort — keine Fehlermeldung.
            console.error(e);
            if (drawer && ['ok', 'teil', 'voll'].indexOf(ergebnis.status) !== -1) schubladeAuf(drawer);
            else zeigeMeldung(a, kaufTexte(a.texte).fehler, 'fehler');
            return ergebnis;
          })
      )
      .catch((e) => {
        console.error(e);
        const meldungText = kaufTexte(a.texte).fehler;
        zeigeMeldung(a, meldungText, 'fehler');
        return { status: 'fehler', meldung: meldungText };
      })
      .then((ergebnis) => {
        laedt(knopf, false);
        if (knopf) laeuft.delete(knopf);
        if (ergebnis && ergebnis.status === 'netz' && ergebnis.zeit) spaeterPruefen(ergebnis, a, k);
        return ergebnis;
      });

    if (knopf) laeuft.set(knopf, lauf);
    return lauf;
  }

  // Den Stand aktuell halten, wenn andere Wege den Korb aendern: ein
  // vollstaendiger Korb (cart.js /cart/change, unser Abgleich) wird uebernommen,
  // jede andere Aenderung macht den Stand unbekannt. Eine FREMDE Aenderung
  // (Menge, "Auf 2 erhoehen", zweiter Tab) raeumt ausserdem die Meldezeile der
  // Warenkorbseite ab — "In den Warenkorb gelegt." las sich sonst wie die
  // Antwort auf diese Aenderung.
  (function beobachteKorb() {
    const sub = getSubscribe();
    if (!sub) return;
    try {
      sub(cartUpdateEventName(), (daten) => {
        const cart = daten && daten.cartData;
        if (cart && Array.isArray(cart.items) && typeof cart.item_count === 'number') korbSetzen(cart);
        else korbVergessen();
        if (!(daten && daten.apKauf)) {
          const zeile = document.querySelector('[data-ap-kauf-seite]');
          if (zeile && zeile.textContent) zeile.textContent = '';
        }
      });
    } catch (e) {
      /* ohne pubsub bleibt es beim Startwert */
    }
  })();

  /* --- Positions-Eigenschaften bestehender Zeilen ------------------------
   * (Fahrzeugdaten Felgen, 29.09.2026: Feld 2.1/2.2 aus dem Warenkorb-Hinweis
   * an jede Felgenposition, assets/ap-fahrzeugschein.js.) Der gemeinsame Weg
   * fuer eine Aenderung OHNE Mengenaenderung — kein eigener fetch auf
   * /cart/change.js in einer Komponente (Probe tests/fahrzeugdaten-felgen.test.js).
   *
   * ApCartAdd.eigenschaften({ zeilen: (korb) => [{key, quantity, properties}], quelle })
   *   zeilen() bekommt den FRISCHEN Korb (/cart.js) und nennt die Zeilen samt
   *   ihrer NEUEN Eigenschaften. Shopify ERSETZT das ganze properties-Objekt —
   *   der Aufrufer liefert deshalb die zusammengefuehrten (alte + neue). Die
   *   Menge bleibt (quantity der Zeile). Nacheinander ueber /cart/change.js,
   *   je mit Zeitlimit (frist), adressiert ueber den key der Zeile; danach EIN
   *   Rundruf cartUpdate mit dem letzten vollen Korb (Schublade, Warenkorbseite
   *   und Montagecheck ziehen nach, der Korbstand bleibt aktuell) — das Promise
   *   wartet auf die Abonnenten.
   *   -> { status: 'ok' | 'leer' | 'teil' | 'fehler' | 'netz' | 'pruefung', geaendert, cart }
   *      leer = keine Zeile zu aendern (schon erledigt, z. B. zweiter Tab) — auch dann EIN
   *      Rundruf mit dem frischen Korb, denn die Ansicht war veraltet. */
  function changeUrl() {
    const u = (window.routes && window.routes.cart_change_url) || '/cart/change';
    return /\.js$/.test(u) ? u : u + '.js';
  }

  function eigenschaften(auftrag) {
    const a = auftrag || {};
    return holeKorb().then((cart) => {
      if (!cart) return { status: 'netz', geaendert: 0, cart: null };
      let zeilen = [];
      try {
        zeilen = (typeof a.zeilen === 'function' ? a.zeilen(cart) : a.zeilen) || [];
      } catch (e) {
        console.error(e);
        zeilen = [];
      }
      zeilen = (Array.isArray(zeilen) ? zeilen : []).filter((z) => z && z.key && z.properties && typeof z.properties === 'object');
      if (!zeilen.length) {
        // Nichts zu aendern: die Ansicht ist aelter als der Korb (anderer Tab, Zeile entfernt) —
        // trotzdem EIN Rundruf mit dem frischen Korb, damit Schublade und Seite den Stand zeigen.
        korbSetzen(cart);
        return veroeffentliche({ source: a.quelle || 'ap-eigenschaften', cartData: cart }).then(() => ({ status: 'leer', geaendert: 0, cart: cart }));
      }
      let letzter = cart;
      let geaendert = 0;
      let fehler = '';
      korb.laufend += 1;
      return zeilen
        .reduce(
          (kette, z) =>
            kette.then((weiter) => {
              if (!weiter) return false;
              const menge = parseInt(z.quantity, 10);
              const body = { id: String(z.key), properties: z.properties };
              if (Number.isFinite(menge) && menge > 0) body.quantity = menge;
              return mitFrist((signal) =>
                fetch(changeUrl(), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                  body: JSON.stringify(body),
                  signal: signal,
                }).then((res) => lies(res).then((data) => ({ res, data })))
              ).then(
                ({ res, data }) => {
                  if (istPruefseite(res, data)) {
                    fehler = 'pruefung';
                    return false;
                  }
                  if (istFehlerAntwort(res, data) || !data || !Array.isArray(data.items)) {
                    fehler = 'fehler';
                    return false;
                  }
                  letzter = data;
                  geaendert += 1;
                  return true;
                },
                () => {
                  fehler = 'netz';
                  return false;
                }
              );
            }),
          Promise.resolve(true)
        )
        .then(() => {
          korb.laufend -= 1;
          korbSetzen(letzter);
          const status = !fehler ? 'ok' : geaendert > 0 ? 'teil' : fehler;
          const rundruf = geaendert > 0 ? veroeffentliche({ source: a.quelle || 'ap-eigenschaften', cartData: letzter }) : Promise.resolve();
          return rundruf.then(() => ({ status: status, geaendert: geaendert, cart: letzter }));
        });
    });
  }

  const ApCartAdd = {
    kauf: kauf,
    add: cartAdd,
    eigenschaften: eigenschaften,
    abgleich: abgleich,
    fehlerText: fehlerText,
    mengeImKorb: mengeImKorb,
    meldung: meldung,
    laedt: laedt,
    texte: kaufTexte,
    frist: FRIST_MS,
  };
  if (!window.ApCartAdd) window.ApCartAdd = ApCartAdd;

  /* ----------------------------------------------------------------------
   * <sticky-atc>
   * Beobachtet den ECHTEN Kaufknopf des Hauptformulars UND den Hauptpreis der
   * Kaufkarte und zeigt eine feste Leiste am unteren Rand, solange KEINER von
   * beiden im Sichtfeld ist — darueber oder darunter. Toggles both the [hidden] attribute and the
   * .is-visible class so CSS can animate the entrance (respecting
   * prefers-reduced-motion).
   *
   * Enhanced add-to-cart: the [data-sticky-atc-add] button reads the MAIN
   * product form's variant id + quantity and POSTs via ApCartAdd, then
   * publishes cartUpdate and opens the cart drawer. Fehler und Teilmengen
   * landen in [data-sticky-atc-msg] (role=alert).
   * -------------------------------------------------------------------- */
  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.target = null;
      this.observer = null;
      this.addBtn = null;
      this.priceObserver = null;
      this.stateObserver = null;
      this.stickyPriceEl = null;
      this.mainPriceEl = null;
      this.mainButton = null;
      this.qtyInput = null;
      this.onAddClick = this.onAddClick.bind(this);
      this.onNotifyClick = this.onNotifyClick.bind(this);
      this.syncPrice = this.syncPrice.bind(this);
      this.syncQtyLabel = this.syncQtyLabel.bind(this);
      this.syncState = this.syncState.bind(this);
      this.onAlarmReady = this.onAlarmReady.bind(this);
    }

    connectedCallback() {
      // WAS die Leiste beobachtet: den ECHTEN Kaufknopf des Hauptformulars.
      //
      // Bis 24.09.2026 stand hier eine Selektorliste
      // 'product-form, form[action*="/cart/add"], .product-form'. Sie trifft in
      // BAUMREIHENFOLGE, und das PayPal-Ratenformular des price-Blocks
      // (form.installment, 0 px hoch, action="/cart/add") steht in der Buybox
      // VOR buy_buttons — beobachtet wurde also eine unsichtbare Zeile ueber
      // dem Knopf. Live (Conti, 390x844): Leiste AN bei y 0–200, AUS 400–600,
      // wieder AN ab 800, waehrend der echte Knopf noch im Bild war — zwei
      // Kaufknoepfe uebereinander. Jetzt: der Knopf selbst, in getrennten
      // Aufrufen (erst die Buybox der Hauptsection, dann irgendein
      // product-form), das Formular nur noch als Rueckfall.
      //
      // Die Leiste steht, solange der Knopf NICHT schneidet — ob er darueber
      // oder darunter liegt, ist Absicht (Betreiberentscheidung 24.09.2026):
      // mobil traegt sie so im ersten Bildschirm den Preis und die Menge, sie
      // verschwindet, solange der echte Knopf zu sehen ist, und kommt nach dem
      // Vorbeiscrollen wieder.
      //
      // Art-Director R2 (26.09.2026, Spec B10): seit dem Design-Umbau steht der
      // Hauptpreis im ersten Bildschirm (mobil ~700 px, LIVE lag er bei
      // 856–1.079 px unter dem Falz). Die Leiste lag bei scrollY 0 dann UEBER
      // der Kaufkarte und deckte PAngV-Zeile, Lagerstand und teils den Preis
      // selbst ab. Jetzt zaehlt zusaetzlich der Hauptpreis (.ap-kauf-preis):
      // ist er im Bild, steht die Leiste nicht — sie bringt ihn ja nur dorthin,
      // wo er fehlt. Die Betreiberabsicht bleibt: liegt der Preis beim Laden
      // unter dem Falz, steht die Leiste wie bisher im ersten Bildschirm.
      //
      // NUR IM HAUPTINHALT SUCHEN (08.09.2026). Der Warenkorb-Schubkasten
      // steht in layout/theme.liquid VOR <main> und bringt bei nicht leerem
      // Korb ueber den Montagecheck eigene Kaufformulare mit
      // (snippets/ap-cart-part.liquid rendert je Partner ein <product-form>).
      // Dokumentweit gewann dieses Partnerformular: beobachtet wurde ein
      // Knoten im GESCHLOSSENEN Schubkasten, und der Kaufknopf legte den
      // PARTNER in den Korb. Ein ausdrueckliches data-target bleibt
      // unangetastet: wer es setzt, meint genau diesen Knoten.
      const wurzel = this.dataset.target ? document : hauptinhalt();
      this.mainButton = this.mainSubmitButton();
      this.target = this.dataset.target
        ? wurzel.querySelector(this.dataset.target)
        : this.mainButton || this.mainProductForm();

      this.addBtn = this.querySelector('[data-sticky-atc-add]');
      this.labelEl = this.addBtn ? this.addBtn.querySelector('[data-sticky-atc-label]') : null;
      this.notifyBtn = this.querySelector('[data-sticky-atc-notify]');
      this.msgEl = this.querySelector('[data-sticky-atc-msg]');
      if (this.addBtn) this.addBtn.addEventListener('click', this.onAddClick);
      if (this.notifyBtn) this.notifyBtn.addEventListener('click', this.onNotifyClick);

      // Mirror the main product price into the sticky bar so it tracks variant
      // changes — tyre sizes have different prices, so a static bar price is wrong.
      this.setupPriceMirror();
      // Knopftext "4× in den Warenkorb" folgt dem echten Mengenfeld.
      this.setupQtyMirror();
      // Ausverkauft / nicht verfuegbar: Zustand des Hauptknopfs spiegeln.
      this.setupStateMirror();
      // Der Lieferalarm-Chip wird erst nach der Auskunft der App freigeschaltet
      // (assets/ap-alarm.js, Ereignis 'ap-alarm:ready') — mobil NACH dem ersten
      // showBar(). Ohne erneuten Abgleich stand dann ein toter „Ausverkauft“-Knopf
      // statt „Benachrichtigen“ in der Leiste.
      document.addEventListener('ap-alarm:ready', this.onAlarmReady);

      // No product form on the page, or no IntersectionObserver: stay hidden.
      if (!this.target || !('IntersectionObserver' in window)) {
        this.hideBar();
        return;
      }

      // Die Standlinie der schwebenden Bedienelemente (--ap-float-floor in
      // automotive.css) hing an DIESER Klasse — also daran, dass diese Seite
      // ueberhaupt eine Kaufleiste bekommen kann, und NICHT daran, ob die
      // Leiste gerade steht. Am Sichtbarkeitsstand haengend sprangen
      // Chat-Blase, "Nach oben" und das Google-Abzeichen bei jedem Ein- und
      // Ausblenden um 136 px, auf dem Handy zweimal je Scrolldurchgang
      // (nachgemessen 08.09.2026 am Live-Shop, Zahlen in automotive.css).
      // Seit dem Schwebe-System (26.09.2026) misst assets/ap-schwebe.js die
      // Leiste LIVE (.is-visible/[hidden] dieses Elements) und alles
      // Schwebende GLEITET mit; die Klasse bleibt der Rueckfall ohne Messung.
      document.body.classList.add('ap-has-sticky-atc');

      // Der Hauptpreis der Kaufkarte (nur ohne ausdrueckliches data-target).
      // BAND-REGEL (Art-Director Feinschliff II R1, 27.09.2026 — Blocker
      // „kein Kaufknopf im ersten Bildschirm"): R2 blendete die Leiste aus,
      // solange der Hauptpreis IRGENDWO im Bild war. Der echte Knopf liegt
      // aber unter dem Falz (390: 1.585 px, 1440: 1.273 px) — im ersten
      // Bildschirm gab es keine Kaufmoeglichkeit mehr, LIVE stand die Leiste
      // schon bei scrollY 0. Jetzt zaehlt der Preis nur, wo die Leiste ihn
      // DECKEN wuerde: im unteren Band aus Leistenhoehe + BAND_LUFT. Steht der
      // Preis samt PAngV-Zeile darueber (Desktop immer, Handy nach der
      // flacheren Buehne), steht die Leiste — sie deckt dann nichts, was R2
      // schuetzen wollte. Liegt er im Band, weicht sie wie bisher.
      //
      // ERSTER BILDSCHIRM HAT VORRANG (Art-Director Feinschliff II R2,
      // 28.09.2026 — Blocker „kein Kaufknopf im ersten Bildschirm" auf JEDEM
      // kuerzeren Handy): die Band-Regel allein hielt die Leiste bei 360x780,
      // 375x667, 390x700 und auf Motorrad-PDPs auch bei 390x844 aus, weil der
      // Preisblock ins Band ragte — der echte Knopf lag bei y 1.013–1.834, es
      // gab keine Kaufhandlung im ersten Bildschirm. Jetzt gilt zweierlei:
      //   1. Beobachtet wird nur die PREISZEILE samt PAngV (.ap-kauf-preis__row),
      //      nicht Achse/Lieferumfang, Staffel-Hinweis oder Raten darum herum.
      //   2. Steht der Nutzer oben (scrollY <= OBEN_PX) und ist der echte Knopf
      //      NICHT im Bild, steht die Leiste — auch wenn der Preis im Band liegt.
      //      Sie wiederholt Preis, „je Reifen", EU-Klasse, Summe und PAngV-Zeile,
      //      die Pflichtangabe bleibt also sichtbar (LIVE-Verhalten). Der Vorrang
      //      haelt, bis der echte Knopf einmal im Bild war (kein Aus/An beim
      //      ersten Wischen), und greift wieder, sobald der Nutzer oben ist.
      this.priceTarget = this.dataset.target ? null : this.mainPriceBlock();
      this.btnInView = false;
      this.priceInBand = false;
      this.vorrangOben = false;
      // RUHIGE LEISTE (Feinschliff 3 FIX, 04.10.2026, gegnerische Pruefung Mobil, major): „oben" ist
      // eine Zone mit Hysterese (obenSonde) und kein Scroll-Lesen mehr; die Leiste wechselt im schnellen
      // Wischen nicht (entscheiden). leisteAn = der zuletzt GESETZTE Zustand.
      // ANFANGSZUSTAND (P1 M2): vorher scrollY beim Einhaengen (Profil M0: connectedCallback 444 ms PDP). „oben" meldet die
      // Sonde; fehlt die Meldung, liest anfangOben() einmal in der ersten Beobachter-Meldung (nach dem Layout). Band und
      // Sonde zuerst ohne Lesung (lageVorlaeufig), gemessen nach dem ersten Layout (nachLayout -> watchPriceBand).
      this.oben = undefined;
      this.lageVorlaeufig = true;
      this.leisteAn = false;
      this.letzterScroll = 0;
      this.leisteUhr = 0;
      this.onBandResize = this.onBandResize.bind(this);
      this.onObenScroll = this.onObenScroll.bind(this);

      // GANZ IM BILD (Art-Director R3, 28.09.2026): der echte Knopf zaehlt nur, wenn er
      // vollstaendig und mindestens 8 px ueber der Unterkante steht. Motorrad 1440x900: Knopf
      // y 858–914, am Falz um 14 px angeschnitten — er galt als „im Bild", die Leiste blieb
      // aus, im ersten Bildschirm stand weder ein ganzer Knopf noch die Leiste.
      this.observer = new IntersectionObserver(
        (entries) => {
          // Nur fuer den Knopf selbst — der Rueckfall (ganzes Formular) kann hoeher als das
          // Bild sein und zaehlt wie bisher, sobald er schneidet.
          const nurKnopf = this.target === this.mainButton;
          entries.forEach((entry) => {
            const ratio = typeof entry.intersectionRatio === 'number' ? entry.intersectionRatio : 1;
            this.btnInView = entry.isIntersecting && (!nurKnopf || ratio >= 0.98);
            this.btnSichtbar = entry.isIntersecting; /* LED-FORK FP-134: ein Teil des Knopfs im Bild (TH-20) */
            this.btnGanz = this.btnInView; /* LED-FORK FP-134 */
          });
          this.entscheiden();
        },
        { root: null, rootMargin: '0px 0px -8px 0px', threshold: [0, 0.5, 0.98, 1] }
      );

      this.observer.observe(this.target);
      /* LED-FORK FP-134 Anfang (TH-20): Knopf über der Unterkante? Das nach oben offene Feld meldet auch Sprünge */
      if (this.hasAttribute('data-led-erst-nach-knopf')) {
        this.oberhalbBeob = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.btnOberhalb = entry.isIntersecting;
          });
          this.entscheiden();
        }, { rootMargin: '100000px 0px -8px 0px' });
        this.oberhalbBeob.observe(this.target);
        this.ledHandy = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null; // ab 750 px R3 (Prüfbefund TW4)
        this.hinweisBeobachten(); // TW5: Cookie-Hinweis
        this.kopfBeobachten(); // Abnahme TW5: wieder eingeblendeter Kopf
      }
      /* LED-FORK FP-134 Ende */
      // Hoehe der Leiste aus dem ResizeObserver (nach dem Layout), nie per offsetHeight (P1 Massnahme 2).
      this.hoeheBeobachten();
      // Band und Sonde: jetzt vorlaeufig OHNE Lesung (lageVorlaeufig — Band = ganzes Sichtfeld, Sonde OBEN_PX),
      // gemessen NACH dem ersten Layout (nachLayout). Oben zaehlt das Band ohnehin nicht (Vorrang des ersten
      // Bildschirms), die erste Entscheidung bleibt dieselbe.
      this.watchPriceBand();
      nachLayout(() => {
        if (!this.isConnected) return;
        this.lageVorlaeufig = false;
        this.watchPriceBand();
      });
      this.watchDotBlock();
      window.addEventListener('resize', this.onBandResize, { passive: true });
      window.addEventListener('scroll', this.onObenScroll, { passive: true });
      // Bilder der Buehne sind geladen: die Lage der Preiszeile (Hysterese „oben") einmal nachziehen — gelesen
      // im naechsten Bild nach dessen Layout, nicht im load-Rueckruf.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => nachLayout(() => this.isConnected && this.obenSonde()), { once: true });
      }
    }

    // Hoehe der Leiste aus dem ResizeObserver statt offsetHeight (P1 M2); Band nachziehen, Aenderung melden.
    hoeheBeobachten() {
      if (this.hoeheBeob || !('ResizeObserver' in window)) return;
      this.hoeheBeob = new ResizeObserver((eintraege) => {
        const e = eintraege[eintraege.length - 1];
        const box = e && e.borderBoxSize ? e.borderBoxSize[0] || e.borderBoxSize : null;
        const h = Math.round(box && box.blockSize > 0 ? box.blockSize : e && e.contentRect ? e.contentRect.height : 0);
        if (!(h > 0)) return;
        this.gemesseneHoehe = h;
        if (this.bandFuer && Math.abs(this.bandFuer.h - h) > 4) this.watchPriceBand();
        this.zustandMelden();
      });
      this.hoeheBeob.observe(this);
    }

    /* LED-FORK FP-134 Anfang (TW5): Shopifys Cookie-Hinweis (fest unten, kommt spät an <body>) deckte beim Erstbesuch
       Knopf und Leiste. Solange er steht, ist seine Oberkante die Unterkante des Bildes, und die Leiste steht über ihm
       (--led-hinweis-h, section-sticky-atc.css; BAUSTEINE §42.1). */
    hinweisBeobachten() {
      if (this.hinweisMo || !('MutationObserver' in window)) return;
      this.hinweisH = 0;
      this.onHinweis = this.hinweisMessen.bind(this);
      this.hinweisAttr = new MutationObserver(this.onHinweis);
      this.hinweisGroesse = 'ResizeObserver' in window ? new ResizeObserver(this.onHinweis) : null;
      this.hinweisMo = new MutationObserver(() => this.hinweisAnbinden());
      this.hinweisMo.observe(document.body, { childList: true });
      document.addEventListener('visitorConsentCollected', this.onHinweis);
      this.hinweisAnbinden();
    }

    hinweisAnbinden() {
      const el = document.getElementById('shopify-pc__banner');
      if (el && el !== this.hinweisEl) {
        this.hinweisEl = el;
        this.hinweisAttr.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
        if (this.hinweisGroesse) this.hinweisGroesse.observe(el);
      }
      this.hinweisMessen();
    }

    // gelesen nach dem Layout (nachLayout), nie im Scroll-Pfad; Rand, solange die Leiste über ihm Platz hat
    hinweisMessen() {
      if (this.hinweisUhr) return;
      this.hinweisUhr = true;
      nachLayout(() => {
        this.hinweisUhr = false;
        const el = this.hinweisEl;
        const vh = window.innerHeight || 0;
        let h = 0;
        if (el && el.isConnected) {
          const r = el.getBoundingClientRect();
          if (r.height > 0 && r.top < vh && r.bottom >= vh - 4) h = Math.round(vh - Math.max(0, r.top));
        }
        if (h > vh - this.leistenHoehe() - 40) h = 0;
        if (h === this.hinweisH || !this.isConnected) return;
        this.hinweisH = h;
        this.style.setProperty('--led-hinweis-h', h + 'px');
        this.randKnopf();
      });
    }

    /* Abnahme TW5: der beim Hochscrollen wieder eingeblendete Kopf (sticky-header) deckte den Hauptknopf bei
       ausgeblendeter Leiste. Eingeblendet ist seine Unterkante die Oberkante des Bildes (BAUSTEINE §45.2); die Höhe
       wird nur beim Wechsel nach dem Layout gelesen. */
    kopfBeobachten() {
      const kopf = document.querySelector('.section-header');
      if (this.kopfMo || !kopf || !('MutationObserver' in window)) return;
      this.kopfH = 0;
      this.kopfAn = null;
      const pruefen = () => {
        const an = kopf.classList.contains('shopify-section-header-sticky') && !kopf.classList.contains('shopify-section-header-hidden');
        if (an === this.kopfAn) return;
        this.kopfAn = an;
        nachLayout(() => {
          const h = this.kopfAn && kopf.isConnected ? Math.round(kopf.getBoundingClientRect().height) : 0;
          if (h === this.kopfH || !this.isConnected) return;
          this.kopfH = h;
          this.randKnopf();
        });
      };
      this.kopfMo = new MutationObserver(pruefen);
      this.kopfMo.observe(kopf, { attributes: true, attributeFilter: ['class'] });
      pruefen();
    }

    // Ränder: oben der eingeblendete Kopf, unten der Hinweis – ein zweites Beobachterpaar, solange einer steht
    randKnopf() {
      [this.randBild, this.randFeld].forEach((b) => b && b.disconnect());
      this.randBild = this.randFeld = null;
      this.randBereit = false;
      if (!((this.hinweisH > 0 || this.kopfH > 0) && this.target)) return this.entscheiden();
      const unten = ' 0px -' + (8 + (this.hinweisH || 0)) + 'px 0px';
      this.randBild = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const r = entry.boundingClientRect;
          const o = entry.rootBounds;
          this.randSichtbar = entry.isIntersecting;
          this.randGanz = entry.isIntersecting && (this.target !== this.mainButton || !(entry.intersectionRatio < 0.98));
          this.randOben = this.kopfH > 0 && !!(r && o) && r.top < o.top - 0.5; // der Kopf deckt ihn (ganz oder oben)
        });
        this.randBereit = true;
        this.entscheiden();
      }, { rootMargin: (this.kopfH > 0 ? '-' + this.kopfH + 'px' : '0px') + unten, threshold: [0, 0.5, 0.98, 1] });
      this.randFeld = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.randOberhalb = entry.isIntersecting;
        });
        this.entscheiden();
      }, { rootMargin: '100000px' + unten });
      this.randBild.observe(this.target);
      this.randFeld.observe(this.target);
    }

    // die Regel aus entscheiden() innerhalb der Ränder; am Handy zählt ein Knopf, den der Kopf deckt, als oben vorbei
    randImBild() {
      return this.ledHandy && this.ledHandy.matches ? !(this.randOberhalb && (!this.randSichtbar || this.randOben)) : !!this.randGanz;
    }
    /* LED-FORK FP-134 Ende */

    // ap:kaufleiste (P1 M10 [K24], Vertrag CLAUDE.md): detail { sichtbar, hoehe } bei jeder Aenderung, hoehe = belegter
    // Platz unten (0, wenn nicht sichtbar); letzter Stand an this.zustand. Klassen wie bisher.
    zustandMelden() {
      const sichtbar = !!this.leisteAn;
      const hoehe = sichtbar ? this.leistenHoehe() : 0;
      const alt = this.zustand;
      if (alt && alt.sichtbar === sichtbar && alt.hoehe === hoehe) return;
      this.zustand = { sichtbar: sichtbar, hoehe: hoehe };
      let ereignis;
      try {
        ereignis = new CustomEvent('ap:kaufleiste', { detail: { sichtbar: sichtbar, hoehe: hoehe } });
      } catch (err) {
        return;
      }
      document.dispatchEvent(ereignis);
    }

    // „OBEN" MIT HYSTERESE (Feinschliff 3 FIX, major): bis 04.10. galt nur scrollY <= OBEN_PX (10) als oben.
    // Die Preiszeile liegt am Seitenanfang aber noch bis scrollY ~77 im Leistenband (Reifen-PDP 390) —
    // beim Hochwischen an den Anfang: Preis im Band -> hideBar(), bei <= 10 Vorrang -> showBar(), ein
    // 80-px-Hueper der ganzen Schwebe-Spalte in 33–383 ms. Jetzt reicht die Zone „oben" bis zu der Hoehe, ab
    // der die Preiszeile das Band verlaesst (je Groessenwechsel aus mainPriceBlock() gerechnet, nie im
    // Scroll-Pfad), und ein IntersectionObserver auf eine unsichtbare Sonde dieser Hoehe am Seitenanfang
    // meldet den Wechsel — kein scrollY-Lesen im Scroll-Pfad mehr (vorher im rAF nach anderen Schreibern:
    // 14x 233 ms erzwungenes Layout bei CPU 4x).
    obenSonde() {
      if (!('IntersectionObserver' in window) || !document.body) return;
      let grenze = StickyAtc.OBEN_PX;
      const preis = this.priceTarget;
      // Vorlaeufig (beim Einhaengen, P1 Massnahme 2) ohne Lesung: Zone OBEN_PX; die gemessene Grenze kommt nach dem
      // ersten Layout (watchPriceBand aus nachLayout).
      if (preis && preis !== this.target && this.bandFuer && !this.lageVorlaeufig) {
        const unten = preis.getBoundingClientRect().bottom + (window.scrollY || window.pageYOffset || 0);
        const bandOben = this.bandFuer.vh - this.bandFuer.h - StickyAtc.BAND_LUFT;
        grenze = Math.max(StickyAtc.OBEN_PX, Math.ceil(unten - bandOben));
      }
      // Nie ueber einen Bildschirm hinaus (eine Fehlmessung soll die Leiste nicht dauerhaft festhalten).
      if (!this.lageVorlaeufig) {
        grenze = Math.min(grenze, Math.max(StickyAtc.OBEN_PX, this.bandFuer ? this.bandFuer.vh : window.innerHeight || 0));
      }
      this.obenGrenze = grenze;
      if (!this.obenEl || !this.obenEl.isConnected) {
        this.obenEl = document.createElement('div');
        this.obenEl.setAttribute('aria-hidden', 'true');
        this.obenEl.setAttribute('data-ap-oben-sonde', '');
        document.body.appendChild(this.obenEl);
        this.obenBeob = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.oben = entry.isIntersecting;
          });
          this.entscheiden();
        });
        this.obenBeob.observe(this.obenEl);
      }
      // display:block gegen Dawns div:empty; absolut am Seitenanfang, Hoehe = Zone „oben" (+1 px: bei
      // scrollY == grenze steht noch 1 px im Bild).
      this.obenEl.style.cssText =
        'display:block;position:absolute;top:0;left:0;width:1px;height:' + (grenze + 1) + 'px;visibility:hidden;pointer-events:none;';
    }

    // DOT-JAHRGAENGE (Phase B, Pruefrunde 1, 29.09.2026 — major UX): solange der Block im Bild
    // steht, weicht die Leiste. Vorher stand sie in 12–17 von 16–20 Scrollschritten ueber dem
    // Block — mit dem PRIMAERKNOPF „4× in den Warenkorb" (Artikel der Seite, andere Summe) direkt
    // ueber „Ihr Satz: 4 Reifen … · 4 Reifen in den Warenkorb" und deckte den DOT-Knopf bis 100 %
    // zu. Wer einen Satz zusammenstellt, legte ueber die Leiste etwas anderes zum anderen Preis ab.
    // GEAENDERT Feinschliff 3 FIX (04.10.2026, major): der Block zaehlt schon 300 px BEVOR er von unten ins
    // Bild kommt (rootMargin unten). Zwischen Kaufknopf und DOT-Block lagen auf der Reifen-PDP 390 nur
    // 241 px Scrollweg — Knopf raus -> Leiste an, Block rein -> Leiste aus, im Fling binnen 67–217 ms.
    watchDotBlock() {
      this.dotInView = false;
      const block = hauptinhalt().querySelector('ap-dot-jahrgaenge');
      if (!block) return;
      this.dotObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.dotInView = entry.isIntersecting;
          });
          this.entscheiden();
        },
        { root: null, rootMargin: StickyAtc.DOT_VORLAUF, threshold: 0 }
      );
      this.dotObserver.observe(block);
    }

    // Sichtbar, wenn der echte Knopf nicht im Bild ist UND entweder der
    // Hauptpreis nicht im Leistenband liegt oder der erste Bildschirm Vorrang
    // hat (siehe connectedCallback) — und nie, solange der DOT-Block im Bild ist.
    // GEAENDERT Feinschliff 3 FIX (04.10.2026, gegnerische Pruefung Mobil, major): jede Meldung der
    // Beobachter wurde SOFORT umgesetzt — im Fling wechselte die Leiste binnen 33–383 ms zweimal (in Ketten
    // bis 5x) den Zustand, und die ganze Schwebe-Spalte glitt jedes Mal 40–85 px vom Zwischenstand aus mit
    // („springt auf eine falsche Position und richtet sich wieder"). Jetzt: der SOLL-Zustand wird wie
    // bisher berechnet, umgeschaltet aber erst, wenn seit dem letzten Scroll-Ereignis WISCH_RUHE_MS
    // vergangen sind; sonst eine Uhr bis dahin, die dann mit den aktuellen Meldungen neu entscheidet. Kein
    // Lesen im Scroll-Pfad (onObenScroll merkt sich nur die Zeit).
    entscheiden() {
      // „oben" noch nicht gemeldet (Sonde): einmal den Anfangsstand holen — nach dem Layout (P1 Massnahme 2).
      if (this.oben === undefined) this.oben = this.anfangOben();
      /* LED-FORK FP-134: am Handy bis er oben vorbei ist, ab 750 px nur ganz im Bild */
      if (this.hasAttribute('data-led-erst-nach-knopf')) this.btnInView = this.ledHandy && this.ledHandy.matches ? !(this.btnOberhalb && !this.btnSichtbar) : !!this.btnGanz;
      if (this.randBild && this.randBereit) this.btnInView = this.randImBild(); /* LED-FORK FP-134 (TW5): Hinweis unten, Kopf oben */
      if (this.btnInView) {
        this.vorrangOben = false;
      } else if (this.oben && !this.hasAttribute('data-led-preis-schuetzen')) { /* LED-FORK FP-078 (sections/sticky-atc.liquid) */
        this.vorrangOben = true;
      }
      const zeigen = !(this.btnInView || this.dotInView || (this.priceInBand && !this.vorrangOben));
      if (zeigen === this.leisteAn) {
        if (this.leisteUhr) window.clearTimeout(this.leisteUhr);
        this.leisteUhr = 0;
        return;
      }
      const seit = jetztMs() - this.letzterScroll;
      if (seit < StickyAtc.WISCH_RUHE_MS) {
        if (!this.leisteUhr) {
          this.leisteUhr = window.setTimeout(() => {
            this.leisteUhr = 0;
            this.entscheiden();
          }, StickyAtc.WISCH_RUHE_MS - seit + 10);
        }
        return;
      }
      if (this.leisteUhr) window.clearTimeout(this.leisteUhr);
      this.leisteUhr = 0;
      if (zeigen) this.showBar();
      else this.hideBar();
    }

    // Scrollen: NUR die Zeit (Feinschliff 3 FIX) — „oben" meldet die Sonde, Knopf/Preis/DOT ihre Beobachter.
    onObenScroll() {
      this.letzterScroll = jetztMs();
    }

    // Eine Lesung fuer die erste Entscheidung, nur aus Beobachter-Meldungen (nach dem Layout), nie im Scroll-Pfad.
    anfangOben() {
      return (window.scrollY || window.pageYOffset || 0) <= StickyAtc.OBEN_PX;
    }

    // Hoehe der Leiste: gemessen, sobald sie einmal stand; vorher die
    // gemessenen Werte der Vorschau (390: 91 px, 1440: 85–121 px).
    // Gemessen meldet der ResizeObserver (hoeheBeobachten) — hier wird nichts gelesen (P1 Massnahme 2).
    leistenHoehe() {
      if (this.gemesseneHoehe) return this.gemesseneHoehe;
      return window.matchMedia('(min-width: 750px)').matches ? 120 : 92;
    }

    // Beobachtet den Hauptpreis NUR im unteren Band, das die Leiste belegt.
    watchPriceBand() {
      if (this.bandObserver) this.bandObserver.disconnect();
      this.bandObserver = null;
      this.priceInBand = false;
      if (!this.priceTarget || this.priceTarget === this.target) {
        this.obenSonde();
        return;
      }
      const BAND_LUFT = StickyAtc.BAND_LUFT;
      // Vorlaeufig (beim Einhaengen, P1 Massnahme 2) ohne Lesung: das Band ist das ganze Sichtfeld. Oben zaehlt es
      // nicht (Vorrang), und nach dem ersten Layout steht das gemessene Band (nachLayout -> watchPriceBand).
      this.bandFuer = this.lageVorlaeufig ? null : { vh: window.innerHeight, h: this.leistenHoehe() };
      const oben = this.bandFuer ? Math.max(0, Math.round(this.bandFuer.vh - this.bandFuer.h - BAND_LUFT)) : 0;
      this.bandObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.priceInBand = entry.isIntersecting;
          });
          this.entscheiden();
        },
        { root: null, rootMargin: `-${oben}px 0px 0px 0px`, threshold: 0 }
      );
      this.bandObserver.observe(this.priceTarget);
      // Feinschliff 3 FIX: die Zone „oben" (Hysterese) haengt am selben Band.
      this.obenSonde();
    }

    // Neues Band nach Groessenwechsel (Drehen, Adressleiste) oder wenn die
    // gemessene Leistenhoehe von der Annahme abweicht.
    onBandResize() {
      window.clearTimeout(this.bandTimer);
      // Gelesen wird im Bild NACH dessen Layout (nachLayout, P1 Massnahme 2), nicht im Uhr-Rueckruf.
      this.bandTimer = window.setTimeout(() => {
        nachLayout(() => {
          if (!this.isConnected) return;
          const h = this.leistenHoehe();
          if (!this.bandFuer || this.bandFuer.vh !== window.innerHeight || Math.abs(this.bandFuer.h - h) > 4) {
            this.watchPriceBand();
          }
        });
      }, 150);
    }

    // Die PREISZEILE der Kaufkarte in <main> (sections/main-product.liquid,
    // .ap-kauf-preis__row = Preis, „je Reifen" und PAngV-Zeile) — nicht der
    // ganze Preisblock mit Achse/Lieferumfang und Staffel-Hinweis (R2
    // 28.09.2026: auf Motorrad-PDPs 137 statt 74 px hoch, er endete im Band).
    // Rueckfall der ganze Block, dann der gespiegelte Hauptpreis.
    mainPriceBlock() {
      const wurzel = hauptinhalt();
      return (
        wurzel.querySelector('product-info[id^="MainProduct"] .ap-kauf-preis__row') ||
        wurzel.querySelector('.product__info-container .ap-kauf-preis__row') ||
        wurzel.querySelector('product-info[id^="MainProduct"] .ap-kauf-preis') ||
        wurzel.querySelector('.product__info-container .ap-kauf-preis') ||
        this.mainPriceEl ||
        null
      );
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.oberhalbBeob) this.oberhalbBeob.disconnect(); /* LED-FORK FP-134 */
      /* LED-FORK FP-134 Anfang (TW5): Beobachter des Cookie-Hinweises und des Kopfes */
      [this.hinweisMo, this.hinweisAttr, this.hinweisGroesse, this.kopfMo, this.randBild, this.randFeld].forEach((b) => b && b.disconnect());
      if (this.onHinweis) document.removeEventListener('visitorConsentCollected', this.onHinweis);
      this.hinweisMo = this.hinweisEl = this.kopfMo = this.randBild = this.randFeld = null;
      /* LED-FORK FP-134 Ende */
      if (this.hoeheBeob) this.hoeheBeob.disconnect();
      this.hoeheBeob = null;
      if (this.bandObserver) this.bandObserver.disconnect();
      if (this.dotObserver) this.dotObserver.disconnect();
      if (this.obenBeob) this.obenBeob.disconnect();
      if (this.obenEl && this.obenEl.parentNode) this.obenEl.parentNode.removeChild(this.obenEl);
      window.clearTimeout(this.leisteUhr);
      if (this.onBandResize) window.removeEventListener('resize', this.onBandResize);
      if (this.onObenScroll) window.removeEventListener('scroll', this.onObenScroll);
      window.clearTimeout(this.bandTimer);
      if (this.priceObserver) this.priceObserver.disconnect();
      if (this.stateObserver) this.stateObserver.disconnect();
      if (this.addBtn) this.addBtn.removeEventListener('click', this.onAddClick);
      if (this.notifyBtn) this.notifyBtn.removeEventListener('click', this.onNotifyClick);
      document.removeEventListener('ap-alarm:ready', this.onAlarmReady);
      if (this.qtyInput) {
        this.qtyInput.removeEventListener('change', this.syncQtyLabel);
        this.qtyInput.removeEventListener('input', this.syncQtyLabel);
      }
      // Sonst bliebe die angehobene Standlinie im Theme-Editor stehen, nachdem
      // die Sektion entfernt wurde.
      document.body.classList.remove('ap-has-sticky-atc');
    }

    /* LED-FORK FP-060 Anfang: Spiegel am Behaelter #price-… samt Einheit (tests/led-kaufleiste.test.js) */
    setupPriceMirror() {
      this.stickyPriceEl = this.querySelector('.ap-sticky-atc__price');
      if (!this.stickyPriceEl) return;
      this.mainPriceEl =
        document.querySelector('.product__info-container .price') ||
        document.querySelector('.product__info-wrapper .price') ||
        document.querySelector('[id^="price-"] .price, .price');
      if (!this.mainPriceEl) return;
      this.mainPriceBox = this.mainPriceEl.parentElement;
      this.syncPrice();
      if (!('MutationObserver' in window)) return;
      this.priceObserver = new MutationObserver(this.syncPrice);
      this.priceObserver.observe(this.mainPriceBox || this.mainPriceEl, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    syncPrice() {
      const box = this.mainPriceBox;
      const preis = (box && box.querySelector('.price')) || this.mainPriceEl;
      if (!preis || !this.stickyPriceEl) return;
      this.mainPriceEl = preis;
      this.stickyPriceEl.innerHTML = preis.outerHTML;
      const einheit = this.querySelector('.ap-sticky-atc__unit');
      if (einheit && box && /^price-/.test(box.id)) {
        const quelle = box.querySelector('.ap-price-unit');
        einheit.textContent = quelle ? quelle.textContent : '';
        einheit.hidden = !quelle;
      }
    }
    /* LED-FORK FP-060 Ende */

    setupQtyMirror() {
      this.qtyInput = this.mainQtyInput();
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', this.syncQtyLabel);
        this.qtyInput.addEventListener('input', this.syncQtyLabel);
      }
      this.syncQtyLabel();
    }

    // "4× in den Warenkorb" — die Leiste nennt die Menge, die sie ablegt. Die
    // Texte kommen aus Liquid (data-label-one / data-label-many mit [n]).
    syncQtyLabel() {
      if (!this.labelEl || !this.addBtn || this.addBtn.disabled) return;
      const menge = this.readQuantity();
      const eins = this.addBtn.getAttribute('data-label-one') || '';
      const viele = this.addBtn.getAttribute('data-label-many') || '';
      const text = menge > 1 && viele.indexOf('[n]') !== -1 ? viele.split('[n]').join(String(menge)) : eins;
      if (text && this.labelEl.textContent !== text) this.labelEl.textContent = text;
    }

    // Zustand des Hauptknopfs spiegeln. Liquid rendert den Anfangszustand
    // schon richtig (ausverkauft = disabled, "Ausverkauft"); bei Varianten
    // (Felgen) schaltet product-form.js toggleSubmitButton() am Hauptknopf
    // disabled und Text um — genau das beobachten wir. Vorher stand auf
    // ausverkauften Seiten neben dem Abzeichen "AUSVERKAUFT" ein aktiver
    // Knopf "In den Warenkorb", der eine leere Schublade oeffnete.
    setupStateMirror() {
      if (!this.mainButton || !this.addBtn) return;
      this.syncState();
      if (!('MutationObserver' in window)) return;
      this.stateObserver = new MutationObserver(this.syncState);
      this.stateObserver.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    syncState() {
      if (!this.mainButton || !this.addBtn) return;
      const aus = this.mainButton.hasAttribute('disabled');
      if (aus) {
        this.addBtn.disabled = true;
        const span = this.mainButton.querySelector('span');
        const text = ((span && span.textContent) || '').trim();
        if (this.labelEl && text) this.labelEl.textContent = text;
      } else {
        this.addBtn.disabled = false;
        this.syncQtyLabel();
      }
      this.syncNotify();
    }

    // Der Lieferalarm-Chip der Buybox (snippets/ap-alarm.liquid) — nur, wenn er
    // wirklich sichtbar ist: er startet hidden, erst die Auskunft der App
    // schaltet ihn frei.
    stockChip() {
      const chip = hauptinhalt().querySelector('[data-chip="stock"]');
      if (!chip || chip.closest('[hidden]')) return null;
      return chip;
    }

    // Ausverkauft UND Lieferalarm verfuegbar: "Benachrichtigen, wenn
    // lieferbar" statt eines toten Knopfs.
    syncNotify() {
      if (!this.notifyBtn || !this.addBtn) return;
      const zeigen = this.addBtn.disabled && !!this.stockChip();
      this.notifyBtn.hidden = !zeigen;
      this.addBtn.hidden = zeigen;
    }

    // Nur Chips DIESER Seite zaehlen (stockChip sucht in <main>); ein Ereignis
    // aus dem Schubkasten o. ae. gleicht trotzdem harmlos ab.
    onAlarmReady() {
      this.syncNotify();
    }

    onNotifyClick() {
      const chip = this.stockChip();
      if (!chip) {
        this.syncNotify();
        return;
      }
      chip.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      if (chip.getAttribute('aria-expanded') !== 'true') chip.click();
    }

    showBar() {
      this.syncNotify();
      const warVersteckt = this.hasAttribute('hidden');
      this.removeAttribute('hidden');
      // Force a frame so the transition runs; skip when reduced motion is on.
      this.leisteAn = true;
      if (prefersReducedMotion()) {
        this.classList.add('is-visible');
      } else {
        // Nur, wenn sie bis zum Bild nicht schon wieder weg sollte (Feinschliff 3 FIX).
        // EINFAHRT AUS [hidden] (Zusammenfuehrung 05.10.2026, gegnerische Pruefung Mobil, major): [hidden] weg und
        // .is-visible im NAECHSTEN Bild fielen in denselben Stil-Lauf — aus display:none gibt es keinen Startwert,
        // die Leiste stand im ersten Bild an der Endlage, waehrend Blase, KI und „Nach oben" ihren 200-ms-Hub erst
        // begannen (5–6 Bilder lag die Blase IN der Leiste ueber deren Kaufknopf; 7 von 8 Erstanzeigen seit
        // f5a8813 — vorher erzwangen Layout-Lesungen im Beobachter zufaellig den Zwischenstand). Jetzt rendert das
        // erste Bild die Leiste unter der Kante (translateY 110 %), .is-visible kommt im ZWEITEN Bild: sie gleitet
        // mit --ap-motion/--ap-ease ein, synchron zum Hub der Spalte (ap-schwebe.js schreibt ihn im selben
        // Mikrotask wie die Klasse). Kein erzwungenes Layout (kein offsetHeight im Scroll-Pfad).
        window.requestAnimationFrame(() => {
          if (!this.leisteAn) return;
          if (!warVersteckt) {
            this.classList.add('is-visible');
            return;
          }
          window.requestAnimationFrame(() => {
            if (this.leisteAn) this.classList.add('is-visible');
          });
        });
      }
      // Erste echte Hoehe: das Band (watchPriceBand) auf sie nachziehen.
      if (this.bandFuer && !this.gemesseneHoehe && this.onBandResize) this.onBandResize();
      this.zustandMelden();
    }

    hideBar() {
      this.leisteAn = false;
      this.classList.remove('is-visible');
      this.setAttribute('hidden', '');
      this.zustandMelden();
    }

    // Der Kaufknopf der Buybox. Getrennte Aufrufe statt einer Selektorliste
    // (Baumreihenfolge, siehe connectedCallback), eingegrenzt auf <main>.
    mainSubmitButton() {
      const wurzel = hauptinhalt();
      return (
        wurzel.querySelector('product-info[id^="MainProduct"] product-form .product-form__submit') ||
        wurzel.querySelector('product-form .product-form__submit') ||
        wurzel.querySelector('product-form [type="submit"]')
      );
    }

    // Locate the main product form on the page.
    //
    // Eingegrenzt auf <main> aus demselben Grund wie in connectedCallback: der
    // Warenkorb-Schubkasten steht davor im Dokument und traegt bei nicht
    // leerem Korb die Partnerformulare des Montagechecks. Ungegrenzt las
    // onAddClick deren name="id" und legte den Schlauch/Zentrierring/das
    // Service-Kit ab statt des angezeigten Produkts. Das Ratenformular
    // (form.installment, ebenfalls action="/cart/add") ist nie gemeint.
    mainProductForm() {
      const wurzel = hauptinhalt();
      return (
        wurzel.querySelector('product-info[id^="MainProduct"] product-form form[action*="/cart/add"]') ||
        wurzel.querySelector('product-form form[action*="/cart/add"]') ||
        wurzel.querySelector('form[action*="/cart/add"]:not(.installment)') ||
        wurzel.querySelector('.product-form')
      );
    }

    // Das Mengenfeld der Buybox. Es steht AUSSERHALB des <form> (Block
    // quantity_selector, per form="product-form-…" angebunden) — ein
    // form.querySelector findet es nicht. Genau daran scheiterte die Leiste
    // seit 57971df: Mengenfeld 4, "4× Satz" gedrueckt, im Korb landete 1
    // (live per /cart.js, Conti 4 -> 1, Felge 2 -> 1). form.elements kennt
    // auch die per form= angebundenen Felder. Rueckfall ueber das form-Attribut
    // — form.id ist durch das Feld input[name="id"] ueberschattet, deshalb
    // getAttribute. Dokumentweit wird NIE gesucht (Schubkasten, s. o.).
    mainQtyInput(form) {
      const f = form || this.mainProductForm();
      if (!f) return null;
      let feld = null;
      if (f.elements && typeof f.elements.namedItem === 'function') {
        feld = f.elements.namedItem('quantity');
        // Mehrere gleichnamige Felder liefern eine RadioNodeList.
        if (feld && !feld.tagName && typeof feld.length === 'number') feld = feld[0] || null;
      }
      if (!feld && typeof f.getAttribute === 'function') {
        const formId = f.getAttribute('id');
        if (formId) {
          feld = hauptinhalt().querySelector('input[name="quantity"][form="' + formId.replace(/"/g, '\\"') + '"]');
        }
      }
      if (!feld && typeof f.querySelector === 'function') {
        feld = f.querySelector('input[name="quantity"], .quantity__input');
      }
      return feld;
    }

    readQuantity(form) {
      const feld = form ? this.mainQtyInput(form) : this.qtyInput || this.mainQtyInput();
      const n = feld ? parseInt(feld.value, 10) : NaN;
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    /** properties[…]-Felder des Hauptformulars als Objekt (null ohne Felder); abgewaehlte Kaestchen
     *  und gesperrte Felder zaehlen nicht — wie beim Absenden des Formulars. */
    formEigenschaften(form) {
      if (!form || typeof form.querySelectorAll !== 'function') return null;
      const out = {};
      let n = 0;
      const felder = form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
      for (let i = 0; i < felder.length; i++) {
        const f = felder[i];
        if (f.disabled) continue;
        if ((f.type === 'checkbox' || f.type === 'radio') && !f.checked) continue;
        const m = /^properties\[(.+)\]$/.exec(f.name || '');
        if (!m) continue;
        out[m[1]] = f.value;
        n++;
      }
      return n ? out : null;
    }

    // Meldetexte kommen aus dem gemeinsamen Pfad (window.apKaufStrings). Die
    // Attribute data-msg-partial / data-msg-error am Host bleiben als
    // Rueckfall stehen, falls das Layout die Texte einmal nicht liefert.
    texte() {
      if (window.apKaufStrings) return null;
      return {
        teilmenge: this.getAttribute('data-msg-partial') || '',
        fehler: this.getAttribute('data-msg-error') || '',
      };
    }

    zeigeMeldung(text) {
      if (this.msgEl) this.msgEl.textContent = text || '';
      // Rueckfall-Standlinie ohne Live-Messung (section-sticky-atc.css): die Klasse ersetzt seit 28.09.2026
      // ein dokumentweites body:has(… __msg:not(:empty)) (Phase D, DT-2).
      document.body.classList.toggle('ap-sticky-atc-meldung', !!text);
    }

    // Klick bis Zustand laeuft ueber window.ApCartAdd.kauf (oben): Ladezustand,
    // Doppelklick-Sperre, Schublade bei Erfolg, Teilmenge und vollem Korb,
    // Meldung in [data-sticky-atc-msg] nur bei echtem Fehler.
    onAddClick(event) {
      if (this.addBtn && this.addBtn.disabled) return;
      // Der Knopf ist type="button" — es gibt keinen Rueckfall, der ohne uns
      // etwas taete. Deshalb endet auch "keine Variante" sichtbar statt stumm.
      if (event) event.preventDefault();
      /* LED-FORK FP-070 Anfang: am Leitprodukt mit Lichtplan kauft die Leiste die Auswahl (assets/led-baukasten.js) */
      const ledKauf = new CustomEvent('led:kauf', { cancelable: true, detail: { knopf: this.addBtn, host: this, meldung: (text) => this.zeigeMeldung(text) } });
      if (!document.dispatchEvent(ledKauf)) return;
      /* LED-FORK FP-070 Ende */
      const form = this.mainProductForm();
      const idInput = form ? form.querySelector('[name="id"]') : null;
      const variantId = idInput && idInput.value ? idInput.value : '';
      if (!variantId) {
        this.zeigeMeldung(ApCartAdd.texte(this.texte()).variante);
        return;
      }

      // Positions-Eigenschaften des Hauptformulars gehen mit (Fahrzeugwahl Felgen: _ap_fahrzeug_id,
      // _ap_passung und _ap_fahrzeug bzw. „Vorabprüfung", assets/ap-fahrzeug.js — THEME-SPEC 8
      // „in allen Hinzufuegen-Wegen"): die Leiste legt dieselbe Position in den Korb wie der Hauptknopf.
      const zeile = { id: Number(variantId) || variantId, quantity: this.readQuantity(form) };
      const eigenschaften = this.formEigenschaften(form);
      if (eigenschaften) zeile.properties = eigenschaften;
      ApCartAdd.kauf({
        knopf: this.addBtn,
        host: this,
        anfrage: [zeile],
        quelle: 'sticky-atc',
        variantId: variantId,
        aktiv: this.addBtn,
        texte: this.texte(),
        meldung: (text) => this.zeigeMeldung(text),
      });
    }
  }
  // Bis zu dieser Scrolltiefe gilt der Nutzer als „oben" (erster Bildschirm).
  StickyAtc.OBEN_PX = 10;
  // Luft ueber der Leiste fuer das Preisband (watchPriceBand) und die Zone „oben" (obenSonde).
  StickyAtc.BAND_LUFT = 8;
  // Feinschliff 3 FIX (04.10.2026): so lange nach dem letzten Scroll-Ereignis wartet ein Wechsel der Leiste
  // (im Fling wechselte sie sonst binnen 33–383 ms zweimal), und so weit unter dem Bild zaehlt der
  // DOT-Block schon als „im Bild" (Luecke Kaufknopf -> DOT-Block 241 px auf der Reifen-PDP 390).
  StickyAtc.WISCH_RUHE_MS = 150;
  StickyAtc.DOT_VORLAUF = '0px 0px 300px 0px';

  /* ----------------------------------------------------------------------
   * <tyre-set-pricing>
   * Toggles between buying a single tyre and a set (e.g. 4). On change it
   * recomputes and displays the total and writes the chosen quantity into the
   * main product form's quantity input so the add-to-cart submits the set.
   * Markup contract:
   *   - radio/select options carry data-set-pricing-qty (or data-qty) and live
   *     in .ap-set-pricing__option
   *   - unit price source: data-unit-price (in minor units, e.g. cents)
   *   - live total target: [data-set-pricing-live-total]
   * -------------------------------------------------------------------- */
  class TyreSetPricing extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onMainQtyChange = this.onMainQtyChange.bind(this);
    }

    connectedCallback() {
      this.unitPrice = parseInt(this.dataset.unitPrice || '0', 10) || 0;
      this.currency = this.dataset.currency || '';
      // Optional dedicated live-total element. We deliberately do NOT touch the
      // per-option price spans (.ap-set-pricing__total / __price) — those are
      // rendered with Shopify's | money filter in the snippet and must stay
      // correct regardless of which radio is selected.
      this.liveTotalEl = this.querySelector('[data-set-pricing-live-total]');
      // Nicht die Huelle merken, sondern den gestylten Knoten darin: sonst
      // ueberschreibt applyQty die Klasse .ap-mwst weg.
      this.mwstEl = this.querySelector('[data-set-pricing-mwst] .ap-mwst');
      // Accept either the contract attribute (data-set-pricing-qty) or the
      // original data-qty so existing markup keeps working.
      this.options = Array.from(
        this.querySelectorAll('[data-set-pricing-qty], [data-qty]')
      );

      // Optional in-section add-to-cart form (progressive-enhancement target).
      this.form = this.querySelector('[data-set-pricing-form]');
      this.addBtn = this.querySelector('[data-set-pricing-add]');
      this.formQtyInput = this.querySelector('[data-set-pricing-form-qty]');
      this.variantInput = this.querySelector('[data-set-pricing-variant]');

      this.options.forEach((opt) => opt.addEventListener('change', this.onChange));
      if (this.form) this.form.addEventListener('submit', this.onSubmit);

      // Reverse sync: when the quantity changes anywhere else (buybox quick-qty,
      // sticky bar, the +/- stepper), reflect it back into our radios + total so
      // all three controls always agree. We never write back to the main input
      // from this handler, so there is no feedback loop.
      this.mainQtyInput = this.findMainQtyInput();
      if (this.mainQtyInput) {
        this.mainQtyInput.addEventListener('change', this.onMainQtyChange);
      }

      // Initialise from the pre-checked option, defaulting to qty 1. When a set
      // is the pre-selected default (qty > 1), mirror it into the product form on
      // load so "Add to cart" submits the whole set without an extra click. A
      // single-tyre default (qty 1) does NOT write, preserving Dawn's
      // server-rendered quantity.
      const checked = this.options.find((o) => o.checked) || this.options[0];
      const initialQty = checked ? this.readQty(checked) : 1;
      this.applyQty(initialQty, initialQty > 1);
    }

    disconnectedCallback() {
      this.options.forEach((opt) => opt.removeEventListener('change', this.onChange));
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.mainQtyInput) {
        this.mainQtyInput.removeEventListener('change', this.onMainQtyChange);
      }
    }

    readQty(el) {
      const raw = (el.dataset.setPricingQty || el.dataset.qty || '1');
      return parseInt(raw, 10) || 1;
    }

    onChange(event) {
      this.applyQty(this.readQty(event.target), true);
    }

    applyQty(qty, writeToForm) {
      this.dataset.selectedQty = String(qty);

      if (this.liveTotalEl && this.unitPrice > 0) {
        const total = this.unitPrice * qty;
        this.liveTotalEl.textContent = formatMoney(total, this.currency);
      }

      // MwSt.-Angabe zur Gesamtzeile mitziehen. Die fertigen Saetze je Stufe
      // stehen als data-mwst-1 / -2 / -4 am Host; fuer Mengen ohne eigene Stufe
      // (der Ruecklauf aus dem Hauptmengenfeld kann 3, 5, 6 melden) greift
      // data-mwst-kurz ohne Klammerwert — den Betrag darf das JS nicht selbst
      // hochrechnen, Stuecksteuer mal Menge weicht einen Cent von der Rechnung ab.
      if (this.mwstEl) {
        const mwstText = this.getAttribute('data-mwst-' + qty) || this.getAttribute('data-mwst-kurz');
        if (mwstText) this.mwstEl.textContent = mwstText;
      }

      // Keep our own hidden ATC quantity current (matters for the no-JS submit).
      if (this.formQtyInput) this.formQtyInput.value = String(qty);

      if (writeToForm) {
        this.writeQuantity(qty);
      }
    }

    // Reflect an EXTERNAL quantity change into our radios + total. Never calls
    // writeQuantity (which would re-dispatch change → infinite loop).
    onMainQtyChange() {
      if (!this.mainQtyInput) return;
      const qty = parseInt(this.mainQtyInput.value, 10) || 1;
      if (String(qty) === this.dataset.selectedQty) return;
      const match = this.options.find((o) => this.readQty(o) === qty);
      if (match && !match.checked) match.checked = true;
      this.applyQty(qty, false);
    }

    // The page's main product quantity input — shared source of truth across the
    // set-pricing radios, the buybox quick-qty and the sticky bar.
    findMainQtyInput() {
      // Eingegrenzt wie hauptinhalt() erklaert: dokumentweit gewann das
      // Partnerformular des Montagechecks im Warenkorb-Schubkasten, und die
      // Satzmenge landete in dessen verstecktem Mengenfeld statt in der Buybox.
      const wurzel = hauptinhalt();
      const form =
        wurzel.querySelector('product-form form[action*="/cart/add"]') ||
        wurzel.querySelector('form[action*="/cart/add"]') ||
        wurzel.querySelector('.product-form');
      const inForm = form && form.querySelector('input[name="quantity"], .quantity__input');
      return (
        inForm ||
        wurzel.querySelector('.product__info-container .quantity__input') ||
        wurzel.querySelector('.quantity__input')
      );
    }

    // Mirror the chosen set size into the main product form's quantity input.
    writeQuantity(qty) {
      const input = this.mainQtyInput || this.findMainQtyInput();
      if (!input) return;
      input.value = String(qty);
      // Dispatch change so Dawn's quantity-input logic + any subscribers react.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // The variant to add: prefer the CURRENT id from the main product form (right
    // after a variant switch), fall back to the rendered hidden id.
    currentVariantId() {
      // ⚠ DIESER WERT WIRD GEKAUFT (onSubmit -> /cart/add.js). Dokumentweit
      // gewann das Partnerformular im Schubkasten, und "4er-Satz kaufen" legte
      // vier Schlaeuche statt vier Reifen ab — der servergerenderte richtige
      // Wert in this.variantInput ist nur der Rueckfall und wurde verdraengt.
      const wurzel = hauptinhalt();
      const form =
        wurzel.querySelector('product-form form[action*="/cart/add"]') ||
        wurzel.querySelector('form[action*="/cart/add"]') ||
        wurzel.querySelector('.product-form');
      const mainId = form && form.querySelector('[name="id"]');
      if (mainId && mainId.value) return mainId.value;
      return this.variantInput && this.variantInput.value ? this.variantInput.value : '';
    }

    onSubmit(event) {
      // Enhance only if we can resolve a variant and fetch exists; otherwise let
      // the native form POST to /cart/add run (progressive-enhancement fallback).
      if (typeof fetch !== 'function') return;
      const variantId = this.currentVariantId();
      if (!variantId) return;
      event.preventDefault();
      const qty =
        parseInt(
          this.dataset.selectedQty || (this.formQtyInput && this.formQtyInput.value) || '1',
          10
        ) || 1;
      this.addToCart(variantId, qty);
    }

    // Klick bis Zustand ueber den gemeinsamen Pfad (window.ApCartAdd.kauf):
    // Ladezustand, Doppelklick-Sperre — vorher gab es hier keine, ein
    // Doppelklick legte 3 statt 2 Felgen ab und zeigte Schublade UND
    // "Nicht alles …" zugleich (live 26.09.2026) —, Schublade bei Erfolg,
    // Teilmenge und vollem Korb, Meldung unter dem Knopf nur bei echtem Fehler.
    addToCart(variantId, quantity) {
      return ApCartAdd.kauf({
        knopf: this.addBtn,
        host: this,
        anfrage: [{ id: Number(variantId) || variantId, quantity: quantity }],
        quelle: 'tyre-set-pricing',
        variantId: variantId,
        aktiv: this.addBtn,
        texte: this.texte(),
        meldung: (text) => this.zeigeMeldung(text),
      });
    }

    // Meldetexte kommen aus dem gemeinsamen Pfad (window.apKaufStrings); die
    // Attribute an der umgebenden Section (sections/tyre-set-pricing.liquid,
    // data-msg-partial / data-msg-error) sind der Rueckfall.
    texte() {
      if (window.apKaufStrings) return null;
      const quelle = this.closest('[data-msg-partial]') || this;
      return {
        teilmenge: quelle.getAttribute('data-msg-partial') || '',
        fehler: quelle.getAttribute('data-msg-error') || '',
      };
    }

    // Meldezeile unter dem Knopf, erst beim ersten Text angelegt: ein
    // eingefuegtes role=alert-Element wird samt Inhalt angesagt. Geleert
    // blendet Dawns base.css den leeren <p> per :empty aus; der naechste Text
    // erscheint wieder zusammen mit der Zeile — derselbe Fall wie beim Anlegen.
    zeigeMeldung(text) {
      if (!this.msgEl) {
        if (!text) return;
        this.msgEl = document.createElement('p');
        this.msgEl.className = 'ap-set-pricing__msg';
        this.msgEl.setAttribute('role', 'alert');
        if (this.form && this.form.parentNode === this) {
          this.form.insertAdjacentElement('afterend', this.msgEl);
        } else {
          this.appendChild(this.msgEl);
        }
      }
      this.msgEl.textContent = text || '';
    }
  }

  /* ----------------------------------------------------------------------
   * <free-shipping-bar>
   * Shows progress toward a free-shipping threshold. Reads data-threshold and
   * data-total (both in minor units / cents), renders a fill bar + message,
   * and recomputes on every cartUpdate by fetching /cart.js.
   * Markup contract:
   *   <free-shipping-bar class="ap-fsb" data-threshold="{cents}" data-total="{cents}"
   *     data-currency-symbol="€"
   *     data-remaining-text="Noch {amount} bis Gratisversand"
   *     data-reached-text="Gratisversand erreicht!">
   *     ... [data-fsb-fill] ... [data-fsb-message] ...
   * -------------------------------------------------------------------- */
  class FreeShippingBar extends HTMLElement {
    constructor() {
      super();
      this.unsubscribe = null;
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold || '0', 10) || 0;
      this.symbol = this.dataset.currencySymbol || '';
      this.fillEl = this.querySelector('[data-fsb-fill]');
      this.messageEl = this.querySelector('[data-fsb-message]');
      this.remainingTpl = this.dataset.remainingText || 'Noch [amount] bis zum Gratisversand';
      this.reachedTpl = this.dataset.reachedText || 'Gratisversand erreicht!';

      // Threshold off (0) — nothing to show.
      if (this.threshold <= 0) {
        this.setAttribute('hidden', '');
        return;
      }

      // Initial render from server-provided total, then subscribe to updates.
      const initialTotal = parseInt(this.dataset.total || '0', 10) || 0;
      this.render(initialTotal);

      const sub = getSubscribe();
      if (sub) {
        this.unsubscribe = sub(cartUpdateEventName(), this.onCartUpdate);
      }
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }

    onCartUpdate(data) {
      // Prefer the total carried in the event payload, else fetch /cart.js.
      const total = data && data.cartData && typeof data.cartData.total_price === 'number'
        ? data.cartData.total_price
        : null;
      if (total !== null) {
        this.render(total);
        return;
      }
      fetch((window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js'), {
        headers: { Accept: 'application/json' },
      })
        .then((r) => r.json())
        .then((cart) => this.render(cart && typeof cart.total_price === 'number' ? cart.total_price : 0))
        .catch(() => {});
    }

    render(totalCents) {
      const total = Number(totalCents) || 0;
      const reached = total >= this.threshold;
      const pct = this.threshold > 0 ? Math.min(100, Math.round((total / this.threshold) * 100)) : 0;

      if (this.fillEl) {
        this.fillEl.style.width = pct + '%';
      }

      // Mark the reached state both ways so the snippet's inline .ap-fsb--reached
      // styles and the global [data-reached] rules in automotive.css both apply.
      this.toggleAttribute('data-reached', reached);
      this.classList.toggle('ap-fsb--reached', reached);

      // Keep the progressbar's aria-valuenow in sync for assistive tech.
      const track = this.querySelector('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', String(pct));

      if (this.messageEl) {
        if (reached) {
          this.messageEl.textContent = this.reachedTpl.replace('[amount]', formatMoney(0, this.symbol));
        } else {
          const remaining = this.threshold - total;
          this.messageEl.textContent = this.remainingTpl.replace(
            '[amount]',
            formatMoney(remaining, this.symbol)
          );
        }
      }

      // Expose progress for assistive tech / styling.
      this.setAttribute('aria-label', this.messageEl ? this.messageEl.textContent : '');
    }
  }

  /* ----------------------------------------------------------------------
   * <shipping-cutoff>
   * Real daily shipping-cutoff countdown using the local browser clock.
   * Markup contract:
   *   <shipping-cutoff class="ap-cutoff" data-cutoff-hour="15"
   *     data-cutoff-minute="0" data-skip-weekend data-before-text="..."
   *     data-after-text="..." data-tz-note="">
   *     ... [data-cutoff-countdown] ... [data-cutoff-state] ...
   * -------------------------------------------------------------------- */
  class ShippingCutoff extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      this.hour = parseInt(this.dataset.cutoffHour || '15', 10);
      this.minute = parseInt(this.dataset.cutoffMinute || '0', 10);
      if (isNaN(this.hour)) this.hour = 15;
      if (isNaN(this.minute)) this.minute = 0;
      this.skipWeekend = this.hasAttribute('data-skip-weekend');
      this.beforeText = this.dataset.beforeText || 'Bestellung in {time} -> Versand noch heute';
      this.afterText = this.dataset.afterText || '';

      this.countdownEl = this.querySelector('[data-cutoff-countdown]');
      this.stateEl = this.querySelector('[data-cutoff-state]');

      // Weekend handling: if enabled and it's Sat/Sun, hide the whole bar.
      const day = new Date().getDay(); // 0=Sun ... 6=Sat
      if (this.skipWeekend && (day === 0 || day === 6)) {
        this.setAttribute('hidden', '');
        return;
      }

      this.tick();
      // Per-second update. prefers-reduced-motion: still update the text (no
      // animation/flash is introduced by a textContent change), but the CSS
      // owns any visual transition so this is safe.
      this.timer = window.setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
    }

    // ms remaining until today's cutoff; <= 0 means we are past it.
    msUntilCutoff() {
      const now = new Date();
      const cutoff = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        this.hour,
        this.minute,
        0,
        0
      );
      return cutoff.getTime() - now.getTime();
    }

    tick() {
      const ms = this.msUntilCutoff();

      if (ms <= 0) {
        // Past cutoff for today.
        this.setAttribute('data-cutoff-state-value', 'after');
        if (this.stateEl) {
          this.stateEl.setAttribute('data-cutoff-state', 'after');
          // Preserve any timezone note child if present, else just set text.
          this.setStateText(this.afterText);
        }
        if (this.countdownEl) {
          this.countdownEl.textContent = '';
          this.countdownEl.classList.add('visually-hidden');
        }
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
        return;
      }

      const totalSeconds = Math.floor(ms / 1000);
      const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
      const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
      const ss = String(totalSeconds % 60).padStart(2, '0');
      const formatted = `${hh}:${mm}:${ss}`;

      this.setAttribute('data-cutoff-state-value', 'before');
      if (this.countdownEl) {
        this.countdownEl.textContent = formatted;
        this.countdownEl.classList.remove('visually-hidden');
      }
      if (this.stateEl) {
        this.stateEl.setAttribute('data-cutoff-state', 'before');
        this.setStateText(this.beforeText.replace('{time}', formatted));
      }
    }

    // Replace the state element's text while keeping a trailing tz-note span.
    setStateText(text) {
      if (!this.stateEl) return;
      const note = this.stateEl.querySelector('.ap-cutoff__note');
      this.stateEl.textContent = text;
      if (note) {
        this.stateEl.appendChild(document.createTextNode(' '));
        this.stateEl.appendChild(note);
      }
    }
  }

  /* ----------------------------------------------------------------------
   * <recently-viewed>
   * Stores the current product (from a [data-recently-viewed-current] JSON
   * blob on the page) in localStorage and renders compact cards (excluding the
   * current product) by cloning the section's <template data-recent-card>.
   * Markup contract:
   *   <recently-viewed class="ap-recent" data-limit="8"
   *     data-root="/" data-money-prefix="€" data-money-suffix=""
   *     data-from-template="Von [price]"></recently-viewed>
   *   <template data-recent-card> ... .ap-recent__link/.ap-recent__image/
   *     .ap-recent__title/[data-recent-price]/[data-recent-vat]/
   *     [data-recent-soldout] ... </template>
   *
   * PREISE KOMMEN LIVE, NIE AUS DEM BROWSER-SPEICHER. Frueher stand der Preis
   * als fertiger Text („€111,29“) im Speicher — geschrieben am Tag des Besuchs,
   * danach unveraendert angezeigt, samt „inkl. MwSt., versandkostenfrei“, und
   * ohne jede Verfuegbarkeitspruefung. Die Preise hier schwanken aber laufend
   * (Preisverlauf „Preis schwankt“): ein Wiederkehrer sah einen alten Betrag,
   * der auf der Produktseite dann anders war — irrefuehrende Preisangabe
   * (UWG § 5) und ein Vertrauensschaden. Jetzt:
   *   1. Karten erscheinen OHNE Preis (Titel, Bild, Link aus dem Speicher).
   *   2. Erst wenn der Abschnitt sich dem Bildschirm naehert
   *      (IntersectionObserver), holt je Karte /products/<handle>.js den
   *      aktuellen Stand — hoechstens data-limit Karten, RV_PARALLEL zugleich.
   *   3. Preis + MwSt.-Satz erscheinen erst mit dieser Antwort. Ausverkauftes
   *      bekommt ein neutrales Etikett; eine 404 (Produkt geloescht/entlistet)
   *      loescht den Eintrag aus dem Speicher und die Karte aus der Liste.
   *   4. Scheitert der Abruf, bleibt die Karte OHNE Preis — lieber keiner als
   *      ein alter.
   * Der Speicher fuehrt deshalb gar keinen Preis mehr; Altdatensaetze verlieren
   * ihn beim naechsten Schreiben. Vertrag: tests/zuletzt-angesehen.test.js.
   * -------------------------------------------------------------------- */
  const RV_KEY = 'ap_recently_viewed';
  const RV_CAP = 20;
  const RV_PARALLEL = 4;
  const RV_TIMEOUT_MS = 8000;

  // Betrag in Cent -> Schreibweise DIESES Shops. Praefix/Suffix leitet die
  // Section aus dem echten Geldformat ab (money bzw. money_with_currency), so
  // steht der nachgeladene Preis in derselben Notation wie jeder serverseitig
  // gerenderte ("€111,29" bzw. "€111,29 EUR").
  function rvMoney(cents, prefix, suffix) {
    const amount = (Number(cents) || 0) / 100;
    /* LED-FORK FP-052 Anfang */
    // K-7: Sprache aus <html lang>, Waehrung aus cart.currency — keine feste Sprache, kein festes Symbol.
    const locale = document.documentElement.lang || undefined;
    const formatted = amount.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (prefix || suffix) return `${prefix || ''}${formatted}${suffix || ''}`;
    if (typeof window.apWaehrung === 'string' && window.apWaehrung) {
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: window.apWaehrung }).format(amount);
      } catch (e) {
        /* unbekannte Waehrung: Zahl mit Symbol */
      }
    }
    const sym = window.apWaehrungSymbol || '';
    return sym ? `${formatted} ${sym}` : formatted;
    /* LED-FORK FP-052 Ende */
  }

  // Der Handle aus dem Datensatz — Altdatensaetze tragen ihn womoeglich nicht,
  // dann aus der Produkt-URL (/products/<handle> bzw. /en/products/<handle>).
  function rvHandle(p) {
    if (p && p.handle) return String(p.handle);
    const m = p && p.url ? String(p.url).match(/\/products\/([^/?#]+)/) : null;
    if (!m) return '';
    try {
      return decodeURIComponent(m[1]);
    } catch (e) {
      return m[1];
    }
  }

  /* LED-FORK FP-051 Anfang */
  /* K-20 (TDDDG § 25): mit data-einwilligung="praeferenz" (sections/recently-viewed.liquid,
   * Einstellung ap_led_zuletzt_einwilligung) liest und schreibt der Verlauf den Browser-Speicher
   * erst, wenn die Praeferenz-Einwilligung vorliegt (Shopify Customer Privacy API). Ohne API oder
   * ohne Einwilligung bleibt der Abschnitt leer; die Einwilligung spaeter holt ihn nach. */
  function rvEinwilligung() {
    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && typeof cp.preferencesProcessingAllowed === 'function' && cp.preferencesProcessingAllowed());
    } catch (e) {
      return false;
    }
  }

  function rvMitEinwilligung(fn) {
    if (rvEinwilligung()) {
      fn();
      return;
    }
    let erledigt = false;
    const einmal = (zugestimmt) => {
      if (erledigt || !(zugestimmt || rvEinwilligung())) return;
      erledigt = true;
      fn();
    };
    document.addEventListener('visitorConsentCollected', (e) => {
      if (e && e.detail && e.detail.preferencesAllowed === true) einmal(true);
    });
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
          if (!err) einmal(false);
        });
      }
    } catch (e) {
      /* ohne API bleibt der Verlauf aus */
    }
  }
  /* LED-FORK FP-051 Ende */

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      /* LED-FORK FP-051 Anfang */
      if (this.dataset.einwilligung === 'praeferenz') {
        rvMitEinwilligung(() => this.starten());
        return;
      }
      this.starten();
    }

    starten() {
      /* LED-FORK FP-051 Ende */
      this.limit = parseInt(this.dataset.limit || '8', 10) || 8;
      // The clone source lives as a sibling/descendant of the section root.
      this.template =
        document.querySelector('template[data-recent-card]') ||
        (this.closest('[data-recently-viewed-section]') &&
          this.closest('[data-recently-viewed-section]').querySelector('template[data-recent-card]'));

      const current = this.readCurrentProduct();
      let items = this.loadStore();

      if (current && current.id != null) {
        // Dedupe by id, newest first, cap the history.
        items = items.filter((p) => String(p.id) !== String(current.id));
        items.unshift(current);
        if (items.length > RV_CAP) items = items.slice(0, RV_CAP);
        this.saveStore(items);
      }

      // Render, excluding the current product (it's the page you're on).
      const currentId = current && current.id != null ? String(current.id) : null;
      const toRender = items
        .filter((p) => (currentId ? String(p.id) !== currentId : true))
        .slice(0, this.limit);

      this.render(toRender);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
    }

    readCurrentProduct() {
      const blob = document.querySelector('[data-recently-viewed-current]');
      if (!blob) return null;
      try {
        const data = JSON.parse(blob.textContent);
        if (!data || data.id == null) return null;
        return {
          id: data.id,
          handle: data.handle || '',
          title: data.title || '',
          url: data.url || '',
          image: data.image || '',
        };
      } catch (e) {
        return null;
      }
    }

    loadStore() {
      try {
        const raw = window.localStorage.getItem(RV_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((p) => p && typeof p === 'object') : [];
      } catch (e) {
        return [];
      }
    }

    saveStore(items) {
      try {
        // Nie einen Preis ablegen — auch nicht den eines Altdatensatzes.
        const clean = items.map((p) => ({
          id: p.id,
          handle: rvHandle(p),
          title: p.title || '',
          url: p.url || '',
          image: p.image || '',
        }));
        window.localStorage.setItem(RV_KEY, JSON.stringify(clean));
      } catch (e) {
        /* storage full / disabled — non-fatal. */
      }
    }

    // Einen Eintrag (per Handle) aus dem Speicher nehmen: das Produkt gibt es
    // nicht mehr (404). Ohne das stuende es bei jedem Besuch wieder da.
    forget(handle) {
      this.saveStore(this.loadStore().filter((p) => rvHandle(p) !== handle));
    }

    render(products) {
      // Nothing to show: keep the section hidden (CSS hides empty container).
      if (!products.length || !this.template) {
        this.removeAttribute('data-state');
        return;
      }

      // The <recently-viewed> element IS the list per the section CSS
      // (.ap-recent { display:grid }). We append the template's <li> cards
      // directly into it. To keep semantics valid (li needs a list parent),
      // expose it as a list via role.
      this.setAttribute('role', 'list');
      this.textContent = '';

      products.forEach((p) => {
        const frag = this.template.content.cloneNode(true);

        const card = frag.querySelector('.ap-recent__card');
        const handle = rvHandle(p);
        if (card && handle) card.setAttribute('data-recent-handle', handle);

        const link = frag.querySelector('.ap-recent__link');
        if (link) link.setAttribute('href', p.url || '#');

        const img = frag.querySelector('.ap-recent__image');
        if (img) {
          if (p.image) {
            img.setAttribute('src', p.image);
            img.setAttribute('alt', p.title || '');
          } else {
            // No image: drop the node so we don't show a broken icon.
            const media = frag.querySelector('.ap-recent__media');
            if (media) media.remove();
          }
        }

        const title = frag.querySelector('.ap-recent__title');
        if (title) title.textContent = p.title || '';

        // Preis und MwSt.-Satz bleiben leer bzw. verborgen, bis refreshCard()
        // den aktuellen Stand geholt hat (siehe Kopfkommentar).
        this.appendChild(frag);
      });

      this.setAttribute('data-state', 'filled');
      this.watch();
    }

    // Erst abrufen, wenn der Abschnitt in die Naehe des Bildschirms kommt: wer
    // nie bis dorthin scrollt, kostet keine Anfrage. Ohne IntersectionObserver
    // (sehr alte Browser) sofort.
    watch() {
      if (this.observer) return;
      if (!('IntersectionObserver' in window)) {
        this.refresh();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          if (this.observer) this.observer.disconnect();
          this.observer = null;
          this.refresh();
        },
        { rootMargin: '300px 0px' }
      );
      this.observer.observe(this);
    }

    async refresh() {
      const cards = Array.from(this.querySelectorAll('.ap-recent__card[data-recent-handle]')).slice(0, this.limit);
      let next = 0;
      const worker = async () => {
        while (next < cards.length) {
          const card = cards[next];
          next += 1;
          await this.refreshCard(card);
        }
      };
      const workers = [];
      for (let i = 0; i < Math.min(RV_PARALLEL, cards.length); i += 1) workers.push(worker());
      await Promise.all(workers);

      // Alle Eintraege waren verschwunden (404): Abschnitt wieder ausblenden.
      if (!this.querySelector('.ap-recent__card')) {
        this.removeAttribute('data-state');
        this.removeAttribute('role');
      }
    }

    async refreshCard(card) {
      const handle = card.getAttribute('data-recent-handle');
      if (!handle) return;
      const root = (this.dataset.root || '/').replace(/\/$/, '');
      const url = `${root}/products/${encodeURIComponent(handle)}.js`;

      let res;
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), RV_TIMEOUT_MS) : null;
      try {
        res = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: ctrl ? ctrl.signal : undefined,
        });
      } catch (e) {
        return; // Netzfehler/Zeitueberschreitung: Karte bleibt ohne Preis.
      } finally {
        if (timer) clearTimeout(timer);
      }

      if (res.status === 404) {
        this.forget(handle);
        card.remove();
        return;
      }
      if (!res.ok) return;

      let product;
      try {
        product = await res.json();
      } catch (e) {
        return;
      }
      if (!product || typeof product.price !== 'number') return;

      const priceEl = card.querySelector('[data-recent-price]');
      if (!priceEl) return;
      const amount = rvMoney(product.price, this.dataset.moneyPrefix, this.dataset.moneySuffix);
      const from = this.dataset.fromTemplate || '';
      priceEl.textContent =
        product.price_varies && from.indexOf('[price]') !== -1 ? from.replace('[price]', amount) : amount;
      // Jetzt steht ein aktueller Preis da — erst jetzt gehoert der
      // MwSt.-/Versandsatz dazu (PAngV § 3/§ 6). Das CSS zeigt ihn ueber
      // [data-priced]; ohne Preis bliebe er eine Angabe ohne Bezug.
      card.setAttribute('data-priced', '');

      const soldOut = card.querySelector('[data-recent-soldout]');
      if (soldOut) soldOut.hidden = product.available !== false;
      card.classList.toggle('ap-recent__card--soldout', product.available === false);
    }
  }

  /* ----------------------------------------------------------------------
   * <before-after>
   * Image comparison slider. The section markup ships two stacked layers
   * (.ap-ba__layer--before / --after, each with an <img data-ba-before|after>)
   * plus a native range input (.ap-ba__range, 0..100). Without JS the CSS shows
   * both images side by side (fallback). On connect we add the `ap-ba--ready`
   * class so the CSS engages overlay mode, then on every range `input` we write
   * the value as a UNITLESS custom property (--ap-ba-pos) on the host; the CSS
   * clips the after-layer and positions the divider via calc(--ap-ba-pos * 1%).
   * Keyboard-accessible (native range). We never read user-supplied HTML.
   * -------------------------------------------------------------------- */
  class BeforeAfter extends HTMLElement {
    constructor() {
      super();
      this.range = null;
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      this.range = this.querySelector('.ap-ba__range') || this.querySelector('input[type="range"]');
      if (!this.range) return; // No control: leave the static side-by-side images.

      // Upgrade from the no-JS side-by-side fallback to the clipped overlay.
      this.classList.add('ap-ba--ready');

      // Reflect the initial position so the first paint matches the thumb.
      this.setPosition(this.range.value);

      // 'input' fires continuously while dragging or using arrow keys; this is
      // a pure CSS-variable write (no layout-thrashing animation) and is safe
      // under prefers-reduced-motion, so we don't gate it.
      this.range.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      if (this.range) this.range.removeEventListener('input', this.onInput);
    }

    onInput(event) {
      this.setPosition(event.target.value);
    }

    setPosition(value) {
      // Clamp to 0..100 defensively; the value originates from a native range.
      let pos = parseFloat(value);
      if (isNaN(pos)) pos = 50;
      pos = Math.max(0, Math.min(100, pos));
      // Unitless number: the CSS multiplies it by 1% (matches section-before-after.css).
      this.style.setProperty('--ap-ba-pos', String(pos));
      // Keep the range value reflected for assistive tech.
      this.range.setAttribute('aria-valuenow', String(Math.round(pos)));
    }
  }

  /* ----------------------------------------------------------------------
   * <countdown-timer>
   * Honest countdown to a real, merchant-set deadline (data-deadline, ISO8601).
   * Every second it writes zero-padded remaining days/hours/minutes/seconds
   * into the [data-cd-*] targets. On expiry it hides the timer face and reveals
   * [data-cd-expired]. No auto-resetting / fake timers. A blank or invalid
   * deadline is a no-op: the static markup is left untouched.
   * Markup contract (matches sections/countdown-offer.liquid):
   *   <countdown-timer class="ap-countdown" data-deadline="2026-07-01T10:00:00">
   *     <div class="ap-countdown__clock">
   *       <span data-cd-days>00</span> <span data-cd-hours>00</span>
   *       <span data-cd-min>00</span>  <span data-cd-sec>00</span>
   *     </div>
   *     <p data-cd-expired hidden>...</p>
   *   </countdown-timer>
   * On expiry we add `ap-countdown--expired` (CSS hides .ap-countdown__clock)
   * and unhide [data-cd-expired]. data-deadline is parsed as a local-time ISO
   * string (no trailing Z) so it matches the shop-timezone server render.
   * -------------------------------------------------------------------- */
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      const raw = (this.dataset.deadline || '').trim();
      if (!raw) return; // No deadline set: leave static markup as-is.

      const parsed = Date.parse(raw);
      if (isNaN(parsed)) return; // Invalid date: do nothing (honest fallback).
      this.deadline = parsed;

      this.daysEl = this.querySelector('[data-cd-days]');
      this.hoursEl = this.querySelector('[data-cd-hours]');
      this.minEl = this.querySelector('[data-cd-min]');
      this.secEl = this.querySelector('[data-cd-sec]');
      // The clock row (.ap-countdown__clock) is hidden on expiry via the host
      // class `ap-countdown--expired` (CSS owns the actual display:none).
      this.expiredEl = this.querySelector('[data-cd-expired]');

      this.tick();
      // Only start the per-second interval if the deadline is still ahead.
      if (this.deadline - Date.now() > 0) {
        this.timer = window.setInterval(this.tick, 1000);
      }
    }

    disconnectedCallback() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    tick() {
      const remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        this.showExpired();
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.write(this.daysEl, days);
      this.write(this.hoursEl, hours);
      this.write(this.minEl, minutes);
      this.write(this.secEl, seconds);
    }

    write(el, value) {
      if (el) el.textContent = String(value).padStart(2, '0');
    }

    showExpired() {
      // The CSS hides .ap-countdown__clock under this host class; this keeps
      // the static (no-JS) and dynamic expired states visually identical.
      this.classList.add('ap-countdown--expired');
      if (this.expiredEl) this.expiredEl.removeAttribute('hidden');
      this.setAttribute('data-cd-state', 'expired');
    }
  }

  /* ----------------------------------------------------------------------
   * Shared focus-trap helpers (BATCH 3 modals/overlays).
   * Dawn defines global `trapFocus` / `removeTrapFocus` / `getFocusableElements`
   * (see assets/global.js) as plain non-window globals. We resolve them lazily
   * and ship a self-contained fallback so these components never throw if the
   * globals are absent (defensive — order of script execution can vary).
   * -------------------------------------------------------------------- */
  function focusableWithin(container) {
    return Array.from(
      container.querySelectorAll(
        'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
      )
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  // Per-instance trap state so multiple overlays don't clobber one shared handler.
  function createFocusTrap(container) {
    let keydownHandler = null;

    function activate(elementToFocus) {
      keydownHandler = function (event) {
        if (event.key !== 'Tab' && event.keyCode !== 9) return;
        const focusable = focusableWithin(container);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!container.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', keydownHandler);
      const target = elementToFocus || focusableWithin(container)[0] || container;
      if (target && typeof target.focus === 'function') {
        // Defer so the element is visible (hidden attr removed) before focusing.
        window.requestAnimationFrame(() => target.focus());
      }
    }

    function release() {
      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler);
        keydownHandler = null;
      }
    }

    return { activate: activate, release: release };
  }

  // Scroll-lock counter so nested/overlapping overlays restore correctly.
  let scrollLockCount = 0;
  function lockScroll() {
    scrollLockCount += 1;
    document.documentElement.classList.add('ap-no-scroll');
    document.body.classList.add('ap-no-scroll');
  }
  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.documentElement.classList.remove('ap-no-scroll');
      document.body.classList.remove('ap-no-scroll');
    }
  }

  function safeLocalStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function safeLocalStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* storage disabled/full — non-fatal. */
    }
  }

  /** Steht gerade ein MODALER Dialog offen (Fahrzeugwahl Felgen, Widerruf — natives <dialog> mit
   *  showModal)? Dann oeffnet sich kein Popup darueber: zwei Fokusfallen gegeneinander, und der
   *  Hintergrund des Dialogs ist inert (THEME-SPEC 11, Phase B 29.09.2026). Ohne :modal-Unterstuetzung
   *  zaehlt jedes offene dialog-Element. */
  function offenesModal() {
    try {
      return !!document.querySelector(':modal');
    } catch (e) {
      return !!document.querySelector('dialog[open]');
    }
  }

  /* ----------------------------------------------------------------------
   * Gemerkte Reifengroesse.
   *
   * Vertrag mit den uebrigen Bausteinen des Shops (auch der Kaskade in
   * sections/collection-prefilter.liquid): Schluessel EXAKT "ap_last_size",
   * Wert JSON {"w":..,"h":..,"d":..,"label":"205/55 R16","url":"<facetten-url>"}.
   * Jede Groessensuche des Themes schreibt ihn, der Chip im Reifenfinder liest
   * ihn. Alles daran ist optional: fehlt oder bricht der Eintrag, passiert
   * nichts — es darf nie ein Platzhalter oder ein Layout-Sprung entstehen.
   * -------------------------------------------------------------------- */
  const LAST_SIZE_KEY = 'ap_last_size';

  function readLastSize() {
    const raw = safeLocalStorageGet(LAST_SIZE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null; // fremd beschriebener/abgeschnittener Eintrag — ignorieren.
    }
  }

  function writeLastSize(entry) {
    try {
      safeLocalStorageSet(LAST_SIZE_KEY, JSON.stringify(entry));
    } catch (e) {
      /* nicht serialisierbar — nicht schlimm, die Suche laeuft trotzdem. */
    }
  }

  function rememberSize(width, aspect, rim, url) {
    writeLastSize({
      w: sizeNumber(width),
      // Leeres h heisst "hat keinen Querschnitt" — wortgleich mit ap-prefilter.js und
      // dem App-Block extensions/tyre-search, die denselben Schluessel schreiben.
      // sizeNumber('-') gaebe sonst die Zeichenkette "-" in den Merker.
      h: aspect === NO_ASPECT ? '' : sizeNumber(aspect),
      d: sizeNumber(rim),
      label: tyreSizeLabel(width, aspect, rim),
      url: url,
    });
  }

  /* ----------------------------------------------------------------------
   * Chip "Zuletzt gesucht" (Markup: sections/reifenfinder.liquid).
   * Markup-Vertrag:
   *   <p class="ap-lastsize" data-ap-last-size hidden>
   *     ... <a data-ap-last-size-link><span data-ap-last-size-label>
   * Ohne gespeicherte Groesse bleibt der Chip [hidden] — deshalb steht er im
   * Liquid auch von Anfang an so da und wird hier nur freigegeben.
   * -------------------------------------------------------------------- */
  function initLastSizeChips() {
    const chips = Array.from(document.querySelectorAll('[data-ap-last-size]'));
    if (!chips.length) return;
    const last = readLastSize();

    chips.forEach((chip) => {
      if (chip.dataset.apLastSizeReady) return;
      chip.dataset.apLastSizeReady = '1';
      if (!last || !last.label || !last.url) return;
      // Nur shop-eigene, relative Ziele. Ein fremd beschriebener Eintrag koennte
      // sonst ein "javascript:"- oder ein externes Ziel in den Link tragen.
      if (String(last.url).charAt(0) !== '/') return;

      const link = chip.querySelector('[data-ap-last-size-link]');
      if (!link) return;
      const label = chip.querySelector('[data-ap-last-size-label]');
      link.setAttribute('href', last.url);
      if (label) label.textContent = last.label;
      else link.textContent = last.label;
      chip.removeAttribute('hidden');
    });
  }

  /* ----------------------------------------------------------------------
   * Back-to-top button.
   * Markup contract (markup agent):
   *   <button class="ap-to-top" data-to-top hidden aria-label="Nach oben">
   * Behaviour: reveal once window.scrollY is past TWO viewport heights
   * (Design-Spezifikation B5, Integration 26.09.2026 — vorher fest 600 px; auf
   * dem Handy lag der Knopf damit schon im ersten Bildschirm ueber dem echten
   * Kaufknopf); click -> smooth-scroll to top (instant under
   * prefers-reduced-motion). The button stays on phones too (Betreiber-Vorgabe:
   * nichts wegnehmen). There may be several pages without this button — guard.
   *
   * GEAENDERT Feinschliff 3 (03.10.2026, Betreiber: „die drei icons … mal weg
   * mal kommen"). Gemessen: „Nach oben" erschien im ersten Bild voll (display
   * none -> sichtbar, die Blende lief nie), die Schwelle an innerHeight hatte
   * keine Hysterese — mit der Adressleiste blinkte er an der Umkehr dreimal an
   * (21/165/335 ms), und jedes Kommen/Gehen vergroesserte die rechte Spalte
   * mitten im Wischen (15 Weichvorgaenge). Jetzt:
   *   - [hidden] faellt EINMAL beim Start; danach blendet .is-visible
   *     (visibility/opacity/scale, automotive.css) — ohne Skript bleibt er weg;
   *   - Hysterese: an ueber 2 x H, aus unter 1,5 x H, H = die GROESSTE
   *     gemessene Fensterhoehe (die Adressleiste verschiebt die Schwelle nicht);
   *   - geschaltet wird nur in RUHE: mit dem Schwebe-System
   *     (html[data-ap-standlinie="live"], assets/ap-schwebe.js) auf dessen
   *     Ereignis ap:schwebe-ruhe — die Spalte waechst und schrumpft nie im
   *     Wischen. Ohne Schwebe-System (Skript blockiert) wie bisher je
   *     Scroll-Bild.
   *   GEAENDERT Feinschliff 3 FIX (04.10.2026, gegnerische Pruefung Mobil,
   *   minor): in der Ruhe erst ENTSCHEIDEN, dann einschalten. Auf
   *   ap:schwebe-ruhe merkt sich der Knopf nur seinen Soll-Zustand
   *   (data-ap-soll="an|aus"); ap-schwebe.js rechnet die Spalte damit, setzt
   *   ggf. ap-schwebe-weicht und meldet ap:schwebe-entschieden — erst dann
   *   schaltet .is-visible. Vorher blendete „Nach oben" in 3 von 9 Faellen ein
   *   (Deckkraft 0,27) und glitt im selben Halt mit der Spalte hinaus.
   * -------------------------------------------------------------------- */
  let backToTopBound = false;
  function initBackToTop() {
    // defineComponents() may run more than once (section re-injection); only
    // bind the single global scroll listener the first time.
    if (backToTopBound) return;
    const buttons = Array.from(document.querySelectorAll('[data-to-top]'));
    if (!buttons.length) return;
    backToTopBound = true;
    const root = document.documentElement;

    // Zwei Bildschirmhoehen an, anderthalb aus; 600/450 px bleiben Untergrenze
    // fuer sehr flache Fenster. H = die groesste gemessene Fensterhoehe.
    let hoehe = window.innerHeight || 0;
    const fenster = () => Math.max(hoehe, window.innerHeight || 0);
    const AN = 2;
    const AUS = 1.5;
    let sichtbar = false;
    // SOLL UEBER SONDEN (P1 M2): zwei Sonden [0, T) am Seitenanfang (T = max(600, 2H) bzw. max(450, 1,5H)) melden per
    // IntersectionObserver, ob scrollY > T — dieselbe Regel ohne Lesung; bis zur ersten Meldung wie bisher.
    let anImBild = null;
    let ausImBild = null;

    function soll() {
      if (anImBild !== null && ausImBild !== null) return sichtbar ? !ausImBild : !anImBild;
      const y = window.scrollY || window.pageYOffset || 0;
      const h = fenster();
      return sichtbar ? y > Math.max(450, AUS * h) : y > Math.max(600, AN * h);
    }

    function setzen(an) {
      sichtbar = an;
      buttons.forEach((btn) => btn.classList.toggle('is-visible', an));
    }

    let ticking = false;
    function apply() {
      ticking = false;
      const an = soll();
      if (an !== sichtbar) setzen(an);
    }

    function onScroll() {
      // Mit dem Schwebe-System entscheidet nur die Ruhe (ap:schwebe-ruhe).
      if (root.getAttribute('data-ap-standlinie') === 'live') return;
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    buttons.forEach((btn) => {
      btn.removeAttribute('hidden');
      btn.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      });
    });

    // Nur die HOEHE allein (Adressleiste) haelt den groessten Wert; aendert sich
    // die Breite (Drehen, Fenster), gilt die neue Hoehe.
    let breite = window.innerWidth || 0;
    window.addEventListener(
      'resize',
      () => {
        const b = window.innerWidth || 0;
        hoehe = b !== breite ? window.innerHeight || 0 : Math.max(hoehe, window.innerHeight || 0);
        breite = b;
      },
      { passive: true }
    );
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('ap:schwebe-ruhe', () => {
      const an = soll();
      buttons.forEach((btn) => btn.setAttribute('data-ap-soll', an ? 'an' : 'aus'));
    });

    // OBEN SOFORT AUS (Zusammenfuehrung 05.10.2026, gegnerische Pruefung Mobil, minor): nach dem Hochwischen bis
    // scrollY 0–25 blieb „Nach oben" bis zur Ruhe-Entscheidung (1,75 s) stehen und ragte im Querformat 844 x 390
    // bis 34 px in den Kopf (Anrufzeile). Oben hat der Knopf kein Ziel mehr: steht der Seitenanfang im Bild
    // (scrollY < eine Bildhoehe), blendet er SOFORT aus — gemeldet von einer unsichtbaren Sonde am Seitenanfang
    // (IntersectionObserver), kein Lesen im Scroll-Pfad. Eingeschaltet wird weiter nur in der Ruhe.
    if ('IntersectionObserver' in window && document.body) {
      const sonde = document.createElement('div');
      sonde.setAttribute('aria-hidden', 'true');
      sonde.setAttribute('data-ap-oben-hoch', '');
      // Die Schwellen-Sonden (SOLL UEBER SONDEN, oben) stehen IN dieser Sonde: eine Einfuegung am <body> statt drei
      // (P1 Massnahme 9). display:block gegen Dawns div:empty; die Hoehen ohne Runden (Schwelle wie in soll()).
      const anSonde = document.createElement('div');
      const ausSonde = document.createElement('div');
      const schwelle = (el, px) => {
        el.style.cssText = 'display:block;position:absolute;top:0;left:0;width:1px;height:' + px + 'px;pointer-events:none;';
      };
      const hoeheSetzen = () => {
        sonde.style.cssText =
          'display:block;position:absolute;top:0;left:0;width:1px;height:' + Math.max(1, Math.round(fenster())) + 'px;visibility:hidden;pointer-events:none;';
        schwelle(anSonde, Math.max(600, AN * fenster()));
        schwelle(ausSonde, Math.max(450, AUS * fenster()));
      };
      hoeheSetzen();
      sonde.appendChild(anSonde);
      sonde.appendChild(ausSonde);
      document.body.appendChild(sonde);
      window.addEventListener('resize', hoeheSetzen, { passive: true });
      const schwellen = new IntersectionObserver((eintraege) => {
        eintraege.forEach((e) => {
          if (e.target === anSonde) anImBild = e.isIntersecting;
          else if (e.target === ausSonde) ausImBild = e.isIntersecting;
        });
        // Ohne Schwebe-System schaltet der Knopf wie bisher je Scroll-Bild — die Meldung kommt nach dem Bild, in dem
        // die Schwelle fiel, und zieht nach (sonst bliebe der letzte Wechsel bis zum naechsten Scroll liegen).
        if (root.getAttribute('data-ap-standlinie') !== 'live') apply();
      });
      schwellen.observe(anSonde);
      schwellen.observe(ausSonde);
      // Die Oben-Sonde bleibt ein eigener Beobachter (unten).
      new IntersectionObserver((eintraege) => {
        const oben = eintraege.length > 0 && eintraege[eintraege.length - 1].isIntersecting;
        if (!oben || !sichtbar) return;
        buttons.forEach((btn) => btn.setAttribute('data-ap-soll', 'aus'));
        // OHNE BLENDE (Phase D Stufe 0, 05.10.2026): die 200-ms-Blende liess den Knopf nach einem Wurf an den
        // Seitenanfang 183 ms bis 34 px im aufgeklappten Kopf stehen (844 x 390). Zwei Bilder ohne Transition
        // (automotive.css .ap-to-top--sofort) — er ist im selben Bild weg; das Einblenden bleibt weich.
        buttons.forEach((btn) => btn.classList.add('ap-to-top--sofort'));
        setzen(false);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => buttons.forEach((btn) => btn.classList.remove('ap-to-top--sofort')));
        });
      }).observe(sonde);
    }
    document.addEventListener('ap:schwebe-entschieden', () => {
      const wert = buttons[0].getAttribute('data-ap-soll');
      if (!wert) return;
      const an = wert === 'an';
      if (an !== sichtbar) setzen(an);
    });
    apply(); // Reflect initial scroll position (e.g. on reload mid-page).
  }

  /* ----------------------------------------------------------------------
   * Google-Haendlerabzeichen auf die Standlinie --ap-float-floor.
   *
   * WARUM NICHT IN CSS (Befund 08.09.2026): der Hub ist die Standlinie minus
   * dem Abstand, den die App selbst haelt — und dieser Abstand ist NICHT
   * konstant. Im Quelltext der App (merchantwidget.js, Funktion q):
   *
   *   const h = window.innerHeight < 480 ? "0"
   *           : window.innerWidth > 480 ? (bottomMargin || "20px")
   *                                     : (mobileBottomMargin || "30px");
   *
   * Er haengt also an BEIDEN Fenstermassen, bricht bei 480 px (nicht bei den
   * 750 px des Themes), wird EINMAL beim Laden berechnet und per !important
   * gesetzt — ohne resize-Handler. Feste Werte im Stylesheet gingen daher in
   * drei Faellen daneben, im schlimmsten schoben sie das Abzeichen unter den
   * Bildschirmrand (Handy quer / 200 % Zoom, wo die App bottom:0 setzt).
   *
   * Hier wird stattdessen der TATSAECHLICH gesetzte Abstand gemessen und der
   * Hub daraus gerechnet. Die Standlinie kommt aus einer 0x0-Sonde, damit die
   * calc()-Kette in CSS die einzige Wahrheit bleibt und hier nichts
   * nachgerechnet wird. Ergebnis ist immer eine Unterkante GENAU auf der
   * Standlinie — ein Wert unter dem Bildschirmrand ist damit ausgeschlossen.
   *
   * `transform` und nicht `bottom`: die App setzt `bottom` inline mit
   * !important, und inline-!important schlaegt jede Zuweisung von aussen.
   * Faellt das Abzeichen weg oder benennt die App ihre Wrapper-Id um, tut
   * diese Funktion nichts.
   * -------------------------------------------------------------------- */
  const ABZEICHEN = '#google-merchantwidget-iframe-wrapper';
  let abzeichenSonde = null;
  let abzeichenGebunden = false;
  // Die LIVE-Standlinie aus dem Ereignis ap:standlinie (assets/ap-schwebe.js rechnet sie ohne
  // Lesezugriff). Solange sie da ist, liest standlinie() die eigene Sonde NICHT: direkt nach dem
  // Schreiben von --ap-float-hindernis an <html> erzwang das eine Stil-Neuberechnung des ganzen
  // Dokuments (Phase D, DT-1, Nachmessung 29.09.2026).
  let abzeichenStandlinie = null;
  let abzeichenGeplant = false;

  /** Hoechstens EINMAL je Bild platzieren (Body-Beobachter, Standlinie). */
  function abzeichenPlanen() {
    if (abzeichenGeplant) return;
    abzeichenGeplant = true;
    window.requestAnimationFrame(() => {
      abzeichenGeplant = false;
      platziereAbzeichen();
    });
  }

  /** Grenze der Glaubwuerdigkeit: die Standlinie ist ein kleiner Abstand vom
   *  unteren Rand (24 px, mit Kaufleiste 156 px plus Safe-Area). Alles
   *  darueber ist eine Fehlmessung und darf das Abzeichen NICHT bewegen. */
  const STANDLINIE_MAX = 400;

  /**
   * Die Standlinie in px. Gelesen wird der GERECHNETE Wert der Sonde, nicht
   * ihr Kasten:
   *
   * ⚠ Dawns base.css:482-496 blendet mit `div:empty { display: none }` jeden
   * leeren Container aus. Eine leere Sonde ist damit display:none, und
   * `getBoundingClientRect()` liefert dann LAUTER NULLEN — die Standlinie
   * waere faelschlich innerHeight. Genau das ist am 08.09.2026 live passiert:
   * gemessen 844 statt 156, Hub 798, das Abzeichen stand 798 px zu hoch und
   * war aus dem Bild geschoben. `display:block` inline schlaegt die Regel
   * (0-1-1) zwar, aber der computed value braucht gar keinen Kasten: `bottom`
   * loest `var()` und `calc()` ohne Layout auf. Beides zusammen, damit ein
   * einzelner Fehlgriff nicht wieder alles verschiebt.
   */
  function standlinie() {
    if (abzeichenStandlinie !== null && document.documentElement.getAttribute('data-ap-standlinie') === 'live') {
      return abzeichenStandlinie;
    }
    if (!abzeichenSonde || !abzeichenSonde.isConnected) {
      abzeichenSonde = document.createElement('div');
      abzeichenSonde.setAttribute('aria-hidden', 'true');
      abzeichenSonde.style.cssText =
        'display:block;position:fixed;left:0;width:0;height:0;visibility:hidden;' +
        'pointer-events:none;bottom:var(--ap-float-floor, 24px)';
      document.body.appendChild(abzeichenSonde);
    }
    const px = parseFloat(window.getComputedStyle(abzeichenSonde).bottom);
    if (!Number.isFinite(px) || px < 0 || px > STANDLINIE_MAX) return null;
    return px;
  }

  /* ----------------------------------------------------------------------
   * WOHIN MIT DEM ABZEICHEN (Art-Director R2, 26.09.2026) — verlegen, nicht
   * entfernen. Seit dem 1360er-Raster beginnt der Inhalt am Schreibtisch bei
   * x 64 (LIVE bei 170): das 84-px-Abzeichen bei x 20 lag auf der linken
   * Karten-/Filterspalte („In den Korb", „DOT/GROESSE", Preisfeld „Von"). Auf
   * dem Handy lag es im ersten Bildschirm ueber der Kaufkarte (Hauptpreis,
   * TESTSIEGER, „Freigegeben fuer") und auf /cart ueber „Menge verringern".
   * Siegel-System 3a: auf dem Handy schwebt kein Siegel ueber dem Inhalt.
   *
   *   RAND     Passt es — um den Faktor ABZ_RAND_SKALA verkleinert — in den
   *            linken Seitenrand neben den Inhalt (1440/1280 px: Rand 64 px,
   *            Abzeichen dann x 4–59), schwebt es dort weiter.
   *   ANDOCKEN Sonst (Handy, schmale Fenster) steht es nur, solange sein
   *            Platz am Ende der Fusszeile (#ap-abzeichen-platz,
   *            sections/footer.liquid) GANZ im Bild ist — dort liegt es ueber
   *            einer freien Flaeche und verdeckt nichts. Dazwischen wartet es
   *            links ausserhalb des Bildes (visibility hidden: nicht tippbar).
   * Das Google-Widget selbst bleibt unangetastet und echt (kein nachgebautes
   * Siegel); ohne Platz im Dokument bleibt alles wie vorher.
   * -------------------------------------------------------------------- */
  // 0.66 (Schwebe-System 26.09.2026, vorher 0.68): 84 px -> 55 px, in der
  // Schwebe-Groesse des Schreibtischs (52–56 px, wie „Nach oben" und die Blase).
  const ABZ_RAND_SKALA = 0.66;
  const ABZ_RAND_LINKS = 4;
  let abzeichenPlatz = null;
  let abzeichenPlatzSichtbar = false;
  let abzeichenPlatzBeobachtet = false;

  /** Linke Kante des Seiteninhalts (erste .page-width in <main>, ohne Polster). */
  function inhaltLinks() {
    const pw = document.querySelector('#MainContent .page-width');
    if (!pw) return 0;
    const cs = window.getComputedStyle(pw);
    return pw.getBoundingClientRect().left + (parseFloat(cs.paddingLeft) || 0);
  }

  function beobachteAbzeichenPlatz() {
    if (abzeichenPlatzBeobachtet) return;
    abzeichenPlatz = document.getElementById('ap-abzeichen-platz');
    if (!abzeichenPlatz || !('IntersectionObserver' in window)) {
      abzeichenPlatz = null;
      return;
    }
    abzeichenPlatzBeobachtet = true;
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          abzeichenPlatzSichtbar = entry.isIntersecting && entry.intersectionRatio >= 0.95;
        });
        platziereAbzeichen();
      },
      { threshold: [0, 0.95, 1] }
    ).observe(abzeichenPlatz);
  }

  /** Klasse am <body> nur bei echter Aenderung setzen — der MutationObserver
   *  auf body[class] ruft platziereAbzeichen sonst endlos erneut auf. */
  function abzeichenKlasse(an) {
    const body = document.body;
    if (body.classList.contains('ap-abzeichen-andocken') !== an) body.classList.toggle('ap-abzeichen-andocken', an);
  }

  /** Zeigt die App das Abzeichen gerade gar nicht (display none oder 0 x 0)? */
  function abzeichenVersteckt(el) {
    return window.getComputedStyle(el).display === 'none' || (!el.offsetWidth && !el.offsetHeight);
  }

  /** Hat die App ihr PANEL aufgeklappt (Klick: 406 x 602 mit Versand, Retouren,
   *  Zahlung — Art-Director R3, gklick-v-1440.json)? Nur die HOEHE zaehlt: die Breite
   *  368 px setzt die App auch ohne Zutun (Pille „Händlerqualität von Google", z. B.
   *  geparkt im Andock-Modus, 368 x 84 gemessen in der Vorschau 768) — mit der Breite
   *  als Merkmal stand html.ap-google-offen dauerhaft und Chat, „Nach oben" und die
   *  Laschen waren am Tablet verschwunden. Gelesen wird die INLINE-Hoehe der App bzw.
   *  offsetHeight (die Klemme im RAND-Modus begrenzt nur die Breite). */
  function abzeichenOffen(el) {
    const h = parseFloat(el.style.height);
    return (Number.isFinite(h) && h > 120) || el.offsetHeight > 120;
  }

  /** Nur bei echter Aenderung (sonst Schleife ueber den MutationObserver auf <html>). */
  function abzeichenOffenKlasse(an) {
    const html = document.documentElement;
    if (html.classList.contains('ap-google-offen') !== an) html.classList.toggle('ap-google-offen', an);
  }

  /* Innenrand der App um die sichtbare Scheibe (Art-Director R3, 28.09.2026,
     Pixelmessung haupt-v-390-start-fuss.png / haupt-v-768-pdp-fuss.png): Wirt 68 px
     -> Scheibe 40 px, Wirt 84 px -> Scheibe 56 px, also je 14 px transparenter Rand.
     Die Scheibe endete dadurch 17 px ueber der Standlinie von Chat und „Nach oben". */
  const ABZ_INNENRAND = 14;

  function platziereAbzeichen() {
    const el = document.querySelector(ABZEICHEN);
    if (!el) return;
    // ⛔ OVERFLOW NIE LEEREN (Art-Director R3, 28.09.2026, Blocker): die App haengt ein
    // TRANSPARENTES iframe in Fenstergroesse (390x798 bzw. 748x988, z 2147483647) in den
    // Wirt und beschneidet es mit `overflow: hidden` am Wirt auf die Scheibe. Das Theme
    // setzte overflow im Andock-Zweig auf '' — danach fing das unsichtbare iframe 86–94 %
    // aller Tipps am Fussende ab (Impressum, Widerruf, AGB, Newsletter, Chat, „Nach oben";
    // Rastermessung gitter2-v-390/-768). Der Wert der App wird beim ersten Kontakt gemerkt
    // und nur wiederhergestellt; ohne eigenen Wert gilt 'hidden' (= Wert der App live).
    if (el.__apAbzOverflow === undefined) el.__apAbzOverflow = el.style.overflow || 'hidden';
    // Art-Director Feinschliff II R1 (wr7): der Andockplatz am Fusszeilenende
    // (108 px) wurde auch reserviert, wenn die App das Abzeichen gar nicht zeigt
    // — am Seitenende standen 246 statt 138 px leere Flaeche. Versteckt = kein
    // Platz. Den Wechsel der App (Stil am Wirt) beobachtet ein eigener Observer;
    // er ruft nur bei einem echten Wechsel sichtbar/versteckt bzw. zu/offen neu auf
    // (unsere eigenen transform-/maxWidth-Schreibungen aendern keinen der beiden
    // Zustaende: keine Schleife).
    const versteckt = abzeichenVersteckt(el);
    const offen = !versteckt && abzeichenOffen(el);
    if (!el.__apAbzBeobachtet && 'MutationObserver' in window) {
      el.__apAbzBeobachtet = true;
      // KOSTEN (Phase D, DT-1, 28.09.2026): CPU-Profil der Vorschau-PDP (4x gedrosselt, 20 s):
      // abzeichenVersteckt 5,2 s Selbstzeit — der Rueckruf lief bei JEDER Stil-Aenderung am
      // Wirt (die App setzt ihren Stil fortlaufend, dazu unsere eigenen transform-Schreibungen)
      // mit getComputedStyle + offsetWidth = erzwungenem Stil/Layout. Jetzt: hoechstens EINE
      // Pruefung je Bild, und unsere eigenen Schreibungen verwirft platziereAbzeichen selbst
      // (takeRecords im finally unten).
      el.__apAbzMo = new MutationObserver(() => {
        if (el.__apAbzGeplant) return;
        el.__apAbzGeplant = true;
        window.requestAnimationFrame(() => {
          el.__apAbzGeplant = false;
          const v = abzeichenVersteckt(el);
          if (v !== el.__apAbzVersteckt || (!v && abzeichenOffen(el)) !== el.__apAbzOffen) platziereAbzeichen();
        });
      });
      el.__apAbzMo.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
    }
    try {
      platziereAbzeichenAn(el, versteckt, offen);
    } finally {
      if (el.__apAbzMo) el.__apAbzMo.takeRecords();
    }
  }

  /** Entparken (Feinschliff 3 FIX, minor): die Endlage OHNE Gleiten setzen — vorher glitt die Huelle
   *  von −424 px herein (220 ms) — und nur einblenden. Die Transition gilt fuer die Blende nur auf opacity
   *  (Inline, schlaegt das Stylesheet), danach wieder die des Stylesheets (transform fuer den Hub). */
  function abzeichenEinblenden(el) {
    el.style.transition = prefersReducedMotion() ? 'none' : 'opacity var(--ap-motion, 200ms) var(--ap-ease, ease)';
    el.style.opacity = '';
    window.clearTimeout(el.__apAbzBlende);
    el.__apAbzBlende = window.setTimeout(() => {
      el.style.transition = '';
    }, 260);
  }

  function platziereAbzeichenAn(el, versteckt, offen) {
    el.__apAbzVersteckt = versteckt;
    el.__apAbzOffen = offen;
    el.style.overflow = el.__apAbzOverflow;
    abzeichenOffenKlasse(offen);
    if (versteckt) {
      abzeichenKlasse(false);
      return;
    }
    const linie = standlinie();
    if (linie === null) return; // Fehlmessung: lieber nichts tun als verschieben.
    // `bottom` bleibt der Wert der App: transform aendert ihn nicht, die
    // Rechnung ist also bei jedem Aufruf dieselbe (idempotent).
    const eigen = parseFloat(window.getComputedStyle(el).bottom);
    if (!Number.isFinite(eigen)) return;
    const hub = Math.round(linie - eigen);

    beobachteAbzeichenPlatz();
    // offsetWidth/-Height/left sind vom transform unberuehrt — die Rechnung
    // bleibt idempotent. Gerechnet wird mit der ZUGEKLAPPTEN Groesse: das
    // Abzeichen ist quadratisch (84 x 84 Desktop, 68 x 68 Handy). Die App
    // setzt die Huelle zeitweise auf ~368 px Breite (aufgeklappt) oder 0 x 0
    // (noch nicht/gerade nicht gezeigt) — dann gilt die bekannte Kante.
    const breite = el.offsetWidth;
    const hoch = el.offsetHeight;
    const kante = hoch > 0 && hoch <= 120 ? hoch : window.innerWidth > 480 ? 84 : 68;
    const links = parseFloat(window.getComputedStyle(el).left);
    const geparkt = el.hasAttribute('data-ap-geparkt');

    // RAND: verkleinert in den linken Seitenrand, wenn er dort Platz hat.
    if (Number.isFinite(links) && ABZ_RAND_LINKS + kante * ABZ_RAND_SKALA + 1 <= inhaltLinks()) {
      abzeichenKlasse(false);
      if (geparkt) {
        el.removeAttribute('data-ap-geparkt');
        el.style.visibility = '';
        el.style.opacity = '';
      }
      // Zugeklappt bleibt der Wirt im Rand auf seine Kante begrenzt und verkleinert
      // (R1 si25: im Ruhezustand lag nichts ueber dem Inhalt neben der Rinne).
      // ⛔ AUFGEKLAPPT (Klick: Panel 406 x 602 mit Versand, Retouren, Zahlung …; Hover:
      // Pille „Händlerqualität von Google") KEINE Klemme und KEINE Verkleinerung — sonst
      // blieb vom Panel ein 55 px schmaler Streifen mit abgeschnittenem Text (Art-Director
      // R3, Blocker „verworfen": LIVE zeigt das Panel voll). Solange es offen ist, gehen
      // Chat, „Nach oben" und die Laschen aus dem Weg (html.ap-google-offen, automotive.css).
      el.style.transformOrigin = 'left bottom';
      if (offen) {
        el.style.maxWidth = '';
        el.style.transform = 'translate(' + Math.round(ABZ_RAND_LINKS - links) + 'px, ' + -hub + 'px)';
      } else {
        el.style.maxWidth = Math.round(kante) + 'px';
        el.style.transform =
          'translate(' + Math.round(ABZ_RAND_LINKS - links) + 'px, ' + -hub + 'px) scale(' + ABZ_RAND_SKALA + ')';
      }
      return;
    }
    el.style.transformOrigin = '';
    // ZUGEKLAPPT AUCH BEIM ANDOCKEN AUF DIE KANTE GEKLEMMT (Feinschliff 3 FIX, 04.10.2026, gegnerische
    // Pruefung Mobil, minor): beim ersten Erreichen des Andockplatzes setzt die App die Huelle noch ~350 ms
    // auf width 400 px (Pille „Händlerqualität von Google", gemessen abzeichen-probe-390.json) — ohne
    // Klemme lag eine 438–480 px breite Huelle (z 2147483647) ~300 ms ueber der Chat-Blase und fing ihre
    // Tipps ab. overflow bleibt der App-Wert hidden. Ohne Klemme nur das aufgeklappte Panel (Klick).
    el.style.maxWidth = offen ? '' : Math.round(kante) + 'px';

    // ANDOCKEN: nur sichtbar, solange sein Platz am Ende der Fusszeile im Bild ist.
    if (abzeichenPlatz) {
      abzeichenKlasse(true);
      document.body.style.setProperty('--ap-abzeichen-h', Math.round(kante) + 'px');
      if (!abzeichenPlatzSichtbar) {
        el.setAttribute('data-ap-geparkt', '');
        el.style.visibility = 'hidden';
        el.style.opacity = '0';
        el.style.transform = 'translate(' + -Math.round((Number.isFinite(links) ? links : 0) + Math.max(breite, kante) + 24) + 'px, ' + -hub + 'px)';
        return;
      }
    } else {
      abzeichenKlasse(false);
    }
    if (geparkt) {
      el.removeAttribute('data-ap-geparkt');
      el.style.visibility = '';
      abzeichenEinblenden(el);
    }
    // Angedockt und zugeklappt: die SCHEIBE (nicht der Wirt mit seinem transparenten
    // Innenrand) endet auf der Standlinie und hat die Schwebe-Groesse (seit Phase D
    // Stufe 0 44 px auf allen Breiten) wie Chat und „Nach oben" — Art-Director R3. Skaliert
    // wird um die untere linke Ecke der Scheibe; aufgeklappt bleibt die App ungeskaliert.
    const scheibe = kante - 2 * ABZ_INNENRAND;
    // --ap-float-size steht in rem (4.4rem, automotive.css) — in px umrechnen.
    const rootCs = window.getComputedStyle(document.documentElement);
    const sollRoh = rootCs.getPropertyValue('--ap-float-size').trim();
    let soll = parseFloat(sollRoh);
    if (/rem$/.test(sollRoh)) soll *= parseFloat(rootCs.fontSize) || 10;
    if (!Number.isFinite(soll) || soll <= 0) soll = 44;
    if (!offen && scheibe > 0 && abzeichenPlatz) {
      const s = Math.round((soll / scheibe) * 1000) / 1000;
      el.style.transformOrigin = ABZ_INNENRAND + 'px ' + (kante - ABZ_INNENRAND) + 'px';
      el.style.transform = 'translate(0px, ' + (ABZ_INNENRAND - hub) + 'px) scale(' + s + ')';
      return;
    }
    el.style.transform = hub ? 'translateY(' + -hub + 'px)' : '';
  }

  function initAbzeichen() {
    if (abzeichenGebunden) return;
    abzeichenGebunden = true;

    platziereAbzeichen();

    // Die App haengt ihren Wrapper spaeter an den <body> und blendet ihn
    // zwischendurch aus; die Standlinie wiederum setzt <sticky-atc> ueber eine
    // Klasse am <body>. Beides beobachten.
    // Hoechstens EINMAL je Bild (Phase D, DT-1): Apps haengen laufend Knoten an den <body>;
    // jeder Wechsel rief bisher sofort platziereAbzeichen (getComputedStyle mehrfach).
    // GEAENDERT Feinschliff 3 FIX (04.10.2026, gegnerische Pruefung Mobil, major „Layout-Lesungen im
    // Scroll-Pfad"): NUR ein ECHTER Wechsel von body.className oder das Kommen/Gehen der Abzeichen-Huelle
    // zaehlt. Dawns StickyHeader.hide() entfernt beim Abwaertswischen in JEDEM Bild 'overflow-hidden' vom
    // <body> (closeSearchModal) — eine Mutation ohne Aenderung, die platziereAbzeichen samt
    // getComputedStyle/offsetWidth je Bild ausloeste (Trace CPU 4x: 182 Aufrufe = 637 ms, 11x erzwungen).
    if ('MutationObserver' in window) {
      let bodyKlasse = document.body.className;
      const istAbzeichen = (n) =>
        !!n && n.nodeType === 1 && (n.id === 'google-merchantwidget-iframe-wrapper' || (n.childElementCount > 0 && !!n.querySelector(ABZEICHEN)));
      new MutationObserver((liste) => {
        let relevant = false;
        for (let i = 0; i < liste.length && !relevant; i++) {
          const r = liste[i];
          if (r.type === 'attributes') {
            if (document.body.className !== bodyKlasse) relevant = true;
            continue;
          }
          for (let a = 0; a < r.addedNodes.length && !relevant; a++) if (istAbzeichen(r.addedNodes[a])) relevant = true;
          for (let e = 0; e < r.removedNodes.length && !relevant; e++) if (istAbzeichen(r.removedNodes[e])) relevant = true;
        }
        bodyKlasse = document.body.className;
        if (relevant) abzeichenPlanen();
      }).observe(document.body, {
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // Die App rechnet ihren Abstand NICHT nach. Nach dem Drehen oder
    // Groessern liefe sie sonst gegen unsere Rechnung.
    window.addEventListener('resize', platziereAbzeichen);
    window.addEventListener('orientationchange', platziereAbzeichen);

    // Schwebe-System (26.09.2026): die Standlinie ist LIVE (assets/ap-schwebe.js
    // misst Kaufleiste und unten verankerte Banner). Jede Aenderung meldet sie
    // hier; die Blende steht in automotive.css (transition auf transform).
    // DT-1: die gerechnete Standlinie aus dem Ereignis uebernehmen und im naechsten Bild platzieren
    // (die Blende auf transform laeuft 220 ms — ein Bild spaeter faellt nicht auf).
    document.addEventListener('ap:standlinie', (e) => {
      const px = e && e.detail ? Number(e.detail.standlinie) : NaN;
      abzeichenStandlinie = Number.isFinite(px) && px >= 0 && px <= STANDLINIE_MAX ? px : null;
      abzeichenPlanen();
    });
  }

  /* ----------------------------------------------------------------------
   * Titel fuer fremd eingefuegte <iframe>.
   *
   * Das Google-Merchant-Widget haengt sein <iframe> per Skript direkt an den
   * <body> — ohne title. Ein iframe ohne Titel ist fuer Vorleseprogramme ein
   * unbeschrifteter Bereich; das Theme kann das Markup nicht erzeugen, wohl
   * aber nachtraeglich beschriften. Der Beobachter laeuft nur, bis der Rahmen
   * da ist, und ruehrt nichts an, was bereits einen Namen hat.
   * -------------------------------------------------------------------- */
  let frameTitlesBound = false;

  function initForeignFrameTitles() {
    if (frameTitlesBound) return;
    frameTitlesBound = true;

    // TODO i18n: nach locales verschieben, sobald fremde Rahmen mehr werden.
    const TITLES = [{ selector: '#merchantwidgetiframe', title: 'Google Shopping – Händlerinformationen' }];

    function apply() {
      let missing = 0;
      TITLES.forEach((entry) => {
        const frame = document.querySelector(entry.selector);
        if (!frame) {
          missing += 1;
          return;
        }
        if (!frame.getAttribute('title') && !frame.getAttribute('aria-label')) {
          frame.setAttribute('title', entry.title);
        }
      });
      return missing === 0;
    }

    if (apply()) return;

    const observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // Fremdskripte, die nie laden, sollen den Beobachter nicht ewig halten.
    window.setTimeout(() => observer.disconnect(), 30000);
  }

  /* ----------------------------------------------------------------------
   * Judge.me-Karussell im Fuss (Pruefrunde 2 Phase C, Linse Leistung): automotive.css reserviert die
   * gemessene Endhoehe der Sektion (Stufen unter 375, mobil, 750–989 und ab 990) — der Lade-CLS kurzer
   * Seiten ist damit weg. Rendert Judge.me aber NICHT (Inhaltsblocker, CDN- oder API-Ausfall), stand
   * die Reservierung als leere Flaeche ueber dem Fuss: die :has()-Bedingung greift schon am
   * serverseitigen Rahmen der App-Sektion, und sections/apps.liquid blendet das Panel aus, solange das
   * Karussell jdgm-hidden traegt. Die Klasse ap-jdgm-leer gibt die Reservierung dann frei.
   * GEAENDERT Zusammenfuehrung Teil 2 (05.10.2026, gegnerische Pruefung, major): die Frist hing an
   * 'load' + 5 s. load haengt an Drittanbietern (Google merchantverse lief bis 12,7 s) — die Luecke
   * stand bis zu 27 s, und geprueft wurde nur EINMAL und nur auf Karten (ein Karussell mit jdgm-hidden
   * blieb reserviert, OZ-Felge 360). Jetzt: Frist ab dem Start dieses Skripts (DOMContentLoaded), dann
   * DAUERHAFT in beide Richtungen nachgefuehrt. Leer heisst: keine Karte ODER Karussell versteckt.
   * GEAENDERT Phase D Teil 2 (P5 Stufe A, Judge.me-Fuss als Fassade): KEINE UHR mehr. Unter echter
   * Drosselung war Judge.me nach 4 s noch nicht da — die Sektion klappte zu und mit den Karten wieder auf
   * (CLS bis 0,566 auf kurzen Seiten, 24/24 Laeufe). Bis zur ersten Karte steht die Fassade auf der
   * reservierten Buehne (sections/apps.liquid, automotive.css). Eingeklappt (ap-jdgm-leer) wird nur, wenn
   *   (a) Judge.me seine Chance hatte — Ereignis, keine Frist: window load (+ Leerlauf), bzw. bei spaetem
   *       Laden (Einstellung „Spät laden" ≠ sofort) erst nach ap:spaet-geladen mit anbieter 'judgeme', und
   *   (b) die Sektion GANZ unterhalb des Bildes liegt (IntersectionObserver) — nie im Bild, nie darueber.
   * Kommen Karten, faellt die Klasse sofort wieder (Inhalt geht vor). Beides dauerhaft nachgefuehrt.
   * -------------------------------------------------------------------- */
  const JDGM_KARTE = '.jdgm-carousel-item, .jdgm-carousel__item, .jdgm-rev, .jdgm-rev-widg__reviews > *';

  function initJudgeMeLeere() {
    const sektion = document.querySelector('.shopify-section[id$="__17863469700a42e826"]');
    if (!sektion) return;
    const leer = () => {
      const karussell = sektion.querySelector('.jdgm-cards-carousel');
      return !sektion.querySelector(JDGM_KARTE) || Boolean(karussell && karussell.classList.contains('jdgm-hidden'));
    };
    let entschieden = false;
    let unterBild = false;
    // toggle mit Zustand schreibt nur bei einem Wechsel — der Beobachter loest sich nie selbst aus.
    const setze = () => {
      if (!leer()) {
        sektion.classList.toggle('ap-jdgm-leer', false);
        return;
      }
      if (entschieden && unterBild) sektion.classList.toggle('ap-jdgm-leer', true);
    };
    if (typeof MutationObserver === 'function') {
      new MutationObserver(setze).observe(sektion.querySelector('.ap-apps-section') || sektion, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((eintraege) => {
        eintraege.forEach((e) => {
          const hoehe = (e.rootBounds && e.rootBounds.height) || window.innerHeight;
          unterBild = !e.isIntersecting && e.boundingClientRect.top >= hoehe;
        });
        setze();
      }).observe(sektion);
    }
    const entscheiden = () => {
      const los = () => {
        entschieden = true;
        setze();
      };
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(los, { timeout: 2000 });
      else Promise.resolve().then(los);
    };
    const nachLoad = () => {
      if (document.readyState === 'complete') entscheiden();
      else window.addEventListener('load', entscheiden, { once: true });
    };
    const spaet = window.apSpaet && typeof window.apSpaet.modus === 'function' ? window.apSpaet.modus('judgeme') : 'sofort';
    if (spaet === 'sofort') {
      nachLoad();
    } else {
      document.addEventListener('ap:spaet-geladen', (e) => {
        if (e && e.detail && e.detail.anbieter === 'judgeme') nachLoad();
      });
    }
  }

  /* ----------------------------------------------------------------------
   * <ap-popup> — promo popup / newsletter modal.
   * Markup contract:
   *   <ap-popup class="ap-popup" data-popup-id="..." data-delay="6000" hidden>
   *     ... [data-popup-close] ... overlay = .ap-popup__overlay ...
   * Shows after data-delay ms OR on desktop exit-intent (mouseout to top),
   * once per data-popup-id unless localStorage 'ap_popup_<id>_dismissed'.
   * Dismiss via [data-popup-close] / overlay / ESC. Focus-trapped while open.
   * -------------------------------------------------------------------- */
  class ApPopup extends HTMLElement {
    constructor() {
      super();
      this.opened = false;
      this.delayTimer = null;
      this.trap = null;
      this.lastFocused = null;
      this.onKeydown = this.onKeydown.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onExitIntent = this.onExitIntent.bind(this);
    }

    get storageKey() {
      const id = this.dataset.popupId || 'default';
      return 'ap_popup_' + id + '_dismissed';
    }

    connectedCallback() {
      // Already dismissed this popup: never show again.
      if (safeLocalStorageGet(this.storageKey)) return;

      const delay = parseInt(this.dataset.delay || '6000', 10);
      if (!isNaN(delay) && delay >= 0) {
        this.delayTimer = window.setTimeout(() => this.open(), delay);
      }

      // Desktop exit-intent: pointer leaves through the top of the viewport.
      if (window.matchMedia && window.matchMedia('(min-width: 990px)').matches) {
        document.addEventListener('mouseout', this.onExitIntent);
      }

      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      if (this.delayTimer) window.clearTimeout(this.delayTimer);
      document.removeEventListener('mouseout', this.onExitIntent);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.trap) this.trap.release();
      if (this.opened) unlockScroll();
    }

    onExitIntent(event) {
      // Fire only when the cursor exits through the top edge to outside the page.
      if (event.clientY > 0) return;
      if (event.relatedTarget || event.toElement) return;
      this.open();
    }

    open() {
      if (this.opened) return;
      if (safeLocalStorageGet(this.storageKey)) return;
      if (offenesModal()) return;
      this.opened = true;
      if (this.delayTimer) window.clearTimeout(this.delayTimer);
      document.removeEventListener('mouseout', this.onExitIntent);

      this.lastFocused = document.activeElement;
      this.removeAttribute('hidden');
      this.classList.add('is-open');
      lockScroll();

      document.addEventListener('keydown', this.onKeydown);
      this.trap = createFocusTrap(this);
      // Prefer the email field; fall back to the close button. The overlay
      // ([data-popup-close] tabindex=-1) is intentionally skipped.
      const initial =
        this.querySelector('[data-popup-initial-focus]') ||
        this.querySelector('.ap-popup__dialog input[type="email"], .ap-popup__dialog input:not([type="hidden"])') ||
        this.querySelector('button[data-popup-close]') ||
        this.querySelector('[data-popup-close]');
      this.trap.activate(initial);
    }

    close() {
      if (!this.opened) return;
      this.opened = false;
      // Persist dismissal so it won't reappear for this visitor.
      safeLocalStorageSet(this.storageKey, '1');

      this.classList.remove('is-open');
      this.setAttribute('hidden', '');
      unlockScroll();

      document.removeEventListener('keydown', this.onKeydown);
      if (this.trap) {
        this.trap.release();
        this.trap = null;
      }
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        event.preventDefault();
        this.close();
      }
    }

    onClick(event) {
      if (event.target.closest('[data-popup-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      // Click on the overlay backdrop (not the inner card) closes too.
      if (event.target.matches('[data-popup-overlay], .ap-popup__overlay')) {
        this.close();
      }
    }
  }

  /* ----------------------------------------------------------------------
   * <ap-age-gate> — first-visit age verification.
   * Markup contract:
   *   <ap-age-gate data-min-age="18" hidden>
   *     ... [data-age-yes] ... [data-age-no] ... [data-age-blocked hidden] ...
   * On connect, if localStorage 'ap_age_ok' is unset: reveal + trap focus +
   * lock scroll. "Yes" sets the flag + hides. "No" swaps to a polite blocked
   * message ([data-age-blocked]) and keeps the overlay (no entry).
   * -------------------------------------------------------------------- */
  const AGE_OK_KEY = 'ap_age_ok';

  class ApAgeGate extends HTMLElement {
    constructor() {
      super();
      this.trap = null;
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      // Already confirmed: stay hidden, do nothing.
      if (safeLocalStorageGet(AGE_OK_KEY)) {
        this.setAttribute('hidden', '');
        return;
      }
      this.reveal();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.trap) this.trap.release();
      unlockScroll();
    }

    reveal() {
      this.removeAttribute('hidden');
      this.classList.add('is-open');
      lockScroll();
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      this.trap = createFocusTrap(this);
      this.trap.activate(this.querySelector('[data-age-yes]'));
    }

    onKeydown(event) {
      // Trap ESC so the user cannot dismiss the gate without choosing.
      if (event.key === 'Escape' || event.keyCode === 27) {
        event.preventDefault();
      }
    }

    onClick(event) {
      if (event.target.closest('[data-age-yes]')) {
        event.preventDefault();
        this.accept();
      } else if (event.target.closest('[data-age-no]')) {
        event.preventDefault();
        this.decline();
      }
    }

    accept() {
      safeLocalStorageSet(AGE_OK_KEY, '1');
      this.classList.remove('is-open');
      this.setAttribute('hidden', '');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.trap) {
        this.trap.release();
        this.trap = null;
      }
      unlockScroll();
    }

    decline() {
      // Swap the Yes/No prompt for a polite blocked message; keep the overlay up.
      // The markup wraps the buttons in [data-age-prompt] when present, else we
      // fall back to hiding the actions row (.ap-age-gate__actions).
      const prompt = this.querySelector('[data-age-prompt]') || this.querySelector('.ap-age-gate__actions');
      const blocked = this.querySelector('[data-age-blocked]');
      if (prompt) prompt.setAttribute('hidden', '');
      if (blocked) {
        blocked.removeAttribute('hidden');
        this.setAttribute('data-age-state', 'blocked');
        // Re-trap focus onto whatever remains; fall back to the blocked alert
        // itself (make it programmatically focusable for screen-reader flow).
        if (!blocked.hasAttribute('tabindex')) blocked.setAttribute('tabindex', '-1');
        if (this.trap) this.trap.release();
        this.trap = createFocusTrap(this);
        this.trap.activate(blocked);
      }
    }
  }

  /* ----------------------------------------------------------------------
   * <ap-hotspots> — image hotspots with click-toggled popovers.
   * Markup contract (sections/image-hotspots.liquid + section-image-hotspots.css):
   *   <ap-hotspots>
   *     <div class="ap-hotspots__stage">
   *       <img class="ap-hotspots__image">
   *       <div class="ap-hotspot-wrap" style="left:%;top:%">
   *         <button class="ap-hotspot" aria-expanded="false"
   *                 aria-controls="HotspotPopover-…">…</button>
   *         <div class="ap-hotspot__popover" id="HotspotPopover-…" hidden>
   *           <button class="ap-hotspot__close" data-hotspot-close>…</button> …
   * Click toggles a hotspot's popover (closing any other). Outside-click + ESC
   * close all; [data-hotspot-close] closes its own. JS sets [data-ap-hotspots-ready]
   * so the CSS knows it's interactive (no-JS: pulse off / close hidden fallback).
   * Near the image top edge the popover flips below via .ap-hotspot__popover--below.
   * -------------------------------------------------------------------- */
  class ApHotspots extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onDocPointer = this.onDocPointer.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.wraps = Array.from(this.querySelectorAll('.ap-hotspot-wrap'));
      this.dots = Array.from(this.querySelectorAll('.ap-hotspot'));
      this.dots.forEach((dot) => {
        dot.setAttribute('aria-expanded', 'false');
        const pop = this.popoverFor(dot);
        if (pop) pop.setAttribute('hidden', '');
      });
      // Signal interactive mode to the CSS (enables pulse, shows close buttons).
      this.setAttribute('data-ap-hotspots-ready', '');

      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onDocPointer);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocPointer);
      document.removeEventListener('keydown', this.onKeydown);
    }

    popoverFor(dot) {
      const id = dot.getAttribute('aria-controls');
      if (id) {
        const byId = this.querySelector('#' + CSS.escape(id));
        if (byId) return byId;
      }
      const wrap = dot.closest('.ap-hotspot-wrap');
      return wrap ? wrap.querySelector('.ap-hotspot__popover') : null;
    }

    closeAll(except) {
      this.dots.forEach((dot) => {
        if (dot === except) return;
        this.setOpen(dot, false);
      });
    }

    setOpen(dot, open) {
      const pop = this.popoverFor(dot);
      const wrap = dot.closest('.ap-hotspot-wrap');
      dot.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (wrap) wrap.classList.toggle('is-open', open);
      if (pop) {
        if (open) {
          pop.removeAttribute('hidden');
          this.positionPopover(pop);
        } else {
          pop.setAttribute('hidden', '');
          pop.classList.remove('ap-hotspot__popover--below');
        }
      }
    }

    // Flip the popover below the dot when it would overflow the top of the stage.
    positionPopover(pop) {
      pop.classList.remove('ap-hotspot__popover--below');
      const stage = this.querySelector('.ap-hotspots__stage') || this;
      const popRect = pop.getBoundingClientRect();
      const stageRect = stage.getBoundingClientRect();
      if (popRect.top < stageRect.top) {
        pop.classList.add('ap-hotspot__popover--below');
      }
    }

    onClick(event) {
      // Close button inside a popover.
      const closeBtn = event.target.closest('[data-hotspot-close]');
      if (closeBtn && this.contains(closeBtn)) {
        event.preventDefault();
        const dot =
          closeBtn.closest('.ap-hotspot-wrap') &&
          closeBtn.closest('.ap-hotspot-wrap').querySelector('.ap-hotspot');
        if (dot) {
          this.setOpen(dot, false);
          dot.focus();
        }
        return;
      }

      const dot = event.target.closest('.ap-hotspot');
      if (!dot || !this.contains(dot)) return;
      event.preventDefault();
      const isOpen = dot.getAttribute('aria-expanded') === 'true';
      this.closeAll(dot);
      this.setOpen(dot, !isOpen);
    }

    onDocPointer(event) {
      if (this.contains(event.target)) return;
      this.closeAll(null);
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        const openDot = this.dots.find((d) => d.getAttribute('aria-expanded') === 'true');
        if (openDot) {
          this.closeAll(null);
          openDot.focus(); // Return focus to the trigger for keyboard users.
        }
      }
    }
  }

  /* ----------------------------------------------------------------------
   * <ap-tabs> — product-tabs progressive enhancement.
   * Markup contract (sections/product-tabs.liquid + section-product-tabs.css):
   *   <ap-tabs data-tabs>
   *     <div class="ap-tabs__list" role="tablist" aria-hidden="true"
   *          data-tablist hidden></div>          <-- empty; JS fills it
   *     <div class="ap-tabs__items">
   *       <details class="ap-tabs__item" [open]>
   *         <summary class="ap-tabs__tab" id="ap-tab-UID" data-tab
   *                  aria-controls="ap-panel-UID"> .ap-tabs__tab-label …
   *         <div class="ap-tabs__panel" id="ap-panel-UID" role="region"
   *              aria-labelledby="ap-tab-UID" data-tab-panel> …
   * BASELINE (no JS): native <details> accordion at every width.
   * ENHANCED: on >=750px we build a real ARIA tab strip (roving tabindex,
   * Left/Right/Home/End nav). On <750px we restore the accordion. The host
   * gets [data-tablist-active] when the tab strip is live (CSS keys off it).
   * -------------------------------------------------------------------- */
  class ApTabs extends HTMLElement {
    constructor() {
      super();
      this.summaries = [];
      this.panels = [];
      this.buttons = [];
      this.activeIndex = 0;
      this.tablistActive = false;
      this.onListClick = this.onListClick.bind(this);
      this.onListKeydown = this.onListKeydown.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
    }

    connectedCallback() {
      this.list = this.querySelector('[data-tablist], .ap-tabs__list');
      this.items = Array.from(this.querySelectorAll('.ap-tabs__item'));
      this.summaries = Array.from(this.querySelectorAll('.ap-tabs__tab, [data-tab]'));
      this.panels = Array.from(this.querySelectorAll('.ap-tabs__panel, [data-tab-panel]'));
      if (!this.list || !this.summaries.length || this.summaries.length !== this.panels.length) {
        return; // Markup not in the expected shape: keep the native accordion.
      }

      // The open <details> (or the first) becomes the initial active tab.
      const openIdx = this.items.findIndex((d) => d.hasAttribute('open'));
      this.activeIndex = openIdx >= 0 ? openIdx : 0;

      this.mq = window.matchMedia('(min-width: 750px)');
      // addEventListener('change') is widely supported; addListener is the fallback.
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMediaChange);
      else if (this.mq.addListener) this.mq.addListener(this.onMediaChange);

      if (this.mq.matches) this.enableTablist();
    }

    disconnectedCallback() {
      this.disableTablist();
      if (this.mq) {
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMediaChange);
        else if (this.mq.removeListener) this.mq.removeListener(this.onMediaChange);
      }
    }

    onMediaChange(event) {
      if (event.matches) this.enableTablist();
      else this.disableTablist();
    }

    enableTablist() {
      if (this.tablistActive) return;
      this.tablistActive = true;

      // Build tab buttons from each summary's label text.
      this.list.textContent = '';
      this.buttons = this.summaries.map((summary, i) => {
        const labelEl = summary.querySelector('.ap-tabs__tab-label');
        const label = (labelEl ? labelEl.textContent : summary.textContent).trim();
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ap-tabs__list-button';
        btn.setAttribute('role', 'tab');
        btn.id = (summary.id || 'ap-tab-' + i) + '-btn';
        const panel = this.panels[i];
        if (panel && panel.id) {
          btn.setAttribute('aria-controls', panel.id);
          panel.setAttribute('aria-labelledby', btn.id);
        }
        // Mini-Icon des Reiters (Feinschliff II E) mitnehmen — dekorativ, der Name bleibt Text.
        const mi = labelEl ? labelEl.querySelector('.ap-mini-icon') : null;
        if (mi) btn.appendChild(mi.cloneNode(true));
        btn.appendChild(document.createTextNode(label));
        this.list.appendChild(btn);
        return btn;
      });

      this.list.removeAttribute('hidden');
      this.list.removeAttribute('aria-hidden');
      this.setAttribute('data-tablist-active', '');

      // In tab mode the <details> must stay forced-open (panels shown/hidden by us).
      this.items.forEach((d) => d.setAttribute('open', ''));

      this.list.addEventListener('click', this.onListClick);
      this.list.addEventListener('keydown', this.onListKeydown);

      this.select(this.activeIndex, false);
    }

    disableTablist() {
      if (!this.tablistActive) return;
      this.tablistActive = false;

      this.list.removeEventListener('click', this.onListClick);
      this.list.removeEventListener('keydown', this.onListKeydown);
      this.list.textContent = '';
      this.list.setAttribute('hidden', '');
      this.list.setAttribute('aria-hidden', 'true');
      this.removeAttribute('data-tablist-active');
      this.buttons = [];

      // Restore the accordion: panels visible, only the active <details> open.
      this.panels.forEach((p) => p.removeAttribute('hidden'));
      this.items.forEach((d, i) => {
        if (i === this.activeIndex) d.setAttribute('open', '');
        else d.removeAttribute('open');
      });
    }

    select(index, focusTab) {
      const max = this.buttons.length - 1;
      let i = index;
      if (i < 0) i = max;
      if (i > max) i = 0;
      this.activeIndex = i;

      this.buttons.forEach((btn, idx) => {
        const selected = idx === i;
        btn.setAttribute('aria-selected', selected ? 'true' : 'false');
        btn.setAttribute('tabindex', selected ? '0' : '-1');
      });
      this.panels.forEach((panel, idx) => {
        if (idx === i) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      });
      if (focusTab && this.buttons[i]) this.buttons[i].focus();
    }

    onListClick(event) {
      const btn = event.target.closest('.ap-tabs__list-button');
      if (!btn) return;
      const index = this.buttons.indexOf(btn);
      if (index >= 0) this.select(index, false);
    }

    onListKeydown(event) {
      const current = this.buttons.indexOf(document.activeElement);
      if (current < 0) return;
      let handled = true;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          this.select(current + 1, true);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          this.select(current - 1, true);
          break;
        case 'Home':
          this.select(0, true);
          break;
        case 'End':
          this.select(this.buttons.length - 1, true);
          break;
        default:
          handled = false;
      }
      if (handled) event.preventDefault();
    }
  }

  /* ----------------------------------------------------------------------
   * <ap-volume-pricing>
   * Clickable set tiers (1x/2x/4x). Selecting a tier sets the desired
   * quantity (mirrored into the main product form). The ATC button adds
   * that quantity of the variant to the cart, refreshes the cart drawer
   * sections, publishes cartUpdate and opens the drawer with the new item.
   * -------------------------------------------------------------------- */
  class ApVolumePricing extends HTMLElement {
    constructor() {
      super();
      this.onTierClick = this.onTierClick.bind(this);
      this.onAdd = this.onAdd.bind(this);
    }

    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.tiers = Array.from(this.querySelectorAll('[data-volume-qty]'));
      this.addBtn = this.querySelector('[data-volume-atc]');
      this.qtyLabel = this.querySelector('[data-volume-atc-qty]');
      var pressed = this.tiers.filter(function (t) { return t.getAttribute('aria-pressed') === 'true'; })[0] || this.tiers[0];
      this.qty = pressed ? parseInt(pressed.dataset.volumeQty, 10) || 1 : 1;
      this.tiers.forEach(function (t) { t.addEventListener('click', this.onTierClick); }, this);
      if (this.addBtn) this.addBtn.addEventListener('click', this.onAdd);
      this.reflect();
    }

    disconnectedCallback() {
      this.tiers.forEach(function (t) { t.removeEventListener('click', this.onTierClick); }, this);
      if (this.addBtn) this.addBtn.removeEventListener('click', this.onAdd);
    }

    onTierClick(event) {
      this.qty = parseInt(event.currentTarget.dataset.volumeQty, 10) || 1;
      this.reflect();
    }

    reflect() {
      var qty = this.qty;
      this.tiers.forEach(function (t) {
        t.setAttribute('aria-pressed', (parseInt(t.dataset.volumeQty, 10) || 1) === qty ? 'true' : 'false');
      });
      if (this.qtyLabel) this.qtyLabel.textContent = String(qty);
      // Eingegrenzt wie hauptinhalt() erklaert: sonst schreibt die Staffelmenge
      // in ein Mengenfeld des Warenkorb-Schubkastens.
      var q = hauptinhalt().querySelector('product-form input[name="quantity"], .product-form__input input[name="quantity"], .quantity__input');
      if (q) { q.value = String(qty); q.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    // Derselbe Warenkorb-Pfad wie jeder andere Kaufknopf (ApCartAdd.kauf,
    // oben): Schublade bei Erfolg, Teilmenge und vollem Korb, Meldung in einer
    // role=alert-Zeile unter dem Knopf nur bei echtem Fehler. Die Schublade
    // zeichnet jetzt Dawns renderContents — der eigene Austausch per
    // replaceWith (renderDrawer) samt Neubindung der Ueberlagerung entfiel
    // damit.
    onAdd() {
      var self = this;
      if (!this.variantId) {
        this.zeigeMeldung(ApCartAdd.texte(this.texte()).variante);
        return null;
      }
      return ApCartAdd.kauf({
        knopf: this.addBtn,
        host: this,
        anfrage: [{ id: Number(this.variantId) || this.variantId, quantity: this.qty }],
        quelle: 'ap-volume-pricing',
        variantId: this.variantId,
        aktiv: this.addBtn,
        texte: this.texte(),
        meldung: function (text) { self.zeigeMeldung(text); },
      });
    }

    texte() {
      if (window.apKaufStrings) return null;
      return {
        teilmenge: this.getAttribute('data-msg-partial') || '',
        fehler: this.getAttribute('data-msg-error') || '',
      };
    }

    // Meldezeile unter dem Kaufknopf — erst beim ersten Text angelegt, damit das
    // eingefuegte role=alert-Element samt Inhalt angesagt wird (wie beim
    // Satzblock, <tyre-set-pricing>.zeigeMeldung).
    zeigeMeldung(text) {
      if (!this.msgEl) {
        if (!text) return;
        this.msgEl = document.createElement('p');
        this.msgEl.className = 'ap-volume__msg';
        this.msgEl.setAttribute('role', 'alert');
        if (this.addBtn && this.addBtn.parentNode) this.addBtn.insertAdjacentElement('afterend', this.msgEl);
        else this.appendChild(this.msgEl);
      }
      this.msgEl.textContent = text || '';
    }
  }

  /* ----------------------------------------------------------------------
   * <ap-quick-qty>
   * Quick quantity boxes (Einzeln / Paar / Satz) in the buybox. Clicking a box
   * sets the main product quantity input (#Quantity-{section}) and fires
   * input/change so Dawn's quantity logic + price-per-item update. Stepper edits
   * are mirrored back to the active box. Progressive enhancement: without JS the
   * standard +/- stepper still works (the boxes are inert buttons).
   * -------------------------------------------------------------------- */
  class ApQuickQty extends HTMLElement {
    connectedCallback() {
      // Target the named main-quantity input; fall back to the page's main
      // product quantity input so a second instance (e.g. the sticky bar) still
      // drives — and stays in sync with — the same quantity.
      this.input =
        (this.dataset.target && document.getElementById(this.dataset.target)) ||
        document.querySelector('.product__info-container .quantity__input') ||
        document.querySelector('.quantity__input');
      this.boxes = Array.prototype.slice.call(this.querySelectorAll('.ap-quick-qty__box'));
      this.onInputChange = this.sync.bind(this);
      this.boxes.forEach((b) => b.addEventListener('click', () => this.pick(b)));
      if (this.input) {
        this.input.addEventListener('change', this.onInputChange);
        this.sync();
        // Re-sync next frame: Dawn may set the quantity value after connect,
        // so the initial highlighted box always matches the real quantity.
        window.requestAnimationFrame(this.onInputChange);
      }
    }

    disconnectedCallback() {
      if (this.input) this.input.removeEventListener('change', this.onInputChange);
    }

    pick(box) {
      if (!this.input) return;
      var qty = box.dataset.qty;
      this.input.value = qty;
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.highlight(qty);
    }

    sync() {
      if (this.input) this.highlight(this.input.value);
    }

    highlight(qty) {
      this.boxes.forEach((b) => {
        var on = b.dataset.qty === String(qty);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.updateSetLine(qty);
    }

    // Satzpreis-Zeile der Buybox mitfuehren.
    //
    // Markup-Vertrag (Gruppe BUYBOX, sections/main-product.liquid):
    //   [data-ap-setline]        Behaelter, data-unit-price in Cent,
    //                            data-qty = gezeigte Menge, data-currency,
    //                            data-label-pair / -set / -total,
    //                            data-noun-one / -many,
    //                            traegt selbst aria-live="polite"
    //   [data-ap-setline-count]  Mengenzahl im Text
    //   [data-ap-setline-noun]   Nomen (Ein- oder Mehrzahl)
    //   [data-ap-setline-total]  Gesamtbetrag
    //   [data-ap-setline-unit]   Betrag je Stueck
    // Nur diese Stellen werden beschrieben — der Behaelter selbst NIE, sonst
    // faellt die serverseitig uebersetzte Beschriftung mit heraus. Die Ansage
    // fuer Vorleseprogramme erledigt das aria-live der Zeile.
    //
    // Die Zeile folgt JEDER Menge, auch der 1. Frueher fiel sie unterhalb von
    // 2 auf die serverseitige Ausgangsmenge zurueck — nach einem Klick auf
    // "1x Einzeln" stand dann "Satzpreis (4 Reifen): 512,00 EUR" ueber einem
    // Kaufbutton, der 1 Reifen ablegt. Damit "Satzpreis (1 Reifen)" nicht die
    // naechste Falschaussage wird, wechselt mit der Menge auch die
    // Beschriftung: 2 = Paarpreis, 4 = Satzpreis, alles andere = Gesamtpreis.
    //
    // Fehlt die Zeile — auf jeder Seite ohne Buybox der Normalfall — oder
    // fehlen alle Ziele, passiert nichts.
    updateSetLine(qty) {
      var wanted = parseInt(qty, 10);
      if (!isFinite(wanted) || wanted <= 0) return;

      // Es kann mehrere geben (Buybox + Sticky-Leiste) — alle mitfuehren.
      var lines = document.querySelectorAll('[data-ap-setline]');
      Array.prototype.forEach.call(lines, function (line) {
        var unit = Number(line.getAttribute('data-unit-price'));
        if (!isFinite(unit) || unit <= 0) return;

        var count = wanted;

        var countEl = line.querySelector('[data-ap-setline-count]');
        var totalEl = line.querySelector('[data-ap-setline-total]');
        var unitEl = line.querySelector('[data-ap-setline-unit]');
        if (!countEl && !totalEl && !unitEl) return;

        var symbol = line.getAttribute('data-currency') || line.getAttribute('data-currency-symbol') || '';
        // Bringt die Zeile fuer eine Stufe einen SERVERSEITIG formatierten Betrag
        // mit (data-money-1 / -2 / -4), hat der Vorrang: nur der trifft die
        // Schreibweise des Shops (z. B. "128,00 EUR" bei aktivem Waehrungscode).
        // formatMoney kann nur ein Symbol anhaengen und bleibt der Rueckfall fuer
        // Mengen, die keine eigene Stufe haben (Stepper-Eingabe).
        var preTotal = line.getAttribute('data-money-' + count);
        var preUnit = line.getAttribute('data-money-1');
        if (countEl) countEl.textContent = String(count);
        if (totalEl) totalEl.textContent = preTotal || formatMoney(unit * count, symbol);
        if (unitEl) unitEl.textContent = preUnit || formatMoney(unit, symbol);

        // Die MwSt.-Angabe wandert mit der Summe. Auch sie kommt fertig aus
        // Liquid (data-mwst-1 / -2 / -4) — der Betrag darf hier NICHT gerechnet
        // werden: unit-Steuer mal Menge liegt bei vier Stueck einen Cent unter
        // dem Wert, den Shopify auf die Rechnung schreibt. Fuer Mengen ohne
        // eigene Stufe (Stepper auf 3, 5, 6 …) liegt deshalb bewusst nur der
        // Kurzsatz ohne Klammerwert bereit.
        // Geschrieben wird der .ap-mwst-Knoten INNERHALB der Huelle, nicht die
        // Huelle: wer die Huelle selbst beschreibt, wirft die Klasse weg und der
        // Satz verliert Groesse und Kontrast.
        var mwstHost = line.querySelector('[data-ap-setline-mwst]');
        if (mwstHost) {
          var mwstText = line.getAttribute('data-mwst-' + count) || line.getAttribute('data-mwst-kurz');
          // Kein Text (Shop nicht auf Bruttopreisen): nichts loeschen. Eine leere
          // Steuerzeile waere schlimmer als keine.
          if (mwstText) (mwstHost.querySelector('.ap-mwst') || mwstHost).textContent = mwstText;
        }

        // Optionaler Zusatz: haengt die Zeile ein [data-ap-setline-word] samt
        // data-label-pair / -set / -total an, tauschen wir auch die
        // Bezeichnung. Fehlt eine davon, bleibt die serverseitig uebersetzte
        // Beschriftung unangetastet.
        var wordEl = line.querySelector('[data-ap-setline-word]');
        var word = line.getAttribute('data-label-total');
        if (count === 2) {
          word = line.getAttribute('data-label-pair');
        } else if (count === 4) {
          word = line.getAttribute('data-label-set');
        }
        if (wordEl && word) wordEl.textContent = word;

        // Nomen in Ein-/Mehrzahl, sonst stuende bei einer Felge "1 Felgen".
        var nounEl = line.querySelector('[data-ap-setline-noun]');
        var noun = count === 1 ? line.getAttribute('data-noun-one') : line.getAttribute('data-noun-many');
        if (nounEl && noun) nounEl.textContent = noun;

        line.setAttribute('data-qty', String(count));
      });
    }
  }

  /* ----------------------------------------------------------------------
   * <b2b-checkout-gate>
   * B2B cart gate: disables the checkout button (and shows a notice) until the
   * cart holds at least data-min items. The REQUIRED commercial-purchase
   * confirmation checkbox in the same block is plain HTML (`required`) and needs
   * no JS — this component only adds the LIVE minimum-quantity gate so the button
   * re-enables/disables on AJAX cart changes without a full reload.
   * Markup contract (snippets/cart-b2b-fields.liquid):
   *   <b2b-checkout-gate data-min="2" data-count="{{ cart.item_count }}"
   *       data-checkout="checkout">
   *     ... <p data-b2b-min-notice [hidden]> ...
   * The initial disabled/notice state is server-rendered, so no-JS visitors
   * (whose cart updates are full-page reloads) are gated correctly too.
   * -------------------------------------------------------------------- */
  class B2bCheckoutGate extends HTMLElement {
    constructor() {
      super();
      this.unsubscribe = null;
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.min = parseInt(this.dataset.min || '0', 10) || 0;
      this.notice = this.querySelector('[data-b2b-min-notice]');
      this.button = document.getElementById(this.dataset.checkout || 'checkout');

      // Nothing to gate (no minimum, or no checkout button on the page).
      if (this.min <= 1 || !this.button) return;

      // Sync once from the server-provided count, then track cart updates.
      this.apply(parseInt(this.dataset.count || '0', 10) || 0);

      const sub = getSubscribe();
      if (sub) this.unsubscribe = sub(cartUpdateEventName(), this.onCartUpdate);
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }

    onCartUpdate(data) {
      // Prefer the item_count carried in the event payload, else fetch /cart.js.
      const count =
        data && data.cartData && typeof data.cartData.item_count === 'number'
          ? data.cartData.item_count
          : null;
      if (count !== null) {
        this.apply(count);
        return;
      }
      fetch(window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js', {
        headers: { Accept: 'application/json' },
      })
        .then((r) => r.json())
        .then((cart) => this.apply(cart && typeof cart.item_count === 'number' ? cart.item_count : 0))
        .catch(() => {});
    }

    apply(count) {
      // Below the minimum (this also covers an empty cart, count 0) -> block.
      // This component owns the below-min disabled state; an empty cart is also
      // below-min, so we never wrongly re-enable a button on an empty cart.
      const below = count < this.min;
      this.button.toggleAttribute('disabled', below);
      this.button.classList.toggle('ap-b2b-blocked', below);
      if (this.notice) this.notice.toggleAttribute('hidden', !below);
      this.toggleAttribute('data-below-min', below);
    }
  }

  /* ----------------------------------------------------------------------
   * <ap-widerruf>
   * Widerrufsformular ueber dem Footer (sections/ap-widerruf.liquid).
   *
   * DREI REGELN, DIE HIER ALLES BESTIMMEN:
   *
   * 1. NICHTS ZEICHNEN, WAS NICHT TRAEGT. Der Knopf erscheint erst, wenn die
   *    App auf GET {proxy} mit { widerruf: true } antwortet — also erst, wenn
   *    dort ein Mailversand konfiguriert ist. Ohne ihn gaebe es keine
   *    Eingangsbestaetigung nach § 356 Abs. 1 BGB, und das Formular wuerde eine
   *    zusagen, die nie kommt. Bei jeder anderen Antwort (Fehler, kein JSON,
   *    alte App-Version) erscheint der Hinweis auf den formlosen Weg.
   *
   * 2. EIN ABGESCHICKTER WIDERRUF DARF NIE ZWEIMAL RAUSGEHEN. Der Knopf wird
   *    fuer die Dauer der Anfrage gesperrt; nach Erfolg wird das Formular durch
   *    die Bestaetigung ersetzt, statt es erneut absendbar zu lassen.
   *
   * 3. KEINE FEHLERMELDUNG, DIE DEN KUNDEN IM UNKLAREN LAESST. Scheitert die
   *    Uebertragung, nennt die Meldung den formlosen Weg — der Widerruf ist
   *    formfrei, eine E-Mail genuegt und wahrt die Frist. Der Kunde darf sein
   *    Recht nie an unserer Technik verlieren.
   * -------------------------------------------------------------------- */
  class ApWiderruf extends HTMLElement {
    constructor() {
      super();
      this.sending = false;
      this.onOpen = this.onOpen.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      // EINMAL fragen (Art-Director R3, 28.09.2026): auf /cart und der Felgen-PDP standen
      // Karte mit „Widerruf erklären" UND der Ersatztext darunter — ein zweiter
      // connectedCallback (Element neu eingehaengt) fragte erneut, die zweite Antwort
      // blendete den Ersatz ein. Entweder Knopf ODER Ersatztext.
      if (this._gefragt) return;
      this._gefragt = true;
      this.panel = this.querySelector('[data-widerruf-panel]');
      this.fallback = this.querySelector('[data-widerruf-fallback]');
      this.dialog = this.querySelector('[data-widerruf-dialog]');
      this.body = this.querySelector('[data-widerruf-body]');
      this.form = this.querySelector('[data-widerruf-form]');
      this.status = this.querySelector('[data-widerruf-status]');
      this.submit = this.querySelector('[data-widerruf-submit]');

      var opener = this.querySelector('[data-widerruf-open]');
      if (opener) opener.addEventListener('click', this.onOpen);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);

      this.askCapability();
    }

    /** Erst fragen, dann zeichnen. */
    askCapability() {
      var url = this.dataset.proxy;
      var self = this;
      if (!url) {
        this.showFallback();
        return;
      }
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .catch(function () {
          return null;
        })
        .then(function (data) {
          // Streng auf === true pruefen: alles andere ist keine Zusage.
          if (data && data.widerruf === true) self.reveal(self.panel);
          else self.showFallback();
        });
    }

    reveal(el) {
      if (el) el.removeAttribute('hidden');
      if (el && el === this.panel && this.fallback) this.fallback.hidden = true;
    }

    showFallback() {
      // Steht der Knopf schon, bleibt es beim Knopf.
      if (this.panel && !this.panel.hidden) return;
      this.reveal(this.fallback);
    }

    onOpen() {
      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      var first = this.querySelector('[data-widerruf-form] input');
      if (first) first.focus();
    }

    setStatus(message, tone) {
      if (!this.status) return;
      this.status.textContent = message || '';
      if (tone) this.status.setAttribute('data-tone', tone);
      else this.status.removeAttribute('data-tone');
    }

    /** Ein zuvor markiertes Feld wieder freigeben. */
    clearInvalid() {
      var marked = this.querySelectorAll('[aria-invalid="true"]');
      for (var i = 0; i < marked.length; i++) marked[i].removeAttribute('aria-invalid');
    }

    /** Das von der App benannte Feld markieren UND anspringen — eine Meldung
     *  ohne Sprungziel laesst den Kunden in einem langen Formular suchen. */
    markInvalid(field) {
      if (!field || !this.form) return;
      var el = this.form.querySelector('[name="' + field + '"]');
      if (!el) return;
      el.setAttribute('aria-invalid', 'true');
      el.focus();
    }

    lock(locked) {
      this.sending = locked;
      if (this.submit) this.submit.disabled = locked;
      if (this.form) this.form.setAttribute('aria-busy', locked ? 'true' : 'false');
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.sending || !this.form) return;

      // Die Browser-Pflichtpruefung zuerst: sie meldet fehlende Felder besser,
      // als wir es koennten. novalidate steht nur im Markup, damit sie GENAU
      // hier laeuft und nicht schon beim Tippen.
      if (typeof this.form.reportValidity === 'function' && !this.form.reportValidity()) return;

      this.clearInvalid();
      this.setStatus(this.dataset.sendingLabel || '', null);
      this.lock(true);

      var data = {};
      var fields = this.form.elements;
      for (var i = 0; i < fields.length; i++) {
        if (fields[i].name) data[fields[i].name] = fields[i].value;
      }

      var self = this;
      fetch(this.dataset.proxy, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(data),
      })
        .then(function (r) {
          return r.json().catch(function () {
            return null;
          });
        })
        .catch(function () {
          return null;
        })
        .then(function (res) {
          self.lock(false);
          if (res && res.ok === true) {
            self.showDone(res.message);
            return;
          }
          if (res && res.message) {
            self.setStatus(res.message, 'error');
            self.markInvalid(res.field);
            return;
          }
          // Netzfehler oder unlesbare Antwort: der formlose Weg steht immer offen,
          // und genau darauf verweist der Text aus der Uebersetzungsdatei.
          self.setStatus(self.dataset.errorLabel || '', 'error');
        });
    }

    /** Formular durch die Bestaetigung ersetzen: danach gibt es nichts mehr
     *  abzuschicken, und ein zweiter Klick kann keinen zweiten Widerruf ausloesen. */
    showDone(message) {
      if (!this.body) return;
      var heading = this.body.querySelector('.ap-widerruf__title');
      var title = heading ? heading.textContent : 'Widerruf';

      var wrap = document.createElement('div');
      wrap.className = 'ap-widerruf__done';
      wrap.setAttribute('role', 'status');

      var h = document.createElement('h3');
      h.id = 'ApWiderrufTitle';
      h.textContent = title;
      var p = document.createElement('p');
      // textContent, nicht innerHTML: die Meldung kommt zwar aus unserer
      // eigenen App, aber sie traegt keinen Grund, Markup zu duerfen.
      p.textContent = message || 'Ihr Widerruf ist bei uns eingegangen.';

      wrap.appendChild(h);
      wrap.appendChild(p);
      this.body.innerHTML = '';
      this.body.appendChild(wrap);
      h.setAttribute('tabindex', '-1');
      h.focus();
    }
  }

  /* ----------------------------------------------------------------------
   * Wischzeilen der Kaufkarte ([data-ap-scrollrow]) — „Weitere Groessen" und
   * „Weitere Farben" (snippets/ap-sibling-sizes, ap-wheel-finishes).
   *
   * Design-Umbau 25.09.2026 (B8): die Vorschau steht in EINER Zeile statt in
   * vier umbrechenden Chip-Reihen. Zwei Aufgaben, beide rein verbessernd — ohne
   * JS bleibt die Zeile per Wischen und Scrollen bedienbar:
   *   1. Die aktuelle Groesse/Farbe ([aria-current]) steht beim Laden in der
   *      Mitte der Zeile. Gesetzt wird scrollLeft der ZEILE — das Element selbst
   *      ins Bild zu holen wuerde die ganze Seite mitrollen.
   *   2. Mit Maus (hover + feiner Zeiger) und nur bei Ueberlauf erscheinen die
   *      Pfeile (44 px); am Anfang bzw. Ende ist der jeweilige Pfeil gesperrt.
   * Mehrfach-Aufruf ist sicher (data-ap-scrollrow-ready).
   * -------------------------------------------------------------------- */
  function initScrollRows(root) {
    const scope = root || document;
    const rows = scope.querySelectorAll('[data-ap-scrollrow]:not([data-ap-scrollrow-ready])');
    if (!rows.length) return;
    const maus = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;

    Array.prototype.forEach.call(rows, (row) => {
      const list = row.querySelector('[data-ap-scrollrow-list]');
      if (!list) return;
      row.setAttribute('data-ap-scrollrow-ready', '');
      const prev = row.querySelector('[data-ap-scrollrow-prev]');
      const next = row.querySelector('[data-ap-scrollrow-next]');

      // Rand-Zustand fuer die Maske (section-main-product.css): ausgeblendet wird
      // nur die Seite, auf der noch Inhalt folgt (Art-Director Feinschliff II R1,
      // pf4 — vorher blendete eine feste Rechtsmaske ausgerechnet den gewaehlten
      // Chip aus, waehrend links zwei Chips ohne Hinweis verborgen lagen).
      const rand = () => {
        const ueberlauf = list.scrollWidth > list.clientWidth + 2;
        const anfang = list.scrollLeft <= 2;
        const ende = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
        list.setAttribute('data-ap-rand', !ueberlauf ? 'keiner' : anfang ? 'anfang' : ende ? 'ende' : 'mitte');
      };

      const zustand = () => {
        rand();
        if (!prev || !next) return;
        const ueberlauf = list.scrollWidth > list.clientWidth + 2;
        const zeigen = ueberlauf && !!(maus && maus.matches);
        prev.hidden = !zeigen;
        next.hidden = !zeigen;
        if (!zeigen) return;
        prev.disabled = list.scrollLeft <= 2;
        next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
      };
      // Pfeile zuerst (sie setzen per :has() den Innenabstand), DANN einrollen.
      zustand();

      // STARTLAGE (R1 pz4/pr12): nicht mehr zentrieren — dabei stand links ein
      // halb abgeschnittener Chip („55/80 R15 83H", „‹ 5,04") und die Zoll-
      // Gruppe der aktuellen Groesse lag unsichtbar links. Jetzt beginnt die
      // Zeile mit dem Zoll-Trenner der aktuellen Groesse (sonst mit dem Chip
      // selbst, 32 px Anschnitt davor); passt der Chip dann nicht ins Bild,
      // steht er an zweiter Stelle mit Anschnitt links.
      // R3 (28.09.2026): stand die aktuelle Groesse tief in ihrer Zoll-Gruppe, rollte die
      // Regel oben sie an den LINKEN Rand — alle kleineren Groessen samt Preis lagen ohne
      // Anschnitt links draussen (Toyo R888R 390: Chips x −1063…−196), sichtbar blieb nur
      // der aktuelle Chip ohne Preis. Passt die Gruppe nicht ins Bild, steht der Chip jetzt
      // MITTIG: links und rechts schneidet je ein Nachbar mit Preis an (die Maske zeigt beide
      // Seiten an, data-ap-rand „mitte").
      const aktuell = list.querySelector('[aria-current]');
      const eintrag = aktuell ? aktuell.closest('li') || aktuell : null;
      if (eintrag && list.scrollWidth > list.clientWidth + 2) {
        const lr = list.getBoundingClientRect();
        const polster = parseFloat(window.getComputedStyle(list).scrollPaddingInlineStart) || 0;
        const lage = (el) => el.getBoundingClientRect().left - lr.left + list.scrollLeft - polster;
        let gruppe = eintrag.previousElementSibling;
        while (gruppe && !gruppe.classList.contains('ap-siblings__group')) gruppe = gruppe.previousElementSibling;
        const er = eintrag.getBoundingClientRect();
        let ziel = gruppe ? lage(gruppe) : lage(eintrag) - 48;
        if (lage(eintrag) + er.width - ziel > list.clientWidth - polster - 32) {
          ziel = lage(eintrag) - Math.max(48, (list.clientWidth - er.width) / 2);
        }
        if (ziel > 2) list.scrollLeft = ziel;
      }

      list.addEventListener('scroll', rand, { passive: true });
      if (!prev || !next) {
        rand();
        return;
      }
      const schritt = (richtung) => {
        list.scrollBy({
          left: richtung * Math.max(120, list.clientWidth * 0.8),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      };
      prev.addEventListener('click', () => schritt(-1));
      next.addEventListener('click', () => schritt(1));
      list.addEventListener('scroll', zustand, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(zustand).observe(list);
      if (maus && typeof maus.addEventListener === 'function') maus.addEventListener('change', zustand);
      zustand();
    });
  }

  /* ----------------------------------------------------------------------
   * Punkte der mobilen Wischgalerie ([data-ap-dots], snippets/product-media-
   * gallery). Dawns <slider-component> schreibt beim Wischen den Zaehler
   * (.slider-counter--current) um — dem folgen die Punkte. Kein eigener
   * Scroll-Rechner: EINE Quelle fuer die aktuelle Seite. Rein optisch; ohne JS
   * bleibt der erste Punkt aktiv, der Zaehler sagt den Stand an.
   * -------------------------------------------------------------------- */
  function initGalleryDots(root) {
    const scope = root || document;
    const alle = scope.querySelectorAll('[data-ap-dots]:not([data-ap-dots-ready])');
    Array.prototype.forEach.call(alle, (dots) => {
      const slider = dots.closest('slider-component');
      const zaehler = slider && slider.querySelector('.slider-counter--current');
      if (!zaehler || !('MutationObserver' in window)) return;
      dots.setAttribute('data-ap-dots-ready', '');
      const punkte = dots.querySelectorAll('.ap-dots__dot');
      const setzen = () => {
        const n = parseInt(zaehler.textContent, 10);
        if (!Number.isFinite(n)) return;
        Array.prototype.forEach.call(punkte, (p, i) => p.classList.toggle('is-active', i === n - 1));
      };
      new MutationObserver(setzen).observe(zaehler, { childList: true, characterData: true, subtree: true });
      setzen();
    });
  }

  /* ----------------------------------------------------------------------
   * Datenwaechter fuer den App-Block „Testberichte" (.ttx-test, tyre-trader-eu,
   * blocks/tyre-tests.liquid) — Art-Director R2, 26.09.2026.
   *
   * Der Block zeigte „Platz 0 von 0", „Platz 4 von 0" und „Geprüfte Größe:
   * 2254518 W/": in Liquid ist 0 wahr (`if r.position and r.total`), und die
   * Kontextzeile kommt ungeprueft aus dem Feed. Irrefuehrende Testangaben
   * (UWG), und Spec B2/B11 verlangt 0 Vorkommen. Die eigentliche Korrektur
   * gehoert in die App (`r.position > 0 and r.total > 0 and r.position <=
   * r.total`, dieselbe Regel wie snippets/automotive-test-score.liquid) und
   * braucht einen App-Deploy. Bis dahin nimmt das Theme NUR die kaputten
   * Fragmente heraus — gueltige Platzierungen und Groessen bleiben stehen,
   * kein Bericht, keine Note, kein Testsieger entfaellt.
   * Die Liste steht weit unter dem Falz: kein Layout-Sprung im Bild.
   * -------------------------------------------------------------------- */
  const TESTGROESSE = /^\d{3}\/\d{2}\s?R?\s?\d{2}/;

  function initTestberichtWaechter(root) {
    const scope = root || document;
    scope.querySelectorAll('.ttx-test .ttx-test__pos').forEach((el) => {
      const m = /Platz\s+(\d+)\s+von\s+(\d+)/.exec(el.textContent || '');
      if (!m) return;
      const platz = Number(m[1]);
      const von = Number(m[2]);
      if (!(platz > 0 && von > 0 && platz <= von)) el.remove();
    });
    scope.querySelectorAll('.ttx-test .ttx-test__what, .ttx-test .ttx-test__res').forEach((el) => {
      const text = el.textContent || '';
      const neu = text.replace(/Gepr\u00fcfte Gr\u00f6\u00dfe:\s*([^,;)]*)[,;]?\s*/g, (ganz, wert) =>
        TESTGROESSE.test(String(wert).trim()) ? ganz : ''
      );
      if (neu === text) return;
      const rest = neu.trim();
      if (rest) {
        el.textContent = rest;
      } else {
        el.remove();
      }
    });
    // KONTEXTTEXT LESBAR (Art-Director R3, 28.09.2026): der Feed liefert ihn roh („Nässe : 3-
    // · Trocken : 2- · Stärken : · kurze Trockenbremswege …") — bei Motorrad eine Textwand von
    // ~25 Zeilen. Nur die Satzzeichen werden geglaettet (kein Wort faellt weg), und laenger
    // als drei Zeilen klappt der Text ein: „Ganzen Test lesen" zeigt ihn vollstaendig.
    scope.querySelectorAll('.ttx-test .ttx-test__what').forEach((el) => {
      if (el.hasAttribute('data-ap-test-glatt')) return;
      el.setAttribute('data-ap-test-glatt', '');
      const roh = el.textContent || '';
      const glatt = roh
        .replace(/\s+:\s*/g, ': ')
        .replace(/:\s*·\s*/g, ': ')
        .replace(/(\s*·\s*){2,}/g, ' · ')
        .replace(/\s*·\s*$/, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
      if (glatt && glatt !== roh.trim()) el.textContent = glatt;
      // Die Section ist aufgeschoben (ap-defer-render, content-visibility): ohne Layout gibt es
      // keine Hoehe zum Messen — dann erst, wenn der Block ins Bild kommt.
      if (!el.clientHeight && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          testKuerzen(el);
        }, { rootMargin: '400px 0px' });
        io.observe(el);
        return;
      }
      testKuerzen(el);
    });
  }

  function testKuerzen(el) {
    if (el.hasAttribute('data-ap-test-kurz')) return;
    el.setAttribute('data-ap-test-kurz', '');
    {
      el.classList.add('ap-test-kurz');
      if (el.scrollHeight <= el.clientHeight + 2) {
        el.classList.remove('ap-test-kurz');
        return;
      }
      const knopf = document.createElement('button');
      knopf.type = 'button';
      knopf.className = 'ap-test-mehr';
      knopf.setAttribute('aria-expanded', 'false');
      // Texte aus der Locale (layout/theme.liquid, window.accessibilityStrings); der Rueckfall
      // gilt nur, wenn das Layout sie nicht liefert.
      const texte = window.accessibilityStrings || {};
      const ganz = texte.testGanzLesen || 'Ganzen Test lesen';
      const weniger = texte.testWeniger || 'Weniger anzeigen';
      knopf.textContent = ganz;
      knopf.addEventListener('click', () => {
        const offen = el.classList.toggle('ap-test-kurz') === false;
        knopf.setAttribute('aria-expanded', offen ? 'true' : 'false');
        knopf.textContent = offen ? weniger : ganz;
      });
      el.insertAdjacentElement('afterend', knopf);
    }
  }

  document.addEventListener('shopify:section:load', (event) => {
    initScrollRows(event.target);
    initGalleryDots(event.target);
    initTestberichtWaechter(event.target);
  });

  /* NACH DEM LAYOUT (P1 M2): ein ResizeObserver meldet ein frisch beobachtetes Element einmal NACH dem Layout des
   * naechsten Bildes und vor dem Malen — Lage-Lesungen dort erzwingen nichts. Ein Beobachter je Bild; ohne
   * ResizeObserver requestAnimationFrame. */
  let nachLayoutListe = null;
  function nachLayout(fn) {
    if (nachLayoutListe) {
      nachLayoutListe.push(fn);
      return;
    }
    nachLayoutListe = [fn];
    const los = () => {
      const liste = nachLayoutListe || [];
      nachLayoutListe = null;
      for (let i = 0; i < liste.length; i++) {
        try {
          liste[i]();
        } catch (fehler) {
          // Ein Auftrag darf die uebrigen nicht mitreissen; der Fehler bleibt sichtbar (Konsole).
          window.setTimeout(() => {
            throw fehler;
          }, 0);
        }
      }
    };
    if (typeof window.ResizeObserver === 'function' && document.documentElement) {
      const beob = new ResizeObserver(() => {
        beob.disconnect();
        los();
      });
      beob.observe(document.documentElement);
    } else if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(los);
    } else {
      los();
    }
  }

  /* ----------------------------------------------------------------------
   * Definitions — guarded so re-injection (section rendering) is safe.
   * -------------------------------------------------------------------- */
  function defineComponents() {
    if (!customElements.get('tyre-size-search')) {
      customElements.define('tyre-size-search', TyreSizeSearch);
    }
    if (!customElements.get('sticky-atc')) {
      customElements.define('sticky-atc', StickyAtc);
    }
    if (!customElements.get('tyre-set-pricing')) {
      customElements.define('tyre-set-pricing', TyreSetPricing);
    }
    if (!customElements.get('free-shipping-bar')) {
      customElements.define('free-shipping-bar', FreeShippingBar);
    }
    if (!customElements.get('shipping-cutoff')) {
      customElements.define('shipping-cutoff', ShippingCutoff);
    }
    if (!customElements.get('recently-viewed')) {
      customElements.define('recently-viewed', RecentlyViewed);
    }
    if (!customElements.get('before-after')) {
      customElements.define('before-after', BeforeAfter);
    }
    if (!customElements.get('countdown-timer')) {
      customElements.define('countdown-timer', CountdownTimer);
    }
    // BATCH 3 components.
    if (!customElements.get('ap-popup')) {
      customElements.define('ap-popup', ApPopup);
    }
    if (!customElements.get('ap-age-gate')) {
      customElements.define('ap-age-gate', ApAgeGate);
    }
    if (!customElements.get('ap-hotspots')) {
      customElements.define('ap-hotspots', ApHotspots);
    }
    if (!customElements.get('ap-tabs')) {
      customElements.define('ap-tabs', ApTabs);
    }
    if (!customElements.get('ap-volume-pricing')) {
      customElements.define('ap-volume-pricing', ApVolumePricing);
    }
    if (!customElements.get('ap-quick-qty')) {
      customElements.define('ap-quick-qty', ApQuickQty);
    }
    if (!customElements.get('b2b-checkout-gate')) {
      customElements.define('b2b-checkout-gate', B2bCheckoutGate);
    }
    if (!customElements.get('ap-widerruf')) {
      customElements.define('ap-widerruf', ApWiderruf);
    }
    // Non-custom-element behaviours.
    // „Nach oben" liest beim Einrichten Fensterhoehe, -breite und Scrollstand — NACH dem ersten Layout statt beim
    // Auswerten der Datei (Phase D Teil 2, P1 Massnahme 2). Er ist bis zwei Bildschirmhoehen ohnehin verborgen.
    nachLayout(initBackToTop);
    initForeignFrameTitles();
    initJudgeMeLeere();
    initAbzeichen();
    initLastSizeChips();
    initScrollRows();
    initGalleryDots();
    initTestberichtWaechter();
  }

  // Defer-safe: the script may run before or after DOMContentLoaded.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', defineComponents, { once: true });
  } else {
    defineComponents();
  }
})();

/* Vertrag der Groessensuche: das Absende-Ziel kommt aus dem Attribut data-coll
   (tyre-size-search.liquid / collection-prefilter.liquid). Ist es gesetzt, filtert
   tyreFacetUrl IN dieser Kollektion; ohne data-coll faellt es auf /search zurueck,
   das die Facetten oberhalb von 1.000 Treffern nicht verlaesslich anwendet. */
